import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import JSZip from 'jszip';
import * as adminService from '../lib/services/adminService';
import * as authService from '../lib/services/authService';
import * as invoiceService from '../lib/services/invoiceService';
import { sanitizeRichText } from '../lib/richText';
import './AdminDashboard.css';

const formatDateForInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultTransactionDateRange = () => {
  const today = new Date();
  today.setDate(today.getDate() + 1); // Include today in the range
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(today.getDate() - 30);
  return {
    start_date: formatDateForInput(thirtyDaysAgo),
    end_date: formatDateForInput(today),
  };
};

const getDefaultOrderDateRange = () => {
  const today = new Date();
  today.setDate(today.getDate() + 1); // Include today in the default range
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(today.getDate() - 30);
  return {
    startDate: formatDateForInput(thirtyDaysAgo),
    endDate: formatDateForInput(today),
  };
};

const getCurrentMonthDateRange = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  return {
    fromDate: formatDateForInput(firstDay),
    toDate: formatDateForInput(lastDay),
  };
};

const getDefaultExpenseForm = () => ({
  company_name: '',
  organization_number: '',
  invoice_number: '',
  currency: 'SEK',
  amount: '',
  vat_percent: '25',
  vat_amount: '',
  expense_date: formatDateForInput(new Date()),
  payment_source: 'company_account',
  is_paid: true,
  paid_on_date: formatDateForInput(new Date()),
  payment_due_date: '',
  notes: '',
});

const PAYMENT_SOURCE_LABELS = {
  company_account: 'Company Account',
  personal_account: 'Personal Account',
  credit_card: 'Credit Card',
  wise_account: 'Wise Account',
};

const getDefaultInvestmentForm = () => ({
  source_name: '',
  reference_number: '',
  currency: 'SEK',
  amount: '',
  investment_date: formatDateForInput(new Date()),
  payment_source: 'company_account',
  is_received: true,
  received_on_date: formatDateForInput(new Date()),
  expected_date: '',
  notes: '',
});

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_API_URL || '/api')
  : (process.env.REACT_APP_API_URL || 'http://localhost:5000/api');

const formatDateYMD = (value, emptyValue = '-') => {
  if (!value) {
    return emptyValue;
  }

  const raw = String(value);
  const isoPrefix = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoPrefix) {
    return isoPrefix[1];
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return emptyValue;
  }

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const getCurrencySymbol = (currency) => (String(currency || '').toUpperCase() === 'EUR' ? '€' : 'kr');

// Renders a multi-line monthly trend chart; series entries define which field to plot on which axis
const renderMonthlyTrendChart = (chartData, series, subtitle) => {
  const leftSeries = series.filter((s) => s.axis === 'left');
  const rightSeries = series.filter((s) => s.axis === 'right');
  const maxLeft = Math.max(1, ...chartData.flatMap((d) => leftSeries.map((s) => Number(d[s.key] || 0))));
  const maxRight = Math.max(1, ...chartData.flatMap((d) => rightSeries.map((s) => Number(d[s.key] || 0))));

  const chartWidth = 980;
  const chartHeight = 320;
  const padding = { top: 24, right: 56, bottom: 46, left: 56 };
  const plotWidth = chartWidth - padding.left - padding.right;
  const plotHeight = chartHeight - padding.top - padding.bottom;

  const getX = (idx) => padding.left + (idx * (plotWidth / (chartData.length - 1)));
  const getLeftY = (val) => padding.top + ((maxLeft - val) / maxLeft) * plotHeight;
  const getRightY = (val) => padding.top + ((maxRight - val) / maxRight) * plotHeight;
  const getY = (s, val) => (s.axis === 'left' ? getLeftY(val) : getRightY(val));

  const buildPath = (s) => chartData
    .map((d, idx) => `${idx === 0 ? 'M' : 'L'} ${getX(idx)} ${getY(s, Number(d[s.key] || 0))}`)
    .join(' ');

  return (
    <div className="monthly-trend-card">
      {subtitle && <p className="monthly-trend-subtitle">{subtitle}</p>}

      <div className="monthly-trend-chart-wrap">
        <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="monthly-trend-chart" role="img" aria-label="Monthly sales and orders line chart">
          <line x1={padding.left} y1={padding.top + plotHeight} x2={padding.left + plotWidth} y2={padding.top + plotHeight} className="axis-line" />
          <line x1={padding.left} y1={padding.top} x2={padding.left} y2={padding.top + plotHeight} className="axis-line" />

          {[0, 1, 2, 3, 4].map((tick) => {
            const y = padding.top + (tick * (plotHeight / 4));
            const leftVal = Math.round(maxLeft - ((maxLeft * tick) / 4));
            const rightVal = Math.round(maxRight - ((maxRight * tick) / 4));
            return (
              <g key={`y-tick-${tick}`}>
                <line x1={padding.left} y1={y} x2={padding.left + plotWidth} y2={y} className="grid-line" />
                <text x={padding.left - 8} y={y + 4} textAnchor="end" className="axis-text">{leftVal}</text>
                <text x={padding.left + plotWidth + 8} y={y + 4} textAnchor="start" className="axis-text">{rightVal}</text>
              </g>
            );
          })}

          {chartData.map((d, idx) => (
            <text key={`x-${d.month}`} x={getX(idx)} y={padding.top + plotHeight + 20} textAnchor="middle" className="axis-text">
              {d.label}
            </text>
          ))}

          {series.map((s) => (
            <path key={`path-${s.key}`} d={buildPath(s)} style={{ fill: 'none', stroke: s.color, strokeWidth: 2.5 }} />
          ))}

          {chartData.map((d, idx) => (
            <g key={`pts-${d.month}`}>
              {series.map((s) => {
                const val = Number(d[s.key] || 0);
                return (
                  <circle key={`pt-${s.key}-${d.month}`} cx={getX(idx)} cy={getY(s, val)} r="3.5" style={{ fill: s.color, stroke: '#fff', strokeWidth: 1.5 }}>
                    <title>{`${d.label} ${s.label}: ${val.toFixed(2)}`}</title>
                  </circle>
                );
              })}
            </g>
          ))}
        </svg>
      </div>

      <div className="monthly-trend-legend">
        {series.map((s) => (
          <span key={`legend-${s.key}`} className="legend-item">
            <span className="legend-line" style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
};

const normalizePhoneNumber = (value = '') => String(value).replace(/[\s()-]/g, '');

const isValidSwedishPhoneNumber = (value = '') => {
  const normalized = normalizePhoneNumber(value);
  if (!normalized) return false;

  if (/^\+46[1-9]\d{6,11}$/.test(normalized)) return true;
  if (/^0046[1-9]\d{6,11}$/.test(normalized)) return true;
  if (/^0[1-9]\d{6,11}$/.test(normalized)) return true;

  return false;
};

export const isValidInternationalPhoneNumber = (value = '', country = '') => {
  const normalized = normalizePhoneNumber(value);
  if (!normalized) return false;

  const normalizedCountry = String(country || '').trim().toLowerCase();
  if (!normalizedCountry || normalizedCountry === 'sweden' || normalizedCountry === 'se' || normalizedCountry === 'sverige') {
    return isValidSwedishPhoneNumber(normalized);
  }

  const digitsOnly = normalized.replace(/^\+/, '').replace(/^00/, '');
  if (!/^\d+$/.test(digitsOnly)) return false;
  return digitsOnly.length >= 7 && digitsOnly.length <= 15;
};


const EU_COUNTRY_OPTIONS = [
  'Sweden', 'Denmark', 'Finland', 'Norway', 'Germany', 'France', 'Italy', 'Spain', 'Netherlands',
  'Belgium', 'Austria', 'Portugal', 'Poland', 'Czech Republic', 'Slovakia', 'Slovenia', 'Romania',
  'Hungary', 'Croatia', 'Bulgaria', 'Greece', 'Ireland', 'Estonia', 'Latvia', 'Lithuania',
  'Luxembourg', 'Malta', 'Cyprus', 'United Kingdom', 'United States', 'Canada', 'Norway',
  'Iceland', 'Switzerland', 'Spain', 'Germany', 'France'
].filter((country, index, array) => array.indexOf(country) === index).sort();

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('analytics');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [productAnalytics, setProductAnalytics] = useState([]);
  const [productProfitAnalytics, setProductProfitAnalytics] = useState([]);
  const [productProfitAnalyticsEur, setProductProfitAnalyticsEur] = useState([]);
  const [analyticsPage, setAnalyticsPage] = useState(1);
  const [profitAnalyticsPage, setProfitAnalyticsPage] = useState(1);
  const [profitAnalyticsEurPage, setProfitAnalyticsEurPage] = useState(1);
  // pagination for the main analytics dashboard tables
  const [analyticsOrdersPage, setAnalyticsOrdersPage] = useState(1);
  const [analyticsTopProductsPage, setAnalyticsTopProductsPage] = useState(1);
  const ANALYTICS_PER_PAGE = 15;
  const [loading, setLoading] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [showOrderItemsPopup, setShowOrderItemsPopup] = useState(false);
  const [selectedOrderForItems, setSelectedOrderForItems] = useState(null);
  const [addProductToOrder, setAddProductToOrder] = useState({ product_id: '', quantity: 1, price_type: 'retail' });
  const [showDateFilter, setShowDateFilter] = useState(false);
  const defaultOrderDateRange = getDefaultOrderDateRange();
  const [dateFilter, setDateFilter] = useState({
    startDate: defaultOrderDateRange.startDate,
    endDate: defaultOrderDateRange.endDate,
  });
  const [activeOrderStatus, setActiveOrderStatus] = useState('pending');
  const [orderCurrentPage, setOrderCurrentPage] = useState(1);
  const [showCorporateForm, setShowCorporateForm] = useState(false);
  const [corporateOrderForm, setCorporateOrderForm] = useState({
    customer_name: '',
    shop_name: '',
    contact_name: '',
    organization_number: '',
    vat_number: '',
    customer_email: '',
    customer_phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    pincode: '',
    country: '',
    notes: '',
    currency: 'SEK',
    items: [],
  });
  const [corporateSelection, setCorporateSelection] = useState({ product_id: '', quantity: 1, price_type: 'corporate' });
  const [corporateOrderError, setCorporateOrderError] = useState('');
  const [corporateFieldErrors, setCorporateFieldErrors] = useState({
    customer_name: '',
    shop_name: '',
    contact_name: '',
    organization_number: '',
    vat_number: '',
    customer_email: '',
    customer_phone: '',
    address_line1: '',
    city: '',
    pincode: '',
    country: '',
    items: ''
  });
  const [creatingCorporateOrder, setCreatingCorporateOrder] = useState(false);
  const [fetchingCorporateDetails, setFetchingCorporateDetails] = useState(false);
  const [searchingCorporateCustomers, setSearchingCorporateCustomers] = useState(false);
  const [corporateCustomerMatches, setCorporateCustomerMatches] = useState([]);
  const corporateCustomerSearchTimeoutRef = useRef(null);
  const [sendingCorporateEmailOrderId, setSendingCorporateEmailOrderId] = useState(null);
  const [companyInfo, setCompanyInfo] = useState({
    company_name: '',
    address: '',
    contact_information: '',
    email: '',
    organization_number: '',
    vat_number: '',
    swish_number: '',
    bank_giro_number: '',
    iban: '',
    swift_code: '',
    bank_name: '',
    bank_address: '',
    account_holder_name: '',
    payment_terms: '',
    banner_message: ''
  });
  const [companyInfoMessage, setCompanyInfoMessage] = useState({ type: '', text: '' });
  const [productMessage, setProductMessage] = useState({ type: '', text: '' });
  const [orderMessage, setOrderMessage] = useState({ type: '', text: '' });
  const [editOrderMessage, setEditOrderMessage] = useState({ type: '', text: '' });
  const [productForm, setProductForm] = useState({
    name: '',
    category: '',
    description: '',
    ingredients: '',
    price: '',
    wholesale_price: '',
    purchase_price: 0,
    wholesale_price_eur: 0,
    vat_percent: '',
    discount_percent: '',
    weight: '',
    image_url: '',
    image_files: [],
    existing_images: [],
    in_stock: true,
    stock_quantity: '',
    gluten_free: false,
    barcode: '',
    batch_code: '',
    show_on_website: true
  });
  const [imageUploadError, setImageUploadError] = useState('');
  const [existingImagesSlide, setExistingImagesSlide] = useState(0);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchHistory, setBatchHistory] = useState([]);
  const [batchModalProduct, setBatchModalProduct] = useState(null);
  const [batchForm, setBatchForm] = useState({ batch_code: '', quantity: '', purchase_price: '', purchase_price_eur: '', supplier_name: '', mfg_date: '', expiry_date: '' });
  const [batchMessage, setBatchMessage] = useState({ type: '', text: '' });
  const [creatingBatch, setCreatingBatch] = useState(false);
  const [editingBatchId, setEditingBatchId] = useState(null);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });
  const [originalOrder, setOriginalOrder] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [discountForm, setDiscountForm] = useState({ type: 'percentage', value: '' });
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productStatusFilter, setProductStatusFilter] = useState('all');
  const [productCurrentPage, setProductCurrentPage] = useState(1);
  const [defectiveInputCounts, setDefectiveInputCounts] = useState({});
  const [sampleUsedInputCounts, setSampleUsedInputCounts] = useState({});
  const [categories, setCategories] = useState([]);
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const ingredientsEditorRef = useRef(null);
  const [offers, setOffers] = useState([]);
  const [offersMessage, setOffersMessage] = useState({ type: '', text: '' });
  const getDefaultOfferForm = () => ({
    title: '',
    offer_type: 'bundle',
    items: [{ category: '', quantity: 2, category_price: '' }],
    discount_percent: '',
    currency: 'SEK',
    description: '',
    display_order: 0,
    start_date: '',
    end_date: '',
  });
  const [offerForm, setOfferForm] = useState(getDefaultOfferForm());
  const [savingOffer, setSavingOffer] = useState(false);
  const [editingOfferId, setEditingOfferId] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const defaultMonthDateRange = getCurrentMonthDateRange();
  const [expenseDateFilter, setExpenseDateFilter] = useState({
    fromDate: defaultMonthDateRange.fromDate,
    toDate: defaultMonthDateRange.toDate,
  });
  const [pendingExpenseDateFilter, setPendingExpenseDateFilter] = useState({
    fromDate: defaultMonthDateRange.fromDate,
    toDate: defaultMonthDateRange.toDate,
  });
  const [expenseMessage, setExpenseMessage] = useState({ type: '', text: '' });
  const [expenseForm, setExpenseForm] = useState(getDefaultExpenseForm());
  const [savingExpense, setSavingExpense] = useState(false);
  const [markPaidModalExpense, setMarkPaidModalExpense] = useState(null);
  const [markPaidOnDate, setMarkPaidOnDate] = useState(formatDateForInput(new Date()));
  const [markingExpensePaid, setMarkingExpensePaid] = useState(false);
  const [selectedExpenseForNotes, setSelectedExpenseForNotes] = useState(null);
  const [expenseIncomeByMonth, setExpenseIncomeByMonth] = useState([]);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expenseEditForm, setExpenseEditForm] = useState(null);
  const [savingEditExpense, setSavingEditExpense] = useState(false);
  const [expenseEditMessage, setExpenseEditMessage] = useState({ type: '', text: '' });
  const [expenseCurrentPage, setExpenseCurrentPage] = useState(1);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(null);
  const [deletingRecord, setDeletingRecord] = useState(false);
  const [investments, setInvestments] = useState([]);
  const [investmentDateFilter, setInvestmentDateFilter] = useState({
    fromDate: defaultMonthDateRange.fromDate,
    toDate: defaultMonthDateRange.toDate,
  });
  const [pendingInvestmentDateFilter, setPendingInvestmentDateFilter] = useState({
    fromDate: defaultMonthDateRange.fromDate,
    toDate: defaultMonthDateRange.toDate,
  });
  const [investmentForm, setInvestmentForm] = useState(getDefaultInvestmentForm());
  const [savingInvestment, setSavingInvestment] = useState(false);
  const [investmentMessage, setInvestmentMessage] = useState({ type: '', text: '' });
  const [editingInvestment, setEditingInvestment] = useState(null);
  const [investmentEditForm, setInvestmentEditForm] = useState(null);
  const [savingEditInvestment, setSavingEditInvestment] = useState(false);
  const [investmentEditMessage, setInvestmentEditMessage] = useState({ type: '', text: '' });
  const [investmentCurrentPage, setInvestmentCurrentPage] = useState(1);

  // Inventory management states
  const [inventoryReport, setInventoryReport] = useState([]);
  const [inventoryTransactions, setInventoryTransactions] = useState([]);
  const [inventorySummary, setInventorySummary] = useState(null);
  const defaultTransactionDateRange = getDefaultTransactionDateRange();
  const [inventoryFilters, setInventoryFilters] = useState({
    product_id: '',
    start_date: defaultTransactionDateRange.start_date,
    end_date: defaultTransactionDateRange.end_date,
    transaction_type: ''
  });
  const [pendingInventoryFilters, setPendingInventoryFilters] = useState({
    product_id: '',
    start_date: defaultTransactionDateRange.start_date,
    end_date: defaultTransactionDateRange.end_date,
    transaction_type: ''
  });
  const [inventoryMessage, setInventoryMessage] = useState({ type: '', text: '' });
  const [inventoryReportPage, setInventoryReportPage] = useState(1);
  const [inventoryTransactionsPage, setInventoryTransactionsPage] = useState(1);
  const [showReturnForm, setShowReturnForm] = useState(false);
  const [returnOrderInput, setReturnOrderInput] = useState('');
  const [returnFormData, setReturnFormData] = useState({
    order_id: '',
    items: [],
    reason: '',
    notes: ''
  });

  const PRODUCTS_PAGE_SIZE = 15;
  const ORDERS_PAGE_SIZE = 20;
  const EXPENSES_PAGE_SIZE = 15;
  const INVESTMENTS_PAGE_SIZE = 15;
  const INVENTORY_REPORT_PAGE_SIZE = 10;
  const INVENTORY_TRANSACTIONS_PAGE_SIZE = 20;

  // Helper function to prevent arrow keys from changing number inputs
  const preventArrowKeyChange = (e) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
    }
  };

  useEffect(() => {
    const handleEscapeClose = (event) => {
      if (event.key !== 'Escape') return;

      if (showOrderItemsPopup) {
        setShowOrderItemsPopup(false);
        setSelectedOrderForItems(null);
        return;
      }

      if (showOrderDetail) {
        setShowOrderDetail(false);
        setEditingOrder(null);
        return;
      }

      if (showProductForm) {
        setShowProductForm(false);
        setEditingProduct(null);
        return;
      }

      if (showBatchModal) {
        handleCancelEditBatch();
        setShowBatchModal(false);
        return;
      }

      if (showCorporateForm) {
        setShowCorporateForm(false);
        return;
      }

      if (showReturnForm) {
        setShowReturnForm(false);
        return;
      }

      if (selectedExpenseForNotes) {
        setSelectedExpenseForNotes(null);
        return;
      }

      if (editingExpense) {
        setEditingExpense(null);
        setExpenseEditForm(null);
        return;
      }

      if (editingInvestment) {
        setEditingInvestment(null);
        setInvestmentEditForm(null);
        return;
      }

      if (markPaidModalExpense) {
        setMarkPaidModalExpense(null);
        return;
      }

      if (deleteConfirmModal) {
        setDeleteConfirmModal(null);
      }
    };

    window.addEventListener('keydown', handleEscapeClose);
    return () => window.removeEventListener('keydown', handleEscapeClose);
  }, [showOrderItemsPopup, showOrderDetail, showProductForm, showBatchModal, showCorporateForm, showReturnForm, selectedExpenseForNotes, markPaidModalExpense, deleteConfirmModal, editingExpense, editingInvestment]);

  useEffect(() => {
    if (!ingredientsEditorRef.current) {
      return;
    }

    if (
      document.activeElement !== ingredientsEditorRef.current &&
      ingredientsEditorRef.current.innerHTML !== (productForm.ingredients || '')
    ) {
      ingredientsEditorRef.current.innerHTML = productForm.ingredients || '';
    }
  }, [productForm.ingredients, showProductForm]);

  const handleIngredientsInput = () => {
    if (!ingredientsEditorRef.current) {
      return;
    }

    setProductForm((prev) => ({
      ...prev,
      ingredients: ingredientsEditorRef.current.innerHTML,
    }));
  };

  const applyIngredientsFormat = (command, value = null) => {
    if (!ingredientsEditorRef.current) {
      return;
    }

    ingredientsEditorRef.current.focus();
    document.execCommand(command, false, value);
    handleIngredientsInput();
  };

  const handleIngredientsLinkInsert = () => {
    const url = window.prompt('Enter link URL (https://...)');
    if (!url) {
      return;
    }

    applyIngredientsFormat('createLink', url);
  };

  const openOrderItemsPopup = (order) => {
    setSelectedOrderForItems(order);
    setShowOrderItemsPopup(true);
  };

  const closeOrderItemsPopup = () => {
    setShowOrderItemsPopup(false);
    setSelectedOrderForItems(null);
  };


  const handleLogout = useCallback(() => {
    authService.logout();
    navigate('/admin/login');
  }, [navigate]);


  const checkAuth = useCallback(() => {
    if (!authService.isAuthenticated()) {
      navigate('/admin/login');
    }
  }, [navigate]);


  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getAdminProducts();
      setProducts(data);
      setDefectiveInputCounts((prev) => {
        const next = { ...prev };
        data.forEach((product) => {
          if (next[product.id] === undefined) {
            next[product.id] = 0;
          }
        });
        return next;
      });
      setSampleUsedInputCounts((prev) => {
        const next = { ...prev };
        data.forEach((product) => {
          if (next[product.id] === undefined) {
            next[product.id] = 0;
          }
        });
        return next;
      });
      const uniqueCategories = [...new Set(data.map(p => p.category))].filter(Boolean).sort();
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Error fetching products:', error);
      if (error.message?.includes('401')) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  }, [handleLogout]);


  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch both orders and returns in parallel
      const [orders, returns] = await Promise.all([
        adminService.getAdminOrders(),
        adminService.getAdminReturns().catch(() => [])
      ]);
      
      // Enrich orders with return information
      const enrichedOrders = orders.map(order => {
        // Get all returns for this order
        const orderReturns = returns.filter(r => r.order_id === order.id && r.status === 'approved');
        
        // Calculate total quantity returned for each item
        const itemReturnsMap = {};
        orderReturns.forEach(ret => {
          if (!itemReturnsMap[ret.order_item_id]) {
            itemReturnsMap[ret.order_item_id] = 0;
          }
          itemReturnsMap[ret.order_item_id] += ret.quantity_returned;
        });
        
        // Enrich items with return information
        const enrichedItems = order.items.map(item => ({
          ...item,
          quantity_returned: itemReturnsMap[item.id] || 0,
          quantity_available: Math.max(0, (item.quantity || 0) - (itemReturnsMap[item.id] || 0))
        }));
        
        return {
          ...order,
          items: enrichedItems,
          hasReturns: orderReturns.length > 0,
          returnDetails: orderReturns
        };
      });
      
      setOrders(enrichedOrders);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }, []);


  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getAdminContacts();
      setContacts(data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  }, []);


  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getAnalyticsDashboard();
      setAnalytics(data);
      // reset pagination when new analytics arrive
      setAnalyticsOrdersPage(1);
      setAnalyticsTopProductsPage(1);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  }, []);


  const fetchProductAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const [productData, profitData, profitDataEur] = await Promise.all([
        adminService.getProductAnalytics(),
        adminService.getProductProfitAnalytics(),
        adminService.getProductProfitAnalyticsEur(),
      ]);
      setProductAnalytics(productData);
      setProductProfitAnalytics(profitData);
      setProductProfitAnalyticsEur(profitDataEur);
      setAnalyticsPage(1); // reset to first page whenever new data arrives
      setProfitAnalyticsPage(1);
      setProfitAnalyticsEurPage(1);
    } catch (error) {
      console.error('Error fetching product analytics:', error);
    } finally {
      setLoading(false);
    }
  }, []);


  const fetchCompanyInfo = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getCompanyInfo();
      setCompanyInfo(data);
    } catch (error) {
      console.error('Error fetching company info:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOffers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getAdminOffers();
      setOffers(data);
    } catch (error) {
      console.error('Error fetching offers:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchInventoryReport = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getInventoryReport();
      setInventoryReport(data);
    } catch (error) {
      console.error('Error fetching inventory report:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchInventoryTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getInventoryTransactions(inventoryFilters);
      setInventoryTransactions(data);
      setInventoryTransactionsPage(1);
    } catch (error) {
      console.error('Error fetching inventory transactions:', error);
    } finally {
      setLoading(false);
    }
  }, [inventoryFilters]);

  const fetchInventorySummary = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getInventorySummary();
      setInventorySummary(data);
    } catch (error) {
      console.error('Error fetching inventory summary:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getAdminExpenses();
      setExpenses(data);
      setExpenseCurrentPage(1);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchInvestments = useCallback(async () => {
    try {
      const data = await adminService.getAdminInvestments();
      setInvestments(Array.isArray(data) ? data : []);
      setInvestmentCurrentPage(1);
    } catch (error) {
      console.error('Error fetching investments:', error);
    }
  }, []);

  const fetchExpenseIncomeByMonth = useCallback(async () => {
    try {
      const data = await adminService.getAdminExpensesMonthlySummary();
      setExpenseIncomeByMonth(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching monthly income vs expenses:', error);
      setExpenseIncomeByMonth([]);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);


  useEffect(() => {
    if (activeTab === 'products') {
      fetchProducts();
      setProductMessage({ type: '', text: '' });
    } else if (activeTab === 'orders') {
      fetchOrders();
      fetchProducts();
      setOrderMessage({ type: '', text: '' });
    } else if (activeTab === 'contacts') {
      fetchContacts();
    } else if (activeTab === 'analytics') {
      fetchAnalytics();
      fetchExpenseIncomeByMonth();
    } else if (activeTab === 'product-analytics') {
      fetchAnalytics();
      fetchProductAnalytics();
    } else if (activeTab === 'company-info') {
      fetchCompanyInfo();
      setCompanyInfoMessage({ type: '', text: '' });
    } else if (activeTab === 'offers') {
      fetchProducts();
      fetchOffers();
      setOffersMessage({ type: '', text: '' });
    } else if (activeTab === 'expenses') {
      fetchExpenses();
      fetchInvestments();
      setExpenseMessage({ type: '', text: '' });
    } else if (activeTab === 'inventory') {
      fetchInventoryReport();
      fetchInventoryTransactions();
      fetchInventorySummary();
      fetchProducts();
      setInventoryMessage({ type: '', text: '' });
    }
  }, [activeTab, fetchProducts, fetchOrders, fetchContacts, fetchAnalytics, fetchProductAnalytics, fetchCompanyInfo, fetchOffers, fetchExpenses, fetchInvestments, fetchExpenseIncomeByMonth, fetchInventoryReport, fetchInventoryTransactions, fetchInventorySummary]);

  useEffect(() => {
    if (activeTab === 'inventory') {
      setPendingInventoryFilters(inventoryFilters);
    }
  }, [activeTab, inventoryFilters]);


  // Clamp product page when filtered results change
  useEffect(() => {
    const filteredCount = products.filter((product) => {
      const searchLower = productSearchTerm.toLowerCase();
      const matchesSearch =
        product.name.toLowerCase().includes(searchLower) ||
        product.category.toLowerCase().includes(searchLower) ||
        product.id.toString().includes(searchLower);
      const matchesStatus =
        productStatusFilter === 'all' ||
        (productStatusFilter === 'active' && product.is_active) ||
        (productStatusFilter === 'inactive' && !product.is_active);
      return matchesSearch && matchesStatus;
    }).length;

    const totalPages = Math.max(1, Math.ceil(filteredCount / PRODUCTS_PAGE_SIZE));
    if (productCurrentPage > totalPages) {
      setProductCurrentPage(totalPages);
    }
  }, [productSearchTerm, productStatusFilter, products, PRODUCTS_PAGE_SIZE, productCurrentPage]);

  useEffect(() => {
    setOrderCurrentPage(1);
  }, [activeOrderStatus, dateFilter.startDate, dateFilter.endDate, orders.length]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(expenses.length / EXPENSES_PAGE_SIZE));
    if (expenseCurrentPage > totalPages) {
      setExpenseCurrentPage(totalPages);
    }
  }, [expenses.length, EXPENSES_PAGE_SIZE, expenseCurrentPage]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(investments.length / INVESTMENTS_PAGE_SIZE));
    if (investmentCurrentPage > totalPages) {
      setInvestmentCurrentPage(totalPages);
    }
  }, [investments.length, INVESTMENTS_PAGE_SIZE, investmentCurrentPage]);

  useEffect(() => {
    const filteredCount = expenses.filter((expense) => {
      const expenseDate = formatDateYMD(expense.expense_date, '');
      if (!expenseDate) return false;
      if (expenseDateFilter.fromDate && expenseDate < expenseDateFilter.fromDate) return false;
      if (expenseDateFilter.toDate && expenseDate > expenseDateFilter.toDate) return false;
      return true;
    }).length;

    const totalPages = Math.max(1, Math.ceil(filteredCount / EXPENSES_PAGE_SIZE));
    if (expenseCurrentPage > totalPages) {
      setExpenseCurrentPage(totalPages);
    }
  }, [expenses, expenseDateFilter.fromDate, expenseDateFilter.toDate, EXPENSES_PAGE_SIZE, expenseCurrentPage]);

  useEffect(() => {
    const filteredCount = investments.filter((investment) => {
      const investmentDate = formatDateYMD(investment.investment_date, '');
      if (!investmentDate) return false;
      if (investmentDateFilter.fromDate && investmentDate < investmentDateFilter.fromDate) return false;
      if (investmentDateFilter.toDate && investmentDate > investmentDateFilter.toDate) return false;
      return true;
    }).length;

    const totalPages = Math.max(1, Math.ceil(filteredCount / INVESTMENTS_PAGE_SIZE));
    if (investmentCurrentPage > totalPages) {
      setInvestmentCurrentPage(totalPages);
    }
  }, [investments, investmentDateFilter.fromDate, investmentDateFilter.toDate, INVESTMENTS_PAGE_SIZE, investmentCurrentPage]);


  const handleCompanyInfoSubmit = async (e) => {
    e.preventDefault();
    setCompanyInfoMessage({ type: '', text: '' });
    try {
      await adminService.updateCompanyInfo(companyInfo);
      setCompanyInfoMessage({ type: 'success', text: 'Company information updated successfully!' });
      fetchCompanyInfo();
      setTimeout(() => setCompanyInfoMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('Error updating company info:', error);
      setCompanyInfoMessage({ type: 'error', text: 'Failed to update company information.' });
    }
  };

  const handleOfferFormChange = (field, value) => {
    setOfferForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleOfferItemChange = (index, field, value) => {
    setOfferForm((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    }));
  };

  const handleAddOfferItemRow = () => {
    setOfferForm((prev) => ({ ...prev, items: [...prev.items, { category: '', quantity: 2, category_price: '' }] }));
  };

  const handleRemoveOfferItemRow = (index) => {
    setOfferForm((prev) => ({
      ...prev,
      items: prev.items.length > 1 ? prev.items.filter((_, i) => i !== index) : prev.items,
    }));
  };

  // Same categories (+ same per-category quantity for bundles) means "same offer",
  // regardless of price/discount/title - mirrors the server-side duplicate check.
  const buildOfferItemsSignature = (items, offerType) => JSON.stringify(
    items
      .map((item) => ({
        category: String(item.category || '').trim().toLowerCase(),
        quantity: offerType === 'bundle' ? Math.max(1, parseInt(item.quantity, 10) || 1) : null,
      }))
      .sort((a, b) => a.category.localeCompare(b.category))
  );

  const handleApplyExpenseDateFilter = () => {
    setExpenseDateFilter({
      fromDate: pendingExpenseDateFilter.fromDate,
      toDate: pendingExpenseDateFilter.toDate,
    });
    setExpenseCurrentPage(1);
  };

  const handleApplyInvestmentDateFilter = () => {
    setInvestmentDateFilter({
      fromDate: pendingInvestmentDateFilter.fromDate,
      toDate: pendingInvestmentDateFilter.toDate,
    });
    setInvestmentCurrentPage(1);
  };

  const normalizedAvailableCategories = categories.map((cat) => String(cat || '').trim().toLowerCase());
  const offerFormCategories = offerForm.items.map((item) => String(item.category || '').trim().toLowerCase()).filter(Boolean);
  const areAllOfferCategoriesAvailable = offerFormCategories.every((cat) => normalizedAvailableCategories.includes(cat));
  const hasDuplicateCategoryInForm = new Set(offerFormCategories).size !== offerFormCategories.length;
  const currentOfferSignature = buildOfferItemsSignature(offerForm.items, String(offerForm.offer_type || 'bundle').trim().toLowerCase());
  const isDuplicateOfferComposition = offerFormCategories.length > 0 && offers.some(
    (offer) => offer.id !== editingOfferId
      && String(offer.offer_type || 'bundle').trim().toLowerCase() === String(offerForm.offer_type || 'bundle').trim().toLowerCase()
      && buildOfferItemsSignature(offer.items || [], String(offer.offer_type || 'bundle').trim().toLowerCase()) === currentOfferSignature
  );

  const handleOfferSubmit = async (e) => {
    e.preventDefault();
    setOffersMessage({ type: '', text: '' });

    const offerType = String(offerForm.offer_type || 'bundle').trim().toLowerCase();
    const discountPercent = parseFloat(offerForm.discount_percent);
    const displayOrder = parseInt(offerForm.display_order, 10) || 0;
    const items = offerForm.items
      .map((item) => ({
        category: String(item.category || '').trim(),
        quantity: parseInt(item.quantity, 10),
        category_price: parseFloat(item.category_price),
      }))
      .filter((item) => item.category);

    if (items.length === 0) {
      setOffersMessage({ type: 'error', text: 'At least one category is required.' });
      return;
    }

    if (!areAllOfferCategoriesAvailable) {
      setOffersMessage({ type: 'error', text: 'Please select categories from existing product categories.' });
      return;
    }

    if (hasDuplicateCategoryInForm) {
      setOffersMessage({ type: 'error', text: 'Each category can only be added once per offer.' });
      return;
    }

    if (!['bundle', 'discount'].includes(offerType)) {
      setOffersMessage({ type: 'error', text: 'Offer type must be bundle or discount.' });
      return;
    }

    if (offerType === 'bundle') {
      const totalQuantity = items.reduce((sum, item) => sum + (Number.isInteger(item.quantity) ? item.quantity : 0), 0);
      if (items.some((item) => !Number.isInteger(item.quantity) || item.quantity < 1)) {
        setOffersMessage({ type: 'error', text: 'Each category needs a quantity of at least 1.' });
        return;
      }
      if (totalQuantity < 2) {
        setOffersMessage({ type: 'error', text: 'Bundle must require at least 2 items in total.' });
        return;
      }
      if (items.some((item) => !Number.isFinite(item.category_price) || item.category_price <= 0)) {
        setOffersMessage({ type: 'error', text: 'Each category needs a price greater than 0.' });
        return;
      }
    }

    if (offerType === 'discount') {
      if (!Number.isFinite(discountPercent) || discountPercent <= 0 || discountPercent > 100) {
        setOffersMessage({ type: 'error', text: 'Discount percent must be between 0 and 100.' });
        return;
      }
    }

    if (isDuplicateOfferComposition) {
      setOffersMessage({ type: 'error', text: 'An offer with the same categories and quantities already exists.' });
      return;
    }

    setSavingOffer(true);
    try {
      const payload = {
        title: offerForm.title,
        offer_type: offerType,
        items: items.map((item) => ({
          category: item.category,
          quantity: offerType === 'bundle' ? item.quantity : null,
          category_price: offerType === 'bundle' ? item.category_price : null,
        })),
        discount_percent: offerType === 'discount' ? discountPercent : null,
        currency: offerForm.currency || 'SEK',
        description: offerForm.description,
        display_order: displayOrder,
        start_date: offerForm.start_date || null,
        end_date: offerForm.end_date || null,
      };

      if (editingOfferId) {
        await adminService.updateAdminOffer(editingOfferId, payload);
      } else {
        await adminService.createAdminOffer(payload);
      }

      setOfferForm(getDefaultOfferForm());
      setEditingOfferId(null);
      await fetchOffers();
      setOffersMessage({ type: 'success', text: `Offer ${editingOfferId ? 'updated' : 'created'} successfully.` });
      setTimeout(() => setOffersMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('Error saving offer:', error);
      setOffersMessage({ type: 'error', text: error.message || 'Failed to save offer.' });
    } finally {
      setSavingOffer(false);
    }
  };

  const handleEditOffer = (offer) => {
    setEditingOfferId(offer.id);
    const offerType = String(offer.offer_type || 'bundle').toLowerCase();
    setOfferForm({
      title: offer.title || '',
      offer_type: offerType,
      items: Array.isArray(offer.items) && offer.items.length > 0
        ? offer.items.map((item) => ({ category: item.category || '', quantity: item.quantity ?? 2, category_price: item.category_price ?? '' }))
        : [{ category: '', quantity: 2, category_price: '' }],
      discount_percent: offer.discount_percent ?? '',
      currency: offer.currency || 'SEK',
      description: offer.description || '',
      display_order: offer.display_order ?? 0,
      start_date: offer.start_date ? formatDateForInput(new Date(offer.start_date)) : '',
      end_date: offer.end_date ? formatDateForInput(new Date(offer.end_date)) : '',
    });
    setOffersMessage({ type: '', text: '' });
  };

  const handleCancelEditOffer = () => {
    setEditingOfferId(null);
    setOfferForm(getDefaultOfferForm());
    setOffersMessage({ type: '', text: '' });
  };

  const handleToggleOffer = async (offer) => {
    try {
      await adminService.toggleAdminOffer(offer.id, !offer.is_active);
      await fetchOffers();
      setOffersMessage({
        type: 'success',
        text: `Offer ${offer.is_active ? 'deactivated' : 'activated'} successfully.`,
      });
      setTimeout(() => setOffersMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('Error toggling offer:', error);
      setOffersMessage({ type: 'error', text: error.message || 'Failed to update offer.' });
    }
  };

  const handleDeleteOffer = async (offerId) => {
    if (!window.confirm('Delete this offer?')) return;

    try {
      await adminService.deleteAdminOffer(offerId);
      await fetchOffers();
      setOffersMessage({ type: 'success', text: 'Offer deleted successfully.' });
      setTimeout(() => setOffersMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('Error deleting offer:', error);
      setOffersMessage({ type: 'error', text: error.message || 'Failed to delete offer.' });
    }
  };

  const handleExpenseFormChange = (field, value) => {
    setExpenseForm((prev) => {
      const next = { ...prev, [field]: value };

      if (field === 'amount' || field === 'vat_percent') {
        const amount = parseFloat(field === 'amount' ? value : next.amount);
        const vatPercent = parseFloat(field === 'vat_percent' ? value : next.vat_percent);
        if (Number.isFinite(amount) && Number.isFinite(vatPercent) && amount >= 0 && vatPercent >= 0) {
          next.vat_amount = ((amount * vatPercent) / 100).toFixed(2);
        } else {
          next.vat_amount = '';
        }
      }

      if (field === 'is_paid' && value === true) {
        next.payment_due_date = '';
        if (!next.paid_on_date) {
          next.paid_on_date = formatDateForInput(new Date());
        }
      }

      if (field === 'is_paid' && value === false) {
        next.paid_on_date = '';
      }
      return next;
    });
  };

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    setExpenseMessage({ type: '', text: '' });

    const amount = parseFloat(expenseForm.amount);
    const vatPercent = parseFloat(expenseForm.vat_percent);
    const vatAmount = parseFloat(expenseForm.vat_amount);

    if (!expenseForm.company_name.trim() || !expenseForm.invoice_number.trim() || !expenseForm.expense_date) {
      setExpenseMessage({ type: 'error', text: 'Company, invoice number, and expense date are required.' });
      return;
    }

    if (!expenseForm.currency || !expenseForm.currency.trim()) {
      setExpenseMessage({ type: 'error', text: 'Currency is required.' });
      return;
    }

    if (!Number.isFinite(amount) || amount <= 0) {
      setExpenseMessage({ type: 'error', text: 'Amount must be greater than 0.' });
      return;
    }

    if (!Number.isFinite(vatPercent) || vatPercent < 0 || vatPercent > 100) {
      setExpenseMessage({ type: 'error', text: 'VAT percent must be between 0 and 100.' });
      return;
    }

    if (!Number.isFinite(vatAmount) || vatAmount < 0) {
      setExpenseMessage({ type: 'error', text: 'VAT amount must be 0 or greater.' });
      return;
    }

    if (!expenseForm.is_paid) {
      if (!expenseForm.payment_due_date) {
        setExpenseMessage({ type: 'error', text: 'Payment due date is required for unpaid expenses.' });
        return;
      }

      if (new Date(expenseForm.payment_due_date) < new Date(expenseForm.expense_date)) {
        setExpenseMessage({ type: 'error', text: 'Payment due date cannot be earlier than expense date.' });
        return;
      }
    } else if (!expenseForm.paid_on_date) {
      setExpenseMessage({ type: 'error', text: 'Paid On date is required when invoice is marked as paid.' });
      return;
    } else if (expenseForm.paid_on_date > formatDateForInput(new Date())) {
      setExpenseMessage({ type: 'error', text: 'Paid On date cannot be in the future.' });
      return;
    }

    setSavingExpense(true);
    try {
      await adminService.createAdminExpense({
        company_name: expenseForm.company_name.trim(),
        organization_number: expenseForm.organization_number?.trim() || null,
        invoice_number: expenseForm.invoice_number.trim(),
        currency: expenseForm.currency.trim().toUpperCase(),
        amount,
        vat_percent: vatPercent,
        vat_amount: vatAmount,
        expense_date: expenseForm.expense_date,
        payment_source: expenseForm.payment_source,
        is_paid: expenseForm.is_paid,
        paid_on_date: expenseForm.is_paid ? expenseForm.paid_on_date : null,
        payment_due_date: expenseForm.is_paid ? null : expenseForm.payment_due_date,
        notes: expenseForm.notes?.trim() || null,
      });

      setExpenseForm(getDefaultExpenseForm());
      await fetchExpenses();
      setExpenseMessage({ type: 'success', text: 'Expense added successfully.' });
      setTimeout(() => setExpenseMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('Error creating expense:', error);
      setExpenseMessage({ type: 'error', text: error.message || 'Failed to add expense.' });
    } finally {
      setSavingExpense(false);
    }
  };

  const openMarkExpensePaidModal = (expense) => {
    setMarkPaidModalExpense(expense);
    setMarkPaidOnDate(formatDateForInput(new Date()));
  };

  const openEditExpense = (expense) => {
    setEditingExpense(expense);
    setExpenseEditMessage({ type: '', text: '' });
    setExpenseEditForm({
      company_name: expense.company_name || '',
      organization_number: expense.organization_number || '',
      invoice_number: expense.invoice_number || '',
      currency: expense.currency || 'SEK',
      amount: expense.amount != null ? String(expense.amount) : '',
      vat_percent: expense.vat_percent != null ? String(expense.vat_percent) : '25',
      vat_amount: expense.vat_amount != null ? String(expense.vat_amount) : '',
      expense_date: expense.expense_date ? String(expense.expense_date).slice(0, 10) : '',
      payment_source: expense.payment_source || 'company_account',
      is_paid: Boolean(expense.is_paid),
      paid_on_date: expense.paid_on_date ? String(expense.paid_on_date).slice(0, 10) : '',
      payment_due_date: expense.payment_due_date ? String(expense.payment_due_date).slice(0, 10) : '',
      notes: expense.notes || '',
    });
  };

  const handleEditExpenseChange = (field, value) => {
    setExpenseEditForm((prev) => {
      const next = { ...prev, [field]: value };

      if (field === 'amount' || field === 'vat_percent') {
        const amt = parseFloat(next.amount);
        const vat = parseFloat(next.vat_percent);
        if (Number.isFinite(amt) && Number.isFinite(vat) && vat >= 0) {
          next.vat_amount = ((amt * vat) / 100).toFixed(2);
        }
      }

      if (field === 'is_paid' && value === true) {
        next.payment_due_date = '';
        if (!next.paid_on_date) {
          next.paid_on_date = formatDateForInput(new Date());
        }
      }

      if (field === 'is_paid' && value === false) {
        next.paid_on_date = '';
      }

      return next;
    });
  };

  const handleEditExpenseSubmit = async () => {
    if (!editingExpense || !expenseEditForm) return;
    setExpenseEditMessage({ type: '', text: '' });

    const amount = parseFloat(expenseEditForm.amount);
    const vatPercent = parseFloat(expenseEditForm.vat_percent);
    const vatAmount = parseFloat(expenseEditForm.vat_amount);

    if (!expenseEditForm.company_name.trim() || !expenseEditForm.invoice_number.trim() || !expenseEditForm.expense_date) {
      setExpenseEditMessage({ type: 'error', text: 'Company, invoice number, and expense date are required.' });
      return;
    }

    if (!expenseEditForm.currency || !expenseEditForm.currency.trim()) {
      setExpenseEditMessage({ type: 'error', text: 'Currency is required.' });
      return;
    }

    if (!Number.isFinite(amount) || amount <= 0) {
      setExpenseEditMessage({ type: 'error', text: 'Amount must be greater than 0.' });
      return;
    }

    if (!Number.isFinite(vatPercent) || vatPercent < 0 || vatPercent > 100) {
      setExpenseEditMessage({ type: 'error', text: 'VAT percent must be between 0 and 100.' });
      return;
    }

    if (!Number.isFinite(vatAmount) || vatAmount < 0) {
      setExpenseEditMessage({ type: 'error', text: 'VAT amount must be 0 or greater.' });
      return;
    }

    if (!expenseEditForm.is_paid) {
      if (!expenseEditForm.payment_due_date) {
        setExpenseEditMessage({ type: 'error', text: 'Payment due date is required for unpaid expenses.' });
        return;
      }
      if (new Date(expenseEditForm.payment_due_date) < new Date(expenseEditForm.expense_date)) {
        setExpenseEditMessage({ type: 'error', text: 'Payment due date cannot be earlier than expense date.' });
        return;
      }
    } else if (!expenseEditForm.paid_on_date) {
      setExpenseEditMessage({ type: 'error', text: 'Paid On date is required when invoice is marked as paid.' });
      return;
    } else if (expenseEditForm.paid_on_date > formatDateForInput(new Date())) {
      setExpenseEditMessage({ type: 'error', text: 'Paid On date cannot be in the future.' });
      return;
    }

    setSavingEditExpense(true);
    try {
      await adminService.updateAdminExpense(editingExpense.id, {
        company_name: expenseEditForm.company_name.trim(),
        organization_number: expenseEditForm.organization_number?.trim() || null,
        invoice_number: expenseEditForm.invoice_number.trim(),
        currency: expenseEditForm.currency.trim().toUpperCase(),
        amount,
        vat_percent: vatPercent,
        vat_amount: vatAmount,
        expense_date: expenseEditForm.expense_date,
        payment_source: expenseEditForm.payment_source,
        is_paid: expenseEditForm.is_paid,
        paid_on_date: expenseEditForm.is_paid ? expenseEditForm.paid_on_date : null,
        payment_due_date: expenseEditForm.is_paid ? null : expenseEditForm.payment_due_date,
        notes: expenseEditForm.notes?.trim() || null,
      });

      setEditingExpense(null);
      setExpenseEditForm(null);
      await fetchExpenses();
      await fetchExpenseIncomeByMonth();
      setExpenseMessage({ type: 'success', text: 'Expense updated successfully.' });
      setTimeout(() => setExpenseMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('Error updating expense:', error);
      setExpenseEditMessage({ type: 'error', text: error.message || 'Failed to update expense.' });
    } finally {
      setSavingEditExpense(false);
    }
  };

  // ==================== INVESTMENT HANDLERS ====================

  const handleInvestmentFormChange = (field, value) => {
    setInvestmentForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'is_received' && value === true && !next.received_on_date) {
        next.received_on_date = formatDateForInput(new Date());
      }
      if (field === 'is_received' && value === false) {
        next.received_on_date = '';
      }
      return next;
    });
  };

  const handleInvestmentSubmit = async (e) => {
    e.preventDefault();
    setInvestmentMessage({ type: '', text: '' });

    const amount = parseFloat(investmentForm.amount);

    if (!investmentForm.source_name.trim() || !investmentForm.investment_date) {
      setInvestmentMessage({ type: 'error', text: 'Source name and investment date are required.' });
      return;
    }

    if (!investmentForm.currency.trim()) {
      setInvestmentMessage({ type: 'error', text: 'Currency is required.' });
      return;
    }

    if (!Number.isFinite(amount) || amount <= 0) {
      setInvestmentMessage({ type: 'error', text: 'Amount must be greater than 0.' });
      return;
    }

    if (investmentForm.is_received) {
      if (!investmentForm.received_on_date) {
        setInvestmentMessage({ type: 'error', text: 'Received On date is required when investment is marked as received.' });
        return;
      }
      if (investmentForm.received_on_date > formatDateForInput(new Date())) {
        setInvestmentMessage({ type: 'error', text: 'Received On date cannot be in the future.' });
        return;
      }
    } else if (!investmentForm.expected_date) {
      setInvestmentMessage({ type: 'error', text: 'Expected date is required for pending investments.' });
      return;
    }

    setSavingInvestment(true);
    try {
      await adminService.createAdminInvestment({
        source_name: investmentForm.source_name.trim(),
        reference_number: investmentForm.reference_number?.trim() || null,
        currency: investmentForm.currency.trim().toUpperCase(),
        amount,
        investment_date: investmentForm.investment_date,
        payment_source: investmentForm.payment_source,
        is_received: investmentForm.is_received,
        received_on_date: investmentForm.is_received ? investmentForm.received_on_date : null,
        expected_date: investmentForm.is_received ? null : investmentForm.expected_date,
        notes: investmentForm.notes?.trim() || null,
      });

      setInvestmentForm(getDefaultInvestmentForm());
      await fetchInvestments();
      await fetchExpenseIncomeByMonth();
      setInvestmentMessage({ type: 'success', text: 'Investment added successfully.' });
      setTimeout(() => setInvestmentMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('Error creating investment:', error);
      setInvestmentMessage({ type: 'error', text: error.message || 'Failed to add investment.' });
    } finally {
      setSavingInvestment(false);
    }
  };

  const openEditInvestment = (investment) => {
    setEditingInvestment(investment);
    setInvestmentEditMessage({ type: '', text: '' });
    setInvestmentEditForm({
      source_name: investment.source_name || '',
      reference_number: investment.reference_number || '',
      currency: investment.currency || 'SEK',
      amount: investment.amount != null ? String(investment.amount) : '',
      investment_date: investment.investment_date ? String(investment.investment_date).slice(0, 10) : '',
      payment_source: investment.payment_source || 'company_account',
      is_received: Boolean(investment.is_received),
      received_on_date: investment.received_on_date ? String(investment.received_on_date).slice(0, 10) : '',
      expected_date: investment.expected_date ? String(investment.expected_date).slice(0, 10) : '',
      notes: investment.notes || '',
    });
  };

  const handleEditInvestmentChange = (field, value) => {
    setInvestmentEditForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'is_received' && value === true && !next.received_on_date) {
        next.received_on_date = formatDateForInput(new Date());
      }
      if (field === 'is_received' && value === false) {
        next.received_on_date = '';
      }
      return next;
    });
  };

  const handleEditInvestmentSubmit = async () => {
    if (!editingInvestment || !investmentEditForm) return;
    setInvestmentEditMessage({ type: '', text: '' });

    const amount = parseFloat(investmentEditForm.amount);

    if (!investmentEditForm.source_name.trim() || !investmentEditForm.investment_date) {
      setInvestmentEditMessage({ type: 'error', text: 'Source name and investment date are required.' });
      return;
    }

    if (!investmentEditForm.currency.trim()) {
      setInvestmentEditMessage({ type: 'error', text: 'Currency is required.' });
      return;
    }

    if (!Number.isFinite(amount) || amount <= 0) {
      setInvestmentEditMessage({ type: 'error', text: 'Amount must be greater than 0.' });
      return;
    }

    if (investmentEditForm.is_received) {
      if (!investmentEditForm.received_on_date) {
        setInvestmentEditMessage({ type: 'error', text: 'Received On date is required when investment is marked as received.' });
        return;
      }
      if (investmentEditForm.received_on_date > formatDateForInput(new Date())) {
        setInvestmentEditMessage({ type: 'error', text: 'Received On date cannot be in the future.' });
        return;
      }
    } else if (!investmentEditForm.expected_date) {
      setInvestmentEditMessage({ type: 'error', text: 'Expected date is required for pending investments.' });
      return;
    }

    setSavingEditInvestment(true);
    try {
      await adminService.updateAdminInvestment(editingInvestment.id, {
        source_name: investmentEditForm.source_name.trim(),
        reference_number: investmentEditForm.reference_number?.trim() || null,
        currency: investmentEditForm.currency.trim().toUpperCase(),
        amount,
        investment_date: investmentEditForm.investment_date,
        payment_source: investmentEditForm.payment_source,
        is_received: investmentEditForm.is_received,
        received_on_date: investmentEditForm.is_received ? investmentEditForm.received_on_date : null,
        expected_date: investmentEditForm.is_received ? null : investmentEditForm.expected_date,
        notes: investmentEditForm.notes?.trim() || null,
      });

      setEditingInvestment(null);
      setInvestmentEditForm(null);
      await fetchInvestments();
      await fetchExpenseIncomeByMonth();
      setInvestmentMessage({ type: 'success', text: 'Investment updated successfully.' });
      setTimeout(() => setInvestmentMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('Error updating investment:', error);
      setInvestmentEditMessage({ type: 'error', text: error.message || 'Failed to update investment.' });
    } finally {
      setSavingEditInvestment(false);
    }
  };

  const handleDeleteExpense = (expenseId) => {
    setDeleteConfirmModal({
      type: 'expense',
      id: expenseId,
      title: 'Delete Expense Record',
      message: 'Are you sure you want to delete this expense record? This action cannot be undone.',
    });
  };

  const handleDeleteInvestment = (investmentId) => {
    setDeleteConfirmModal({
      type: 'investment',
      id: investmentId,
      title: 'Delete Investment Record',
      message: 'Are you sure you want to delete this investment record? This action cannot be undone.',
    });
  };

  const handleConfirmDeleteRecord = async () => {
    if (!deleteConfirmModal) return;

    setDeletingRecord(true);
    try {
      if (deleteConfirmModal.type === 'expense') {
        await adminService.deleteAdminExpense(deleteConfirmModal.id);

        if (editingExpense && editingExpense.id === deleteConfirmModal.id) {
          setEditingExpense(null);
          setExpenseEditForm(null);
        }

        await fetchExpenses();
        await fetchExpenseIncomeByMonth();
        setExpenseMessage({ type: 'success', text: 'Expense deleted successfully.' });
        setTimeout(() => setExpenseMessage({ type: '', text: '' }), 5000);
      } else if (deleteConfirmModal.type === 'investment') {
        await adminService.deleteAdminInvestment(deleteConfirmModal.id);

        if (editingInvestment && editingInvestment.id === deleteConfirmModal.id) {
          setEditingInvestment(null);
          setInvestmentEditForm(null);
        }

        await fetchInvestments();
        await fetchExpenseIncomeByMonth();
        setInvestmentMessage({ type: 'success', text: 'Investment deleted successfully.' });
        setTimeout(() => setInvestmentMessage({ type: '', text: '' }), 5000);
      }

      setDeleteConfirmModal(null);
    } catch (error) {
      console.error('Error deleting record:', error);

      if (deleteConfirmModal.type === 'expense') {
        setExpenseMessage({ type: 'error', text: error.message || 'Failed to delete expense.' });
      } else {
        setInvestmentMessage({ type: 'error', text: error.message || 'Failed to delete investment.' });
      }
    } finally {
      setDeletingRecord(false);
    }
  };

  const handleMarkExpensePaid = async () => {
    if (!markPaidModalExpense) return;
    if (!markPaidOnDate) {
      setExpenseMessage({ type: 'error', text: 'Please select a Paid On date.' });
      return;
    }

    if (markPaidOnDate > formatDateForInput(new Date())) {
      setExpenseMessage({ type: 'error', text: 'Paid On date cannot be in the future.' });
      return;
    }

    setMarkingExpensePaid(true);
    try {
      await adminService.updateAdminExpensePaymentStatus(markPaidModalExpense.id, {
        is_paid: true,
        paid_on_date: markPaidOnDate,
      });
      await fetchExpenses();
      setMarkPaidModalExpense(null);
      setExpenseMessage({ type: 'success', text: 'Expense marked as paid.' });
      setTimeout(() => setExpenseMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('Error updating expense payment status:', error);
      setExpenseMessage({ type: 'error', text: error.message || 'Failed to update expense status.' });
    } finally {
      setMarkingExpensePaid(false);
    }
  };

  const openExpenseNotes = (expense) => {
    setSelectedExpenseForNotes(expense);
  };


  const corporateItemsWithDetails = corporateOrderForm.items.map((item) => {
    const product = products.find((p) => String(p.id) === String(item.product_id));
    const priceType = item.price_type === 'retail' ? 'retail' : 'corporate';
    const price = product
      ? (priceType === 'retail'
        ? (product.price ?? 0)
        : (corporateOrderForm.currency === 'EUR'
          ? (product.wholesale_price_eur ?? product.wholesale_price ?? product.price ?? 0)
          : (product.wholesale_price ?? product.price ?? 0)))
      : 0;
    return {
      ...item,
      name: product ? product.name : 'Unknown product',
      priceType,
      price,
    };
  });

  const corporateOrderTotal = corporateItemsWithDetails.reduce(
    (sum, item) => sum + (item.price || 0) * (item.quantity || 0),
    0
  );

  const corporateCurrencySymbol = corporateOrderForm.currency === 'EUR' ? '€' : 'kr';

  const inferCorporatePriceType = (product, itemPrice) => {
    if (!product) return 'corporate';
    const retailPrice = Number(product.price ?? 0);
    const corporatePrice = Number(product.wholesale_price ?? product.price ?? 0);
    const currentPrice = Number(itemPrice ?? 0);
    const retailDiff = Math.abs(currentPrice - retailPrice);
    const corporateDiff = Math.abs(currentPrice - corporatePrice);
    return retailDiff <= corporateDiff ? 'retail' : 'corporate';
  };


  const recomputeOrderTotals = (order) => {
    const itemsTotal = (order.items || []).reduce(
      (sum, item) => sum + (Number(item.price) || 0) * (Number(item.quantity) || 0),
      0
    );
    const shipping = parseFloat(order.shipping_charges || 0) || 0;
    const discountType = String(order.discount_type || 'percentage').toLowerCase() === 'amount' ? 'amount' : 'percentage';
    const discountPercent = parseFloat(order.discount_percent || 0) || 0;
    const absoluteDiscount = parseFloat(order.discount_amount || 0) || 0;
    const discountAmount = discountType === 'amount'
      ? Math.max(0, Math.min(absoluteDiscount, itemsTotal))
      : (itemsTotal * discountPercent) / 100;
    const grandTotal = itemsTotal + shipping - discountAmount;
    return { itemsTotal, shipping, discountType, discountPercent, discountAmount, grandTotal };
  };


  // Validation functions for corporate orders
  const validateCorporateField = (fieldName, value, form) => {
    let error = '';

    switch (fieldName) {
      case 'customer_name':
        if (!value || value.trim() === '') {
          error = 'Customer name is required';
        } else if (value.trim().length < 2) {
          error = 'Customer name must be at least 2 characters';
        }
        break;

      case 'shop_name':
        if (value && value.trim() !== '' && value.trim().length < 2) {
          error = 'Shop name must be at least 2 characters';
        }
        break;

      case 'contact_name':
        if (value && value.trim() !== '' && value.trim().length < 2) {
          error = 'Contact name must be at least 2 characters';
        }
        break;

      case 'organization_number':
        if (!value || value.trim() === '') {
          error = 'Organization number is required';
        } else if (value.trim().length < 5) {
          error = 'Organization number must be at least 5 characters';
        }
        break;

      case 'vat_number':
        if (form?.country && form.country.trim() !== '' && form.country.trim() !== 'Sweden' && (!value || value.trim() === '')) {
          error = 'VAT number is required for customers outside Sweden';
        }
        break;

      case 'customer_email':
        if (!value || value.trim() === '') {
          error = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = 'Please enter a valid email address';
        }
        break;

      case 'customer_phone':
        if (!value || value.trim() === '') {
          error = 'Phone number is required';
        } else if (!isValidInternationalPhoneNumber(value, form?.country)) {
          error = 'Enter a valid phone number for the selected country';
        }
        break;

      case 'address_line1':
        if (!value || value.trim() === '') {
          error = 'Address Line 1 is required';
        } else if (value.trim().length < 3) {
          error = 'Address must be at least 3 characters';
        }
        break;

      case 'city':
        if (!value || value.trim() === '') {
          error = 'City is required';
        }
        break;

      case 'pincode':
        if (!value || value.trim() === '') {
          error = 'Postal code is required';
        }
        break;

      case 'country':
        if (!value || value.trim() === '') {
          error = 'Country is required';
        }
        break;

      case 'items':
        if (!form.items || form.items.length === 0) {
          error = 'Please add at least one item to the order';
        }
        break;

      default:
        break;
    }

    return error;
  };

  const validateAllCorporateFields = (form) => {
    const errors = {
      customer_name: validateCorporateField('customer_name', form.customer_name, form),
      shop_name: validateCorporateField('shop_name', form.shop_name, form),
      contact_name: validateCorporateField('contact_name', form.contact_name, form),
      organization_number: validateCorporateField('organization_number', form.organization_number, form),
      vat_number: validateCorporateField('vat_number', form.vat_number, form),
      customer_email: validateCorporateField('customer_email', form.customer_email, form),
      customer_phone: validateCorporateField('customer_phone', form.customer_phone, form),
      address_line1: validateCorporateField('address_line1', form.address_line1, form),
      city: validateCorporateField('city', form.city, form),
      pincode: validateCorporateField('pincode', form.pincode, form),
      country: validateCorporateField('country', form.country, form),
      items: validateCorporateField('items', '', form)
    };
    return errors;
  };

  const resetCorporateOrderForm = () => {
    setCorporateOrderForm({
      customer_name: '',
      shop_name: '',
      contact_name: '',
      organization_number: '',
      vat_number: '',
      customer_email: '',
      customer_phone: '',
      address_line1: '',
      address_line2: '',
      city: '',
      pincode: '',
      country: '',
      notes: '',
      currency: 'SEK',
      items: [],
    });
    setCorporateSelection({ product_id: '', quantity: 1, price_type: 'corporate' });
    setCorporateOrderError('');
    setCorporateFieldErrors({
      customer_name: '',
      organization_number: '',
      vat_number: '',
      customer_email: '',
      customer_phone: '',
      address_line1: '',
      city: '',
      pincode: '',
      country: '',
      items: ''
    });
    setCorporateCustomerMatches([]);
  };

  const searchStoredCorporateCustomers = useCallback((inputValue) => {
    const query = String(inputValue || '').trim();

    if (corporateCustomerSearchTimeoutRef.current) {
      clearTimeout(corporateCustomerSearchTimeoutRef.current);
      corporateCustomerSearchTimeoutRef.current = null;
    }

    if (query.length < 2) {
      setSearchingCorporateCustomers(false);
      setCorporateCustomerMatches([]);
      return;
    }

    corporateCustomerSearchTimeoutRef.current = setTimeout(async () => {
      setSearchingCorporateCustomers(true);
      try {
        const matches = await adminService.searchStoredCorporateCustomers(query);
        setCorporateCustomerMatches(Array.isArray(matches) ? matches : []);
      } catch (error) {
        console.error('Error searching stored corporate customers:', error);
        setCorporateCustomerMatches([]);
      } finally {
        setSearchingCorporateCustomers(false);
      }
    }, 250);
  }, []);

  const handleSelectStoredCorporateCustomer = (selectedCustomer) => {
    if (!selectedCustomer) return;

    const normalizedShopName = String(selectedCustomer.shop_name || '').trim();
    const normalizedContactName = String(selectedCustomer.contact_name || '').trim();

    setCorporateOrderForm((prev) => ({
      ...prev,
      customer_name: selectedCustomer.customer_name || prev.customer_name,
      shop_name: normalizedShopName,
      contact_name: normalizedContactName,
      organization_number: selectedCustomer.organization_number || prev.organization_number,
      vat_number: selectedCustomer.vat_number || prev.vat_number,
      customer_email: selectedCustomer.customer_email || prev.customer_email,
      customer_phone: selectedCustomer.customer_phone || prev.customer_phone,
      address_line1: selectedCustomer.address_line1 || prev.address_line1,
      address_line2: selectedCustomer.address_line2 || prev.address_line2,
      city: selectedCustomer.city || prev.city,
      pincode: selectedCustomer.pincode || prev.pincode,
      country: selectedCustomer.country || prev.country,
    }));

    setCorporateFieldErrors((prev) => ({
      ...prev,
      customer_name: '',
      shop_name: '',
      contact_name: '',
      organization_number: '',
      customer_email: '',
      customer_phone: '',
      address_line1: '',
      city: '',
      pincode: '',
      country: '',
    }));

    setCorporateOrderError('');
    setCorporateCustomerMatches([]);
  };

  const handleFetchCorporateDetails = async () => {
    setCorporateOrderError('');

    const organizationNumber = String(corporateOrderForm.organization_number || '').trim();
    const organizationError = validateCorporateField('organization_number', organizationNumber, corporateOrderForm);
    if (organizationError) {
      setCorporateFieldErrors((prev) => ({
        ...prev,
        organization_number: organizationError,
      }));
      return;
    }

    setFetchingCorporateDetails(true);
    try {
      const details = await adminService.fetchCorporateDetailsByOrganizationNumber(organizationNumber);

      setCorporateOrderForm((prev) => ({
        ...prev,
        customer_name: details.customer_name || prev.customer_name,
        shop_name: details.shop_name || prev.shop_name,
        contact_name: details.contact_name || prev.contact_name,
        organization_number: details.organization_number || organizationNumber || prev.organization_number,
        vat_number: details.vat_number || prev.vat_number,
        customer_email: details.customer_email || prev.customer_email,
        customer_phone: details.customer_phone || prev.customer_phone,
        address_line1: details.address_line1 || prev.address_line1,
        address_line2: details.address_line2 || prev.address_line2,
        city: details.city || prev.city,
        pincode: details.pincode || prev.pincode,
        country: details.country || prev.country,
      }));

      setCorporateFieldErrors((prev) => ({
        ...prev,
        customer_name: '',
        shop_name: '',
        contact_name: '',
        organization_number: '',
        address_line1: '',
        city: '',
        pincode: '',
        country: '',
      }));
      setCorporateCustomerMatches([]);
    } catch (error) {
      setCorporateOrderError(error.message || 'Failed to fetch organization details.');
    } finally {
      setFetchingCorporateDetails(false);
    }
  };


  const handleAddCorporateItem = () => {
    setCorporateOrderError('');
    if (!corporateSelection.product_id) {
      setCorporateOrderError('Select a product to add.');
      return;
    }
    const selectedProduct = products.find(
      (p) => String(p.id) === String(corporateSelection.product_id)
    );
    if (!selectedProduct) {
      setCorporateOrderError('Selected product not found.');
      return;
    }
    const stockQuantity = parseInt(selectedProduct.stock_quantity, 10) || 0;
    const defectiveCount = parseInt(selectedProduct.defective_count, 10) || 0;
    const availableStock = Math.max(0, stockQuantity - defectiveCount);

    if (!selectedProduct.in_stock || availableStock < 1) {
      setCorporateOrderError('Selected product is out of stock.');
      return;
    }

    const quantity = Math.max(1, parseInt(corporateSelection.quantity, 10) || 1);
    const priceType = corporateSelection.price_type === 'retail' ? 'retail' : 'corporate';
    const existingIndex = corporateOrderForm.items.findIndex(
      (i) =>
        String(i.product_id) === String(corporateSelection.product_id) &&
        (i.price_type === 'retail' ? 'retail' : 'corporate') === priceType
    );

    const existingQuantity = existingIndex >= 0
      ? Math.max(1, parseInt(corporateOrderForm.items[existingIndex].quantity, 10) || 1)
      : 0;
    const requestedTotalQuantity = existingQuantity + quantity;
    if (requestedTotalQuantity > availableStock) {
      setCorporateOrderError(
        `Insufficient available quantity. Available (stock - defective): ${availableStock}, requested total: ${requestedTotalQuantity}`
      );
      return;
    }

    let updatedItems = [...corporateOrderForm.items];
    if (existingIndex >= 0) {
      const existingItem = updatedItems[existingIndex];
      updatedItems[existingIndex] = { ...existingItem, quantity: existingItem.quantity + quantity };
    } else {
      updatedItems.push({ product_id: corporateSelection.product_id, quantity, price_type: priceType });
    }
    setCorporateOrderForm({ ...corporateOrderForm, items: updatedItems });
    setCorporateSelection({ product_id: '', quantity: 1, price_type: 'corporate' });
  };


  const handleCorporateItemQuantityChange = (productId, priceType, quantity) => {
    const parsedQty = quantity === '' ? '' : Math.max(1, parseInt(quantity, 10) || 1);
    const updatedItems = corporateOrderForm.items.map((item) =>
      String(item.product_id) === String(productId) &&
      (item.price_type === 'retail' ? 'retail' : 'corporate') === (priceType === 'retail' ? 'retail' : 'corporate')
        ? { ...item, quantity: parsedQty }
        : item
    );
    setCorporateOrderForm({ ...corporateOrderForm, items: updatedItems });
  };


  const handleRemoveCorporateItem = (productId, priceType) => {
    const updatedItems = corporateOrderForm.items.filter(
      (item) => !(
        String(item.product_id) === String(productId) &&
        (item.price_type === 'retail' ? 'retail' : 'corporate') === (priceType === 'retail' ? 'retail' : 'corporate')
      )
    );
    setCorporateOrderForm({ ...corporateOrderForm, items: updatedItems });
  };


  const handleCreateCorporateOrder = async (e) => {
    e.preventDefault();
    setCorporateOrderError('');

    // Validate all fields before submission
    const validationErrors = validateAllCorporateFields(corporateOrderForm);
    setCorporateFieldErrors(validationErrors);

    // Check if there are any validation errors
    const hasErrors = Object.values(validationErrors).some(error => error !== '');
    if (hasErrors) {
      return;
    }

    const sanitizedItems = corporateOrderForm.items.map((item) => ({
      product_id: item.product_id,
      quantity: Math.max(1, parseInt(item.quantity, 10) || 1),
      price_type: item.price_type === 'retail' ? 'retail' : 'corporate',
    }));

    // Validate out-of-stock items before creating order
    const hasOutOfStock = sanitizedItems.some((item) => {
      const p = products.find((prod) => String(prod.id) === String(item.product_id));
      if (!p || !p.in_stock) return true;
      const stockQuantity = parseInt(p.stock_quantity, 10) || 0;
      const defectiveCount = parseInt(p.defective_count, 10) || 0;
      const availableStock = Math.max(0, stockQuantity - defectiveCount);
      return availableStock < (Math.max(1, parseInt(item.quantity, 10) || 1));
    });
    if (hasOutOfStock) {
      setCorporateOrderError('One or more selected items exceed available quantity (stock - defective).');
      return;
    }

    // Combine address fields into shipping_address for backend compatibility
    const shipping_address = [
      corporateOrderForm.address_line1,
      corporateOrderForm.address_line2,
      corporateOrderForm.city,
      corporateOrderForm.pincode,
      corporateOrderForm.country
    ].filter(field => field && field.trim() !== '').join(', ');

    setCreatingCorporateOrder(true);
    try {
      await adminService.createCorporateOrder({
        customer_name: corporateOrderForm.customer_name,
        shop_name: corporateOrderForm.shop_name,
        contact_name: corporateOrderForm.contact_name,
        organization_number: corporateOrderForm.organization_number,
        vat_number: corporateOrderForm.vat_number,
        customer_email: corporateOrderForm.customer_email,
        customer_phone: corporateOrderForm.customer_phone,
        shipping_address: shipping_address,
        address_line1: corporateOrderForm.address_line1,
        address_line2: corporateOrderForm.address_line2,
        city: corporateOrderForm.city,
        pincode: corporateOrderForm.pincode,
        country: corporateOrderForm.country,
        notes: corporateOrderForm.notes,
        currency: corporateOrderForm.currency,
        items: sanitizedItems,
      });
      resetCorporateOrderForm();
      setShowCorporateForm(false);
      fetchOrders();
      setOrderMessage({ type: 'success', text: 'Corporate order created successfully. Use Send Email to notify the corporate customer.' });
      setTimeout(() => setOrderMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      const message = error.message || 'Failed to create corporate order. Please try again.';
      setCorporateOrderError(message);
    } finally {
      setCreatingCorporateOrder(false);
    }
  };

  const handleSendCorporateOrderEmail = async (orderId) => {
    setSendingCorporateEmailOrderId(orderId);
    try {
      await adminService.sendCorporateOrderEmail(orderId);
      setOrderMessage({ type: 'success', text: 'Corporate order email queued successfully.' });
      setTimeout(() => setOrderMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('Error sending corporate order email:', error);
      setOrderMessage({ type: 'error', text: error.message || 'Failed to send corporate order email.' });
    } finally {
      setSendingCorporateEmailOrderId(null);
    }
  };


  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordMessage({ type: '', text: '' });


    try {
      await authService.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      setPasswordMessage({ type: 'success', text: 'Password changed successfully!' });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setPasswordMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setPasswordMessage({
        type: 'error',
        text: error.message || 'Failed to change password'
      });
    }
  };

  const handleInventoryFilterChange = async () => {
    // Apply pending filters to active filters
    const nextFilters = { ...pendingInventoryFilters };
    setInventoryFilters(nextFilters);
    setInventoryReportPage(1);
    setInventoryTransactionsPage(1);
  };

  const handleResetInventoryTransactionFilters = async () => {
    const defaultRange = getDefaultTransactionDateRange();
    const resetFilters = {
      product_id: '',
      start_date: defaultRange.start_date,
      end_date: defaultRange.end_date,
      transaction_type: ''
    };

    setPendingInventoryFilters(resetFilters);
    setInventoryFilters(resetFilters);
    setInventoryReportPage(1);
    setInventoryTransactionsPage(1);
  };

  const exportInventoryCSV = () => {
    if (inventoryReport.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = ['Product ID', 'Product Name', 'Category', 'Weight (g)', 'Stock', 'Product Price', 'Purchase Price', 'Total Stock Value', 'Status'];
    const rows = inventoryReport.map(item => {
      const productPrice = parseFloat(item.price || 0);
      const totalPurchasePrice = (item.purchase_price || 0) + (item.wholesale_price_eur || 0);
      return [
        item.id,
        item.name,
        item.category,
        item.weight || '-',
        item.current_stock,
        productPrice.toFixed(2),
        totalPurchasePrice.toFixed(2),
        (item.current_stock * totalPurchasePrice).toFixed(2),
        item.current_stock === 0 ? 'Out of Stock' : 'In Stock'
      ];
    });

    let csvContent = headers.join(',') + '\n';
    rows.forEach(row => {
      csvContent += row.map(cell => `"${cell}"`).join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `inventory-report-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportInventoryTransactionsCSV = () => {
    if (inventoryTransactions.length === 0) {
      alert('No transaction data to export');
      return;
    }

    const headers = ['Transaction ID', 'Date', 'Product', 'Weight (g)', 'Type', 'Quantity', 'Reference', 'Notes', 'Created By'];
    const rows = inventoryTransactions.map(transaction => [
      transaction.id,
      transaction.created_at ? new Date(transaction.created_at).toLocaleDateString() : '-',
      transaction.product_name || '-',
      transaction.weight ? `${transaction.weight}g` : '-',
      transaction.transaction_type || '-',
      transaction.quantity,
      transaction.reference_type === 'order' ? `Order #${transaction.reference_id}` :
        transaction.reference_type === 'return' ? `Return #${transaction.reference_id}` :
          (transaction.reference_type || '-'),
      transaction.notes || '-',
      transaction.created_by || '-',
    ]);

    let csvContent = headers.join(',') + '\n';
    rows.forEach(row => {
      csvContent += row.map(cell => `"${cell}"`).join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `inventory-transactions-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCreateReturn = async (e) => {
    e.preventDefault();
    setOrderMessage({ type: '', text: '' });

    if (!returnFormData.order_id) {
      setOrderMessage({ type: 'error', text: 'Please select an order' });
      return;
    }

    const itemsToReturn = returnFormData.items.filter(item => item.quantity_returned > 0);
    if (itemsToReturn.length === 0) {
      setOrderMessage({ type: 'error', text: 'Please specify at least one item to return with quantity greater than 0' });
      return;
    }

    try {
      // Directly process returns (auto-approved)
      for (const item of itemsToReturn) {
        // Create return with approved status
        const returnResponse = await adminService.createReturn({
          order_id: returnFormData.order_id,
          order_item_id: item.order_item_id,
          product_id: item.product_id,
          quantity_returned: item.quantity_returned,
          reason: returnFormData.reason || '',
          notes: returnFormData.notes || '',
          status: 'approved'
        });
        
        // Immediately approve the return to restore inventory
        if (returnResponse.id) {
          await adminService.updateReturnStatus(returnResponse.id, 'approved');
        }
      }
      
      setOrderMessage({ type: 'success', text: `Order returned successfully for ${itemsToReturn.length} item(s). Inventory has been updated.` });
      setShowReturnForm(false);
      setReturnFormData({
        order_id: '',
        items: [],
        reason: '',
        notes: ''
      });
      setReturnOrderInput('');
      
      // Refresh orders to reflect the returns
      // Add a small delay to ensure backend has processed the returns
      setTimeout(() => {
        fetchOrders();
      }, 500);
      
      setTimeout(() => setOrderMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      setOrderMessage({ type: 'error', text: error.response?.data?.error || 'Failed to process return' });
    }
  };

  const getEligibleReturnOrders = () => {
    const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
    const now = Date.now();

    return orders.filter((o) => {
      const status = String(o.status || '').toLowerCase();
      if (status !== 'shipped' && status !== 'delivered') {
        return false;
      }

      const createdAtMs = new Date(o.created_at).getTime();
      if (Number.isNaN(createdAtMs)) {
        return false;
      }

      return (now - createdAtMs) <= THIRTY_DAYS_MS;
    });
  };

  const handleReturnOrderInputChange = (value) => {
    setReturnOrderInput(value);

    const selectedOrder = getEligibleReturnOrders().find(
      (o) => String(o.order_number || '').toLowerCase() === String(value || '').trim().toLowerCase()
    );

    if (!selectedOrder) {
      setReturnFormData((prev) => ({
        ...prev,
        order_id: '',
        items: []
      }));
      return;
    }

    setReturnFormData((prev) => ({
      ...prev,
      order_id: String(selectedOrder.id),
      items: selectedOrder?.items?.map(item => ({
        order_item_id: item.id,
        product_id: item.product_id,
        product_name: item.product_name,
        max_quantity: item.quantity,
        quantity_returned_already: item.quantity_returned || 0,
        available_quantity: item.quantity_available !== undefined ? item.quantity_available : item.quantity,
        quantity_returned: 0
      })) || []
    }));
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      // Create FormData to handle file upload
      const formData = new FormData();
      formData.append('name', productForm.name);
      formData.append('category', productForm.category);
      formData.append('description', productForm.description);
      formData.append('ingredients', sanitizeRichText(productForm.ingredients));
      formData.append('price', productForm.price);
      formData.append('wholesale_price', productForm.wholesale_price || 0);
      formData.append('purchase_price', 0);
      formData.append('wholesale_price_eur', productForm.wholesale_price_eur || 0);
      formData.append('vat_percent', productForm.vat_percent || 0);
      formData.append('discount_percent', productForm.discount_percent || 0);
      formData.append('weight', productForm.weight || 0);
      formData.append('image_url', productForm.image_url);
      formData.append('in_stock', productForm.in_stock);
      formData.append('stock_quantity', productForm.stock_quantity);
      formData.append('gluten_free', productForm.gluten_free);
      formData.append('barcode', productForm.barcode);
      formData.append('batch_code', productForm.batch_code);
      formData.append('show_on_website', productForm.show_on_website);
     
      // Add files if uploaded
      if (productForm.image_files && productForm.image_files.length > 0) {
        productForm.image_files.forEach((file) => {
          formData.append('images', file);
        });
      }


      if (editingProduct) {
        await adminService.updateProduct(editingProduct.id, formData);
      } else {
        await adminService.createProduct(formData);
      }
      setShowProductForm(false);
      setEditingProduct(null);
      setProductForm({
        name: '',
        category: '',
        description: '',
        ingredients: '',
        price: '',
        wholesale_price: '',
        purchase_price: 0,
        wholesale_price_eur: 0,
        vat_percent: '',
        discount_percent: '',
        image_url: '',
        image_files: [],
        existing_images: [],
        in_stock: true,
        stock_quantity: '',
        gluten_free: false,
        barcode: '',
        show_on_website: true
      });
      fetchProducts();
      setProductMessage({ type: 'success', text: editingProduct ? 'Product updated successfully!' : 'Product added successfully!' });
      setTimeout(() => setProductMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('Error saving product:', error);
      setProductMessage({ type: 'error', text: 'Failed to save product. Please try again.' });
    }
  };


  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setImageUploadError('');
    setExistingImagesSlide(0);
    setProductForm({
      name: product.name,
      category: product.category,
      description: product.description || '',
      ingredients: sanitizeRichText(product.ingredients || ''),
      price: product.price,
      wholesale_price: product.wholesale_price ?? '',
      purchase_price: 0,
      wholesale_price_eur: product.wholesale_price_eur ?? 0,
      vat_percent: product.vat_percent ?? '',
      discount_percent: product.discount_percent ?? '',
      weight: product.weight ?? '',
      image_url: product.image_url || '',
      image_files: [],
      existing_images: Array.isArray(product.images) ? product.images : (product.image_url ? [product.image_url] : []),
      in_stock: product.in_stock,
      stock_quantity: product.stock_quantity ?? '',
      gluten_free: product.gluten_free || false,
      barcode: product.barcode || '',
      batch_code: '',
      show_on_website: product.show_on_website !== false
    });
    setShowProductForm(true);
  };


  const handleToggleProductStatus = async (id, currentStatus) => {
    try {
      await adminService.toggleProductStatus(id, !currentStatus);
      fetchProducts();
      setProductMessage({ type: 'success', text: 'Product status updated successfully!' });
      setTimeout(() => setProductMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('Error toggling product status:', error);
      setProductMessage({ type: 'error', text: 'Failed to update product status. Please try again.' });
    }
  };

  const openBatchModal = async (product) => {
    try {
      const batches = await adminService.getProductBatches(product.id);
      setBatchHistory(batches || []);
      setBatchModalProduct(product);
      setBatchForm({ batch_code: '', quantity: '', purchase_price: '', supplier_name: '', mfg_date: '', expiry_date: '' });
      setBatchMessage({ type: '', text: '' });
      setShowBatchModal(true);
    } catch (error) {
      console.error('Error loading batch history:', error);
      setProductMessage({ type: 'error', text: 'Failed to load batch history' });
      setTimeout(() => setProductMessage({ type: '', text: '' }), 5000);
    }
  };

  const handleCreateBatch = async (e) => {
    e.preventDefault();
    if (!batchForm.mfg_date) {
      setBatchMessage({ type: 'error', text: 'Manufacturing date is required' });
      return;
    }

    if (!batchForm.batch_code || !batchForm.quantity || !String(batchForm.supplier_name || '').trim()) {
      setBatchMessage({ type: 'error', text: 'Batch code, quantity, and supplier name are required' });
      return;
    }

    if (batchForm.expiry_date && batchForm.expiry_date < batchForm.mfg_date) {
      setBatchMessage({ type: 'error', text: 'Exp. Date must be on or after Mfg. Date' });
      return;
    }

    const normalizedBatchCode = String(batchForm.batch_code).trim().toLowerCase();
    
    // Check if batch code already exists
    if (batchHistory.some(b => String(b.batch_code).trim().toLowerCase() === normalizedBatchCode)) {
      setBatchMessage({ type: 'error', text: 'Batch code already exists for this product' });
      return;
    }

    const qty = parseInt(batchForm.quantity, 10);
    if (Number.isNaN(qty) || qty <= 0) {
      setBatchMessage({ type: 'error', text: 'Quantity must be a positive number' });
      return;
    }

    let purchasePrice = 0;
    if (batchForm.purchase_price !== '' && batchForm.purchase_price !== null && batchForm.purchase_price !== undefined) {
      purchasePrice = parseFloat(batchForm.purchase_price);
      if (Number.isNaN(purchasePrice) || purchasePrice < 0) {
        setBatchMessage({ type: 'error', text: 'Purchase price must be a valid non-negative number' });
        return;
      }
    }

    let purchasePriceEur = 0;
    if (batchForm.purchase_price_eur !== '' && batchForm.purchase_price_eur !== null && batchForm.purchase_price_eur !== undefined) {
      purchasePriceEur = parseFloat(batchForm.purchase_price_eur);
      if (Number.isNaN(purchasePriceEur) || purchasePriceEur < 0) {
        setBatchMessage({ type: 'error', text: 'Purchase price (EUR) must be a valid non-negative number' });
        return;
      }
    }

    setCreatingBatch(true);
    try {
      await adminService.createProductBatch(batchModalProduct.id, {
        batch_code: batchForm.batch_code,
        quantity: qty,
        purchase_price: purchasePrice,
        purchase_price_eur: purchasePriceEur,
        supplier_name: String(batchForm.supplier_name).trim(),
        mfg_date: batchForm.mfg_date,
        expiry_date: batchForm.expiry_date || null
      });
      setBatchMessage({ type: 'success', text: 'Batch added successfully!' });
      setBatchForm({ batch_code: '', quantity: '', purchase_price: '', purchase_price_eur: '', supplier_name: '', mfg_date: '', expiry_date: '' });
      // Refresh batch history
      const batches = await adminService.getProductBatches(batchModalProduct.id);
      setBatchHistory(batches || []);
      // Refresh products to update stock
      await fetchProducts();
      setTimeout(() => setBatchMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error creating batch:', error);
      setBatchMessage({ type: 'error', text: error.message || 'Failed to create batch' });
    } finally {
      setCreatingBatch(false);
    }
  };

  const handleEditBatch = (batch) => {
    setEditingBatchId(batch.id);
    setBatchForm({
      batch_code: batch.batch_code,
      quantity: String(batch.quantity),
      supplier_name: batch.supplier_name || '',
      purchase_price: batch.purchase_price ?? '',
      purchase_price_eur: batch.purchase_price_eur ?? '',
      mfg_date: formatDateYMD(batch.mfg_date) === '-' ? '' : formatDateYMD(batch.mfg_date),
      expiry_date: formatDateYMD(batch.expiry_date, '')
    });
  };

  const handleCancelEditBatch = () => {
    setEditingBatchId(null);
    setBatchForm({ batch_code: '', quantity: '', purchase_price: '', purchase_price_eur: '', supplier_name: '', mfg_date: '', expiry_date: '' });
    setBatchMessage({ type: '', text: '' });
  };

  const handleSaveEditBatch = async (batch) => {
    const qty = parseInt(batchForm.quantity, 10);
    if (Number.isNaN(qty) || qty <= 0) {
      setBatchMessage({ type: 'error', text: 'Quantity must be a positive number' });
      return;
    }

    let purchasePrice = 0;
    if (batchForm.purchase_price !== '' && batchForm.purchase_price !== null && batchForm.purchase_price !== undefined) {
      purchasePrice = parseFloat(batchForm.purchase_price);
      if (Number.isNaN(purchasePrice) || purchasePrice < 0) {
        setBatchMessage({ type: 'error', text: 'Purchase price must be a valid non-negative number' });
        return;
      }
    }

    let purchasePriceEur = 0;
    if (batchForm.purchase_price_eur !== '' && batchForm.purchase_price_eur !== null && batchForm.purchase_price_eur !== undefined) {
      purchasePriceEur = parseFloat(batchForm.purchase_price_eur);
      if (Number.isNaN(purchasePriceEur) || purchasePriceEur < 0) {
        setBatchMessage({ type: 'error', text: 'Purchase price (EUR) must be a valid non-negative number' });
        return;
      }
    }

    if (qty === batch.quantity && purchasePrice === (batch.purchase_price ?? 0) && purchasePriceEur === (batch.purchase_price_eur ?? 0)) {
      setBatchMessage({ type: 'error', text: 'No changes were made' });
      return;
    }

    setCreatingBatch(true);
    try {
      await adminService.updateProductBatch(batchModalProduct.id, batch.id, {
        quantity: qty,
        purchase_price: purchasePrice,
        purchase_price_eur: purchasePriceEur
      });
      setBatchMessage({ type: 'success', text: 'Batch updated successfully!' });
      setBatchForm({ batch_code: '', quantity: '', purchase_price: '', purchase_price_eur: '', supplier_name: '', mfg_date: '', expiry_date: '' });
      setEditingBatchId(null);
      // Refresh batch history
      const batches = await adminService.getProductBatches(batchModalProduct.id);
      setBatchHistory(batches || []);
      // Refresh products to update stock
      await fetchProducts();
      setTimeout(() => setBatchMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error updating batch:', error);
      setBatchMessage({ type: 'error', text: error.message || 'Failed to update batch' });
    } finally {
      setCreatingBatch(false);
    }
  };

  const handleUpdateDefectiveCount = async (product) => {
    try {
      const rawValue = defectiveInputCounts[product.id];
      const parsed = parseInt(rawValue, 10);
      const nextDefectiveCount = Number.isNaN(parsed) ? 0 : Math.max(0, parsed);

      await adminService.updateProductDefectiveCount(product.id, nextDefectiveCount);
      setDefectiveInputCounts((prev) => ({ ...prev, [product.id]: 0 }));
      await fetchProducts();

      setProductMessage({ type: 'success', text: 'Defective count updated successfully!' });
      setTimeout(() => setProductMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('Error updating defective count:', error);
      setProductMessage({ type: 'error', text: error.message || 'Failed to update defective count. Please try again.' });
    }
  };

  const handleUpdateUsedSampleCount = async (product) => {
    try {
      const rawValue = sampleUsedInputCounts[product.id];
      const parsed = parseInt(rawValue, 10);
      const usedCount = Number.isNaN(parsed) ? 0 : Math.max(0, parsed);

      await adminService.updateProductUsedSampleCount(product.id, usedCount);
      setSampleUsedInputCounts((prev) => ({ ...prev, [product.id]: 0 }));
      await fetchProducts();

      setProductMessage({ type: 'success', text: 'Used sample count updated successfully!' });
      setTimeout(() => setProductMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('Error updating used sample count:', error);
      setProductMessage({ type: 'error', text: error.message || 'Failed to update used sample count. Please try again.' });
    }
  };


  const handleOrderStatusUpdate = async (orderId, status) => {
    try {
      await adminService.updateOrderStatus(orderId, status);
      fetchOrders();
      if (editingOrder && editingOrder.id === orderId) {
        setEditingOrder({...editingOrder, status});
      }
      setOrderMessage({ type: 'success', text: 'Order status updated successfully!' });
      setTimeout(() => setOrderMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('Error updating order status:', error);
      setOrderMessage({ type: 'error', text: 'Failed to update order status. Please try again.' });
    }
  };

  const handleOrderPaymentUpdate = async (orderId, isPaid) => {
    try {
      await adminService.updateOrderPaymentStatus(orderId, isPaid);
      await fetchOrders();
      if (editingOrder && editingOrder.id === orderId) {
        setEditingOrder({
          ...editingOrder,
          is_paid: isPaid,
          paid_at: isPaid ? new Date().toISOString() : null
        });
      }
      setOrderMessage({ type: 'success', text: `Order marked as ${isPaid ? 'paid' : 'unpaid'} successfully!` });
      setTimeout(() => setOrderMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('Error updating order payment status:', error);
      setOrderMessage({ type: 'error', text: 'Failed to update payment status. Please try again.' });
    }
  };


  const handleRemoveOrderItem = async (orderId, itemId) => {
    if (!window.confirm('Remove this item from the order?')) return;
   
    try {
      // Update only local state - persistence happens on Done Editing
      if (editingOrder && editingOrder.id === orderId) {
        const updatedItems = editingOrder.items.filter(item => item.id !== itemId);
        const totals = recomputeOrderTotals({ ...editingOrder, items: updatedItems });
        setEditingOrder({
          ...editingOrder,
          items: updatedItems,
          total_amount: totals.itemsTotal,
          computed_total_with_shipping: totals.grandTotal,
        });
      }
      setEditOrderMessage({ type: 'success', text: 'Item removed. Click "Done Editing" to save changes.' });
      setTimeout(() => setEditOrderMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('Error removing order item:', error);
      setEditOrderMessage({ type: 'error', text: 'Failed to remove item. Please try again.' });
      setTimeout(() => setEditOrderMessage({ type: '', text: '' }), 5000);
    }
  };


  const handleUpdateOrderItemQuantity = async (orderId, itemId, quantity) => {
    const parsedQty = parseInt(quantity, 10);
    const newQuantity = quantity === ''
      ? ''
      : (Number.isNaN(parsedQty) || parsedQty < 1 ? 1 : parsedQty);

    // Update only locally; persistence happens on Done Editing
    setEditingOrder((prev) => {
      if (!prev || prev.id !== orderId) return prev;
      const updatedItems = (prev.items || []).map((item) =>
        item.id === itemId ? { ...item, quantity: newQuantity } : item
      );
      const updatedOrder = { ...prev, items: updatedItems };
      const totals = recomputeOrderTotals(updatedOrder);
      return { ...updatedOrder, total_amount: totals.itemsTotal, computed_total_with_shipping: totals.grandTotal };
    });
  };


  const handleUpdateOrderItemPrice = async (orderId, itemId, price) => {
    const parsedPrice = parseFloat(price);
    const newPrice = price === ''
      ? ''
      : (Number.isNaN(parsedPrice) || parsedPrice < 0 ? 0 : parsedPrice);

    // Update only locally; persistence happens on Done Editing
    setEditingOrder((prev) => {
      if (!prev || prev.id !== orderId) return prev;
      const updatedItems = (prev.items || []).map((item) =>
        item.id === itemId ? { ...item, price: newPrice } : item
      );
      const updatedOrder = { ...prev, items: updatedItems };
      const totals = recomputeOrderTotals(updatedOrder);
      return { ...updatedOrder, total_amount: totals.itemsTotal, computed_total_with_shipping: totals.grandTotal };
    });
  };


  const handleContactStatusUpdate = async (contactId, status) => {
    try {
      const token = localStorage.getItem('auth_token');
      await fetch(`${API_BASE_URL}/admin/contacts`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status, contactId })
      });
      fetchContacts();
    } catch (error) {
      console.error('Error updating contact status:', error);
    }
  };


  const handleDownloadInvoice = async (orderId, orderNumber) => {
    try {
      await invoiceService.downloadInvoice(orderId, orderNumber);
      setOrderMessage({ type: 'success', text: 'Invoice downloaded successfully!' });
      setTimeout(() => setOrderMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      setOrderMessage({ type: 'error', text: 'Failed to download invoice. Please try again.' });
    }
  };


  const handleDownloadAllInvoices = async () => {
    try {
      // Get delivered orders with the current filters applied
      const twoMonthsAgo = new Date();
      twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);


      let filteredOrders = orders.filter(o => o.status === 'delivered');


      // Apply date filter
      if (dateFilter.startDate || dateFilter.endDate) {
        filteredOrders = filteredOrders.filter(o => {
          const orderDate = new Date(o.created_at);
          if (dateFilter.startDate) {
            const startDate = new Date(dateFilter.startDate);
            if (orderDate < startDate) return false;
          }
          if (dateFilter.endDate) {
            const endDate = new Date(dateFilter.endDate);
            endDate.setHours(23, 59, 59);
            if (orderDate > endDate) return false;
          }
          return true;
        });
      } else {
        filteredOrders = filteredOrders.filter(o => new Date(o.created_at) >= twoMonthsAgo);
      }


      if (filteredOrders.length === 0) {
        setOrderMessage({ type: 'error', text: 'No delivered orders to download' });
        setTimeout(() => setOrderMessage({ type: '', text: '' }), 5000);
        return;
      }


      // Get order IDs
      const orderIds = filteredOrders.map(o => o.id);


      // Show loading state
      setLoading(true);


      // Fetch batch invoices
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/invoice/orders/batch/invoices`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ orderIds })
      });

      if (!response.ok) {
        throw new Error('Failed to download batch invoices');
      }

      const data = await response.json();

      // Create ZIP file
      const zip = new JSZip();
      data.invoices.forEach((invoice) => {
        const binaryString = atob(invoice.buffer);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        zip.file(invoice.filename, bytes);
      });

      // Generate and download ZIP
      zip.generateAsync({ type: 'blob' }).then((content) => {
        const url = window.URL.createObjectURL(content);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Invoices_${new Date().getTime()}.zip`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
      });


      setLoading(false);
      setOrderMessage({ type: 'success', text: 'All invoices downloaded successfully!' });
      setTimeout(() => setOrderMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error downloading all invoices:', error);
      setOrderMessage({ type: 'error', text: 'Failed to download invoices. Please try again.' });
      setLoading(false);
    }
  };


const handleAddProductToOrder = () => {
    if (!addProductToOrder.product_id) {
      setEditOrderMessage({ type: 'error', text: 'Please select a product' });
      setTimeout(() => setEditOrderMessage({ type: '', text: '' }), 3000);
      return;
    }
   
    const newQuantity = parseInt(addProductToOrder.quantity, 10) || 1;
    const selectedProduct = products.find(
      (p) => p.id === parseInt(addProductToOrder.product_id, 10)
    );

    if (!selectedProduct) {
      setEditOrderMessage({ type: 'error', text: 'Selected product not found' });
      setTimeout(() => setEditOrderMessage({ type: '', text: '' }), 3000);
      return;
    }

    const availableStock = Math.max(
      0,
      (parseInt(selectedProduct.stock_quantity, 10) || 0) - (parseInt(selectedProduct.defective_count, 10) || 0)
    );

    // Validate available quantity using Stock - Defective
    if (!selectedProduct.in_stock || availableStock < 1) {
      setEditOrderMessage({ type: 'error', text: 'Selected product is out of stock (after defective adjustment)' });
      setTimeout(() => setEditOrderMessage({ type: '', text: '' }), 3000);
      return;
    }

    if (newQuantity > availableStock) {
      setEditOrderMessage({
        type: 'error',
        text: `Insufficient available quantity. Available (stock - defective): ${availableStock}, requested: ${newQuantity}`
      });
      setTimeout(() => setEditOrderMessage({ type: '', text: '' }), 3000);
      return;
    }

    const isCorporateOrder = Boolean(editingOrder?.organization_number);
    const selectedPriceType = isCorporateOrder
      ? (addProductToOrder.price_type === 'retail' ? 'retail' : 'corporate')
      : 'retail';
    const price = isCorporateOrder
      ? (selectedPriceType === 'retail'
        ? (selectedProduct.price ?? 0)
        : (selectedProduct.wholesale_price ?? selectedProduct.price ?? 0))
      : (selectedProduct.price ?? 0);

    setEditingOrder((prev) => {
      if (!prev) return prev;
      
      const existingIndex = (prev.items || []).findIndex((item) => {
        if (String(item.product_id) !== String(selectedProduct.id)) return false;
        if (!isCorporateOrder) return true;
        const itemType = item.price_type || inferCorporatePriceType(selectedProduct, item.price);
        return itemType === selectedPriceType;
      });
      
      let updatedItems;
      if (existingIndex >= 0) {
        // Merge with existing item
        updatedItems = [...prev.items];
        const existing = updatedItems[existingIndex];
        updatedItems[existingIndex] = {
          ...existing,
          quantity: (existing.quantity || 0) + newQuantity,
          price: isCorporateOrder ? price : (existing.price ?? price),
          ...(isCorporateOrder ? { price_type: selectedPriceType } : {})
        };
      } else {
        // Add new item with temporary negative ID to distinguish from persisted items
        const newItem = {
          id: -Date.now(), // Temporary ID (negative)
          product_id: selectedProduct.id,
          product_name: selectedProduct.name,
          price,
          quantity: newQuantity,
          ...(isCorporateOrder ? { price_type: selectedPriceType } : {})
        };
        updatedItems = [...(prev.items || []), newItem];
      }
      
      const totals = recomputeOrderTotals({ ...prev, items: updatedItems });
      return {
        ...prev,
        items: updatedItems,
        total_amount: totals.itemsTotal,
        computed_total_with_shipping: totals.grandTotal
      };
    });

    setAddProductToOrder({
      product_id: '',
      quantity: 1,
      price_type: isCorporateOrder ? selectedPriceType : 'retail'
    });
  };

  const handleOrderItemPriceTypeChange = (itemId, selectedType) => {
    const normalizedType = selectedType === 'retail' ? 'retail' : 'corporate';
    setEditingOrder((prev) => {
      if (!prev || !prev.organization_number) return prev;
      const updatedItems = (prev.items || []).map((item) => {
        if (item.id !== itemId) return item;
        const product = products.find((p) => String(p.id) === String(item.product_id));
        if (!product) return { ...item, price_type: normalizedType };
        const nextPrice = normalizedType === 'retail'
          ? (product.price ?? 0)
          : (product.wholesale_price ?? product.price ?? 0);
        return {
          ...item,
          price_type: normalizedType,
          price: nextPrice,
        };
      });
      const totals = recomputeOrderTotals({ ...prev, items: updatedItems });
      return {
        ...prev,
        items: updatedItems,
        total_amount: totals.itemsTotal,
        computed_total_with_shipping: totals.grandTotal
      };
    });
  };

  const handleShippingInputChange = (orderId, value, commit = false) => {
    const isEmpty = value === '';
    const parsed = isEmpty ? 0 : (parseFloat(value) || 0);
    const shippingValue = commit ? parsed : (isEmpty ? '' : parsed);

    setEditingOrder((prev) => {
      if (!prev || prev.id !== orderId) return prev;
      const updated = { ...prev, shipping_charges: shippingValue };
      const totals = recomputeOrderTotals({ ...updated, shipping_charges: parsed });
      return { ...updated, total_amount: totals.itemsTotal, computed_total_with_shipping: totals.grandTotal };
    });
  };

  const handleApplyDiscount = () => {
    if (!editingOrder || discountForm.value === '') {
      setEditOrderMessage({ type: 'error', text: 'Please enter a discount value' });
      setTimeout(() => setEditOrderMessage({ type: '', text: '' }), 5000);
      return;
    }

    const discountType = discountForm.type === 'amount' ? 'amount' : 'percentage';
    const discountValue = parseFloat(discountForm.value);
    if (isNaN(discountValue) || discountValue < 0) {
      setEditOrderMessage({ type: 'error', text: 'Discount value must be a non-negative number' });
      setTimeout(() => setEditOrderMessage({ type: '', text: '' }), 5000);
      return;
    }

    const orderSubtotal = parseFloat(editingOrder.total_amount || 0) || 0;

    if (discountType === 'percentage' && discountValue > 100) {
      setEditOrderMessage({ type: 'error', text: 'Discount percentage must be between 0 and 100' });
      setTimeout(() => setEditOrderMessage({ type: '', text: '' }), 5000);
      return;
    }

    if (discountType === 'amount' && discountValue > orderSubtotal) {
      setEditOrderMessage({ type: 'error', text: 'Discount amount cannot exceed order subtotal' });
      setTimeout(() => setEditOrderMessage({ type: '', text: '' }), 5000);
      return;
    }

    const nextPercent = discountType === 'percentage'
      ? discountValue
      : (orderSubtotal > 0 ? (discountValue / orderSubtotal) * 100 : 0);
    const nextAmount = discountType === 'amount'
      ? discountValue
      : (orderSubtotal * discountValue) / 100;

    // Update only local state - will be saved on "Done Editing"
    const totals = recomputeOrderTotals({
      ...editingOrder,
      discount_type: discountType,
      discount_percent: nextPercent,
      discount_amount: nextAmount
    });
    
    setEditingOrder({
      ...editingOrder,
      discount_type: discountType,
      discount_percent: nextPercent,
      discount_amount: nextAmount,
      computed_total_with_shipping: totals.grandTotal
    });

    setEditOrderMessage({ type: 'success', text: 'Discount applied. Click "Done Editing" to save.' });
    setTimeout(() => setEditOrderMessage({ type: '', text: '' }), 5000);
    setDiscountForm({ type: discountType, value: '' });
  };

  const handleRemoveDiscount = () => {
    if (!editingOrder) return;

    const totals = recomputeOrderTotals({
      ...editingOrder,
      discount_type: editingOrder.discount_type || 'percentage',
      discount_percent: 0,
      discount_amount: 0
    });
    
    setEditingOrder({
      ...editingOrder,
      discount_percent: 0,
      discount_amount: 0,
      computed_total_with_shipping: totals.grandTotal
    });

    setEditOrderMessage({ type: 'success', text: 'Discount removed. Click "Done Editing" to save.' });
    setTimeout(() => setEditOrderMessage({ type: '', text: '' }), 5000);
    setDiscountForm((prev) => ({ ...prev, value: '' }));
  };

  const handleSaveOrderEdits = async () => {
    if (!editingOrder || !originalOrder) {
      setShowOrderDetail(false);
      setEditingOrder(null);
      return;
    }

    setSavingOrder(true);
    try {
      const toNumber = (value, fallback = 0) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : fallback;
      };

      const toInt = (value, fallback = 0) => {
        const parsed = parseInt(value, 10);
        return Number.isNaN(parsed) ? fallback : parsed;
      };

      const apiRequest = async (url, options) => {
        const response = await fetch(url, options);
        if (!response.ok) {
          let errorMessage = `Request failed (${response.status})`;
          let rawBody = '';

          try {
            rawBody = await response.text();
          } catch (_textErr) {
            rawBody = '';
          }

          if (rawBody) {
            try {
              const parsed = JSON.parse(rawBody);
              errorMessage = parsed?.error || rawBody || errorMessage;
            } catch (_jsonErr) {
              errorMessage = rawBody;
            }
          } else if (response.statusText) {
            errorMessage = `${errorMessage} ${response.statusText}`;
          }

          throw new Error(errorMessage);
        }
        return response;
      };

      // Basic validation: ensure all quantities are at least 1 (and not empty)
      const invalidQty = (editingOrder.items || []).find((item) => {
        const q = item.quantity;
        const parsed = parseInt(q, 10);
        return q === '' || Number.isNaN(parsed) || parsed < 1;
      });
      if (invalidQty) {
        setEditOrderMessage({ type: 'error', text: 'Please ensure all item quantities are at least 1 before saving.' });
        setTimeout(() => setEditOrderMessage({ type: '', text: '' }), 5000);
        setSavingOrder(false);
        return;
      }

      const normalizedShipping = toNumber(editingOrder.shipping_charges, 0);
      const originalShipping = toNumber(originalOrder.shipping_charges, 0);
      const normalizedNotes = String(editingOrder.notes || '').trim();
      const originalNotes = String(originalOrder.notes || '').trim();
      const normalizedDiscountType = editingOrder.discount_type === 'amount' ? 'amount' : 'percentage';
      const originalDiscountType = originalOrder.discount_type === 'amount' ? 'amount' : 'percentage';
      const normalizedDiscountPercent = toNumber(editingOrder.discount_percent, 0);
      const originalDiscountPercent = toNumber(originalOrder.discount_percent, 0);
      const normalizedDiscountAmount = toNumber(editingOrder.discount_amount, 0);
      const originalDiscountAmount = toNumber(originalOrder.discount_amount, 0);

      // Persist notes if changed
      if (normalizedNotes !== originalNotes) {
        const token = localStorage.getItem('auth_token');
        await apiRequest(`${API_BASE_URL}/admin/orders/${editingOrder.id}/notes`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ notes: normalizedNotes })
        });
      }

      // Persist shipping if changed
      if (normalizedShipping !== originalShipping) {
        const token = localStorage.getItem('auth_token');
        await apiRequest(`${API_BASE_URL}/admin/orders/${editingOrder.id}/shipping`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ shipping_charges: normalizedShipping })
        });
      }

      // Identify and persist newly added items (negative or missing IDs)
      const originalItemIds = new Set((originalOrder.items || []).map((item) => item.id));
      const newItems = (editingOrder.items || []).filter((item) => item.id < 0 || !originalItemIds.has(item.id));

      for (const item of newItems) {
        const token = localStorage.getItem('auth_token');
        const normalizedQty = toInt(item.quantity, 1);
        const normalizedPrice = toNumber(item.price, 0);
        const isCorporateOrder = Boolean(editingOrder.organization_number);
        await apiRequest(`${API_BASE_URL}/admin/orders/${editingOrder.id}/items`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            product_id: item.product_id,
            quantity: normalizedQty,
            price: normalizedPrice,
            ...(isCorporateOrder ? { price_type: item.price_type === 'retail' ? 'retail' : 'corporate' } : {})
          })
        });
      }

      // Persist quantity and price-type changes for existing items
      const originalMap = new Map((originalOrder.items || []).map((item) => [item.id, {
        quantity: toInt(item.quantity, 0),
        price: toNumber(item.price, 0),
        price_type: item.price_type || 'corporate'
      }]));
      const changedItems = (editingOrder.items || []).filter(
        (item) => {
          if (!(item.id > 0 && originalItemIds.has(item.id))) return false;
          const originalItem = originalMap.get(item.id);
          if (!originalItem) return false;
          const quantityChanged = originalItem.quantity !== toInt(item.quantity, 0);
          const priceChanged = originalItem.price !== toNumber(item.price, 0);
          const isCorporateOrder = Boolean(editingOrder.organization_number);
          if (!isCorporateOrder) return quantityChanged || priceChanged;
          const product = products.find((p) => String(p.id) === String(item.product_id));
          const currentType = item.price_type || inferCorporatePriceType(product, item.price);
          const originalType = originalItem.price_type || inferCorporatePriceType(product, originalItem.price);
          return quantityChanged || priceChanged || currentType !== originalType;
        }
      );

      for (const item of changedItems) {
        const token = localStorage.getItem('auth_token');
        const normalizedQty = toInt(item.quantity, 1);
        const normalizedPrice = toNumber(item.price, 0);
        const isCorporateOrder = Boolean(editingOrder.organization_number);
        await apiRequest(`${API_BASE_URL}/admin/orders/${editingOrder.id}/items/${item.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            quantity: normalizedQty,
            price: normalizedPrice,
            ...(isCorporateOrder ? { price_type: item.price_type === 'retail' ? 'retail' : 'corporate' } : {})
          })
        });
      }

      // Identify and persist removed items (items that were in original but not in current)
      const currentItemIds = new Set((editingOrder.items || []).map((item) => item.id).filter((id) => id > 0));
      const removedItemIds = Array.from(originalItemIds).filter((id) => !currentItemIds.has(id));

      for (const itemId of removedItemIds) {
        const token = localStorage.getItem('auth_token');
        await apiRequest(`${API_BASE_URL}/admin/orders/${editingOrder.id}/items/${itemId}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
      }

      const shouldPersistDiscount =
        normalizedDiscountType !== originalDiscountType ||
        normalizedDiscountPercent !== originalDiscountPercent ||
        normalizedDiscountAmount !== originalDiscountAmount;

      if (shouldPersistDiscount) {
        const token = localStorage.getItem('auth_token');
        await apiRequest(`${API_BASE_URL}/admin/orders/${editingOrder.id}/discount`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            discount_type: normalizedDiscountType,
            discount_percent: normalizedDiscountPercent,
            discount_amount: normalizedDiscountAmount
          })
        });
      }

      await fetchOrders();
      
      // Publish all order edits to Service Bus after changes are saved
      const token = localStorage.getItem('auth_token');
      try {
        await fetch(`${API_BASE_URL}/admin/orders/${editingOrder.id}/publish-edits`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
      } catch (publishError) {
        console.error('Error publishing order edits to service bus:', publishError);
        // Don't fail the UI - this is a non-critical async operation
      }
      
      setOriginalOrder(JSON.parse(JSON.stringify(editingOrder)));
      setShowOrderDetail(false);
      setEditingOrder(null);
      setOrderMessage({ type: 'success', text: 'Order changes saved successfully!' });
      setTimeout(() => setOrderMessage({ type: '', text: '' }), 5000);
    } catch (error) {
      console.error('Error saving order edits:', error);
      const errorText = error.message || 'Failed to save order changes. Please try again.';
      setEditOrderMessage({ type: 'error', text: errorText });
      setTimeout(() => setEditOrderMessage({ type: '', text: '' }), 5000);
    } finally {
      setSavingOrder(false);
    }
  };


  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div className="container">
          <div className="admin-header-content">
            <h1>Admin Dashboard</h1>
            <button onClick={handleLogout} className="btn btn-outline">
              Logout
            </button>
          </div>
        </div>
      </div>


      <div className="admin-content">
        <div className="container">
          <div className="admin-tabs">
            <button
              className={`tab-btn ${activeTab === 'analytics' ? 'active' : ''}`}
              onClick={() => setActiveTab('analytics')}
            >
              📊 Dashboard
            </button>
            <button
              className={`tab-btn ${activeTab === 'products' ? 'active' : ''}`}
              onClick={() => setActiveTab('products')}
            >
              📦 Products
            </button>
            <button
              className={`tab-btn ${activeTab === 'orders' ? 'active' : ''}`}
              onClick={() => setActiveTab('orders')}
            >
              🛒 Orders
            </button>
            <button
              className={`tab-btn ${activeTab === 'inventory' ? 'active' : ''}`}
              onClick={() => setActiveTab('inventory')}
            >
              📊 Product Inventory
            </button>
            <button
              className={`tab-btn ${activeTab === 'product-analytics' ? 'active' : ''}`}
              onClick={() => setActiveTab('product-analytics')}
            >
              📈 Analytics
            </button>
            <button
              className={`tab-btn ${activeTab === 'contacts' ? 'active' : ''}`}
              onClick={() => setActiveTab('contacts')}
            >
              📧 Messages
            </button>
            <button
              className={`tab-btn ${activeTab === 'company-info' ? 'active' : ''}`}
              onClick={() => setActiveTab('company-info')}
            >
              🏢 Company Info
            </button>
            <button
              className={`tab-btn ${activeTab === 'offers' ? 'active' : ''}`}
              onClick={() => setActiveTab('offers')}
            >
              🎯 Offers
            </button>
            <button
              className={`tab-btn ${activeTab === 'expenses' ? 'active' : ''}`}
              onClick={() => setActiveTab('expenses')}
            >
              💸 Investments & Expenses
            </button>
            <button
              className={`tab-btn ${activeTab === 'change-password' ? 'active' : ''}`}
              onClick={() => setActiveTab('change-password')}
            >
              🔒 Change Password
            </button>
          </div>


          {activeTab === 'analytics' && analytics && (
            <div className="admin-section">
              <h2>Paid Income Dashboard</h2>
              <div className="analytics-grid">
                <div className="stat-card">
                  <h3>Total Paid Income ({new Date().getFullYear()})</h3>
                  <p className="stat-value">kr {parseFloat(analytics.sales.current_year_sales || 0).toFixed(2)}</p>
                  <p className="stat-label">{analytics.sales.total_orders} Orders</p>
                  <p className="stat-sub" style={{ color: '#ffffff' }}>
                    Unpaid: kr {parseFloat(analytics.sales.unpaid_amount || 0).toFixed(2)}
                  </p>
                  <p className="stat-sub">Current month paid income: kr {parseFloat(analytics.sales.current_month_sales || 0).toFixed(2)}</p>
                </div>
                <div className="stat-card">
                  <h3>Low Stock Items</h3>
                  <p className="stat-value">{analytics.inventory.low_stock}</p>
                  <p className="stat-label">Products below 20 units</p>
                </div>
                <div className="stat-card">
                  <h3>Orders In Current Month</h3>
                  <p className="stat-value">{parseInt(analytics.sales.current_month_orders || 0, 10)}</p>
                  <p className="stat-label">Last month total: {parseInt(analytics.sales.last_month_orders || 0, 10)}</p>
                </div>
              </div>

              <div className="analytics-section">
                <h3>{new Date().getFullYear()} Monthly Income vs Expenses</h3>
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Paid Orders Income</th>
                      <th>Investments</th>
                      <th>Total Income</th>
                      <th>Expenses</th>
                      <th>Net</th>
                      <th>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenseIncomeByMonth.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ textAlign: 'center' }}>No monthly summary available.</td>
                      </tr>
                    ) : (
                      expenseIncomeByMonth.map((item) => {
                        const monthNumber = Number(item.month_number || 0);
                        const monthLabel = MONTH_LABELS[monthNumber - 1] || String(monthNumber);
                        const totalIncome = Number(item.total_income || 0);
                        const totalInvestments = Number(item.total_investments || 0);
                        const combinedIncome = Number(item.combined_income || 0);
                        const totalExpenses = Number(item.total_expenses || 0);
                        const netAmount = Number(item.net_amount || 0);
                        const balance = Number(item.balance || 0);

                        return (
                          <tr key={monthNumber}>
                            <td>{monthLabel}</td>
                            <td>{totalIncome.toFixed(2)}</td>
                            <td>{totalInvestments.toFixed(2)}</td>
                            <td>{combinedIncome.toFixed(2)}</td>
                            <td>{totalExpenses.toFixed(2)}</td>
                            <td style={{ fontWeight: 700, color: netAmount >= 0 ? '#166534' : '#B91C1C' }}>
                              {netAmount.toFixed(2)}
                            </td>
                            <td style={{ fontWeight: 700, color: balance >= 0 ? '#166534' : '#B91C1C' }}>
                              {balance.toFixed(2)}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>


              <div className="analytics-section">
                <h3 style={{ padding: '8px 0' }}>Orders by Status</h3>
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Count</th>
                      <th>Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.ordersByStatus
                      .slice((analyticsOrdersPage - 1) * ANALYTICS_PER_PAGE, analyticsOrdersPage * ANALYTICS_PER_PAGE)
                      .map((status) => (
                        <tr key={status.status}>
                          <td>{status.status}</td>
                          <td>{status.count}</td>
                          <td>kr {parseFloat(status.revenue || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                {analytics.ordersByStatus.length > ANALYTICS_PER_PAGE && (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
                    <button
                      onClick={() => setAnalyticsOrdersPage(p => Math.max(1, p - 1))}
                      disabled={analyticsOrdersPage === 1}
                      className="btn btn-sm"
                    >
                      ← Previous
                    </button>
                    <span>
                      Page {analyticsOrdersPage} of {Math.ceil(analytics.ordersByStatus.length / ANALYTICS_PER_PAGE)}
                    </span>
                    <button
                      onClick={() => setAnalyticsOrdersPage(p => p + 1)}
                      disabled={analyticsOrdersPage >= Math.ceil(analytics.ordersByStatus.length / ANALYTICS_PER_PAGE)}
                      className="btn btn-sm"
                    >
                      Next →
                    </button>
                  </div>
                )}
              </div>


              <div className="analytics-section">
                <h3 style={{ padding: '8px 0' }}>Top 5 Sold Products Per Category</h3>
                {(() => {
                  const groupedTopProducts = (analytics.topProducts || []).reduce((acc, product) => {
                    const category = product.category || 'Uncategorized';
                    if (!acc[category]) acc[category] = [];
                    acc[category].push(product);
                    return acc;
                  }, {});

                  const categoryEntries = Object.entries(groupedTopProducts).sort((a, b) => a[0].localeCompare(b[0]));
                  const pagedCategoryEntries = categoryEntries.slice(
                    (analyticsTopProductsPage - 1) * ANALYTICS_PER_PAGE,
                    analyticsTopProductsPage * ANALYTICS_PER_PAGE
                  );

                  return (
                    <>
                      {pagedCategoryEntries.map(([category, products]) => (
                        <details
                          key={category}
                          style={{ marginBottom: '18px', border: '1px solid var(--border-color)', borderRadius: '8px', background: '#fff' }}
                        >
                          <summary
                            style={{
                              margin: 0,
                              padding: '10px 12px',
                              cursor: 'pointer',
                              fontWeight: 600,
                              listStylePosition: 'inside'
                            }}
                          >
                            {category}
                          </summary>
                          <div style={{ padding: '0 12px 10px 12px' }}>
                            <table className="analytics-table" style={{ marginTop: '8px' }}>
                              <thead>
                                <tr>
                                  <th>Product</th>
                                  <th>Total Sold</th>
                                  <th>Revenue</th>
                                </tr>
                              </thead>
                              <tbody>
                                {products.map((product) => (
                                  <tr key={product.id}>
                                    <td>{product.name}</td>
                                    <td>{product.total_sold}</td>
                                    <td>kr {parseFloat(product.revenue).toFixed(2)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </details>
                      ))}
                    </>
                  );
                })()}
                {(() => {
                  const groupedTopProducts = (analytics.topProducts || []).reduce((acc, product) => {
                    const category = product.category || 'Uncategorized';
                    if (!acc[category]) acc[category] = [];
                    acc[category].push(product);
                    return acc;
                  }, {});

                  const totalCategoryCount = Object.keys(groupedTopProducts).length;

                  return totalCategoryCount > ANALYTICS_PER_PAGE;
                })() && (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
                    <button
                      onClick={() => setAnalyticsTopProductsPage(p => Math.max(1, p - 1))}
                      disabled={analyticsTopProductsPage === 1}
                      className="btn btn-sm"
                    >
                      ← Previous
                    </button>
                    <span>
                      Page {analyticsTopProductsPage} of {Math.ceil(Object.keys((analytics.topProducts || []).reduce((acc, product) => {
                        const category = product.category || 'Uncategorized';
                        if (!acc[category]) acc[category] = true;
                        return acc;
                      }, {})).length / ANALYTICS_PER_PAGE)}
                    </span>
                    <button
                      onClick={() => setAnalyticsTopProductsPage(p => p + 1)}
                      disabled={analyticsTopProductsPage >= Math.ceil(Object.keys((analytics.topProducts || []).reduce((acc, product) => {
                        const category = product.category || 'Uncategorized';
                        if (!acc[category]) acc[category] = true;
                        return acc;
                      }, {})).length / ANALYTICS_PER_PAGE)}
                      className="btn btn-sm"
                    >
                      Next →
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

            {showBatchModal && (
              <div className="product-form-modal">
                <div className="modal-content batch-modal-content">
                  <div className="modal-header">
                    <h3>
                      Batch History{batchModalProduct?.name ? ` - ${batchModalProduct.name}` : ''}
                      {batchModalProduct && (
                        <span style={{ marginLeft: '8px', fontSize: '0.95rem', fontWeight: '500', color: 'var(--text-gray)' }}>
                          Avg Purchase Price: kr {batchHistory.length > 0
                            ? (() => {
                                const totalQuantity = batchHistory.reduce((sum, batch) => sum + (parseInt(batch.quantity || 0, 10) || 0), 0);
                                const totalCost = batchHistory.reduce((sum, batch) => sum + (parseFloat(batch.purchase_price || 0) * (parseInt(batch.quantity || 0, 10) || 0)), 0);
                                return totalQuantity > 0 ? (totalCost / totalQuantity).toFixed(2) : '0.00';
                              })()
                            : parseFloat(batchModalProduct.purchase_price || 0).toFixed(2)}
                        </span>
                      )}
                    </h3>
                    <button
                      type="button"
                      className="modal-close-btn"
                      onClick={() => {
                        handleCancelEditBatch();
                        setShowBatchModal(false);
                      }}
                      aria-label="Close"
                    >
                      x
                    </button>
                  </div>
                  
                  {batchMessage.text && (
                    <div className={`alert alert-${batchMessage.type}`} style={{ marginBottom: '15px' }}>
                      {batchMessage.text}
                    </div>
                  )}

                  <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                    <h4 style={{ marginTop: 0, marginBottom: '12px', fontSize: '0.95rem' }}>{editingBatchId ? 'Edit Batch' : 'Add New Batch'}</h4>
                    <form onSubmit={editingBatchId ? (e) => { e.preventDefault(); handleSaveEditBatch(batchHistory.find(b => b.id === editingBatchId)); } : handleCreateBatch} style={{ display: 'flex', gap: '10px', flexWrap: 'nowrap' }}>
                      <div style={{ flex: 1, minWidth: '150px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.85rem', fontWeight: '600' }}>Batch Code *</label>
                        <input
                          type="text"
                          required
                          disabled={!!editingBatchId}
                          value={batchForm.batch_code}
                          onChange={(e) => setBatchForm({ ...batchForm, batch_code: e.target.value })}
                          placeholder="Enter batch code"
                          style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.9rem' }}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: '120px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.85rem', fontWeight: '600' }}>Quantity *</label>
                        <input
                          type="number"
                          required
                          min="1"
                          value={batchForm.quantity}
                          onChange={(e) => setBatchForm({ ...batchForm, quantity: e.target.value })}
                          placeholder="Enter quantity"
                          onKeyDown={preventArrowKeyChange}
                          style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.9rem' }}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: '140px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.85rem', fontWeight: '600' }}>Purchase Price</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={batchForm.purchase_price}
                          onChange={(e) => setBatchForm({ ...batchForm, purchase_price: e.target.value })}
                          placeholder="0.00"
                          onKeyDown={preventArrowKeyChange}
                          style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.9rem' }}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: '140px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.85rem', fontWeight: '600' }}>Purchase Price (EUR)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={batchForm.purchase_price_eur}
                          onChange={(e) => setBatchForm({ ...batchForm, purchase_price_eur: e.target.value })}
                          placeholder="0.00"
                          onKeyDown={preventArrowKeyChange}
                          style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.9rem' }}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: '180px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.85rem', fontWeight: '600' }}>Supplier Name *</label>
                        <input
                          type="text"
                          required
                          disabled={!!editingBatchId}
                          value={batchForm.supplier_name}
                          onChange={(e) => setBatchForm({ ...batchForm, supplier_name: e.target.value })}
                          placeholder="Enter supplier name"
                          style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.9rem' }}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: '160px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.85rem', fontWeight: '600' }}>Mfg. Date *</label>
                        <input
                          type="date"
                          required
                          disabled={!!editingBatchId}
                          value={batchForm.mfg_date}
                          onChange={(e) => setBatchForm({ ...batchForm, mfg_date: e.target.value })}
                          style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.9rem' }}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: '160px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.85rem', fontWeight: '600' }}>Exp. Date</label>
                        <input
                          type="date"
                          disabled={!!editingBatchId}
                          value={batchForm.expiry_date}
                          min={batchForm.mfg_date || undefined}
                          onChange={(e) => setBatchForm({ ...batchForm, expiry_date: e.target.value })}
                          style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.9rem' }}
                        />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                        {editingBatchId ? (
                          <>
                            <button 
                              type="submit" 
                              className="btn-edit" 
                              disabled={creatingBatch}
                              style={{ padding: '6px 12px', fontSize: '12px', marginBottom: 0, marginRight: 0 }}
                            >
                              {creatingBatch ? 'Saving...' : 'Save'}
                            </button>
                            <button 
                              type="button" 
                              className="btn-outline" 
                              onClick={handleCancelEditBatch}
                              style={{ padding: '6px 12px', fontSize: '12px', marginBottom: 0, marginRight: 0, borderColor: '#6b7280', color: '#6b7280' }}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button 
                            type="submit" 
                            className="btn-edit" 
                            disabled={creatingBatch}
                            style={{ padding: '6px 12px', fontSize: '12px', marginBottom: 0 }}
                          >
                            {creatingBatch ? 'Adding...' : 'Add Batch'}
                          </button>
                        )}
                      </div>
                    </form>
                  </div>

                  <div className="table-wrapper" style={{ maxHeight: '50vh', overflowY: 'auto', marginBottom: '15px' }}>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Batch Code</th>
                          <th>Quantity</th>
                          <th>Purchase Price</th>
                          <th>Purchase Price (EUR)</th>
                          <th>Supplier</th>
                          <th>Mfg. Date</th>
                          <th>Exp. Date</th>
                          <th>Created By</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {batchHistory.length === 0 ? (
                          <tr><td colSpan={9} style={{ padding: '12px', textAlign: 'center' }}>No batch records found</td></tr>
                        ) : (
                          batchHistory.map((b, index) => (
                            <tr key={b.id}>
                              <td>{b.batch_code}</td>
                              <td>{editingBatchId === b.id ? (
                                <input
                                  type="number"
                                  min="1"
                                  value={batchForm.quantity}
                                  onChange={(e) => setBatchForm({ ...batchForm, quantity: e.target.value })}
                                  onKeyDown={preventArrowKeyChange}
                                  style={{ padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.85rem', width: '70px' }}
                                />
                              ) : (
                                b.quantity
                              )}</td>
                              <td>{b.purchase_price == null ? '0.00' : Number(b.purchase_price).toFixed(2)}</td>
                              <td>{b.purchase_price_eur == null ? '0.00' : Number(b.purchase_price_eur).toFixed(2)}</td>
                              <td>{b.supplier_name || '-'}</td>
                              <td>{formatDateYMD(b.mfg_date)}</td>
                              <td>{formatDateYMD(b.expiry_date, '')}</td>
                              <td>{b.created_by}</td>
                              <td>
                                {editingBatchId === null && (
                                  <button
                                    type="button"
                                    onClick={() => handleEditBatch(b)}
                                    style={{ padding: '4px 8px', fontSize: '11px', backgroundColor: '#f59e0b', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}
                                  >
                                    Edit
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="form-actions" style={{ marginTop: '12px', justifyContent: 'flex-end' }}>
                    <button 
                      type="button" 
                      className="btn-outline" 
                      onClick={() => {
                        handleCancelEditBatch();
                        setShowBatchModal(false);
                      }}
                      style={{ padding: '6px 12px', fontSize: '12px' }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}


          {activeTab === 'product-analytics' && (
            <>
              <div className="admin-section" style={{ width: '100%', maxWidth: '100%' }}>
                <h2>Current Year Sales Trend - Sweden</h2>
                {loading ? (
                  <div className="loading"><div className="spinner"></div></div>
                ) : (
                  (() => {
                    const trendRows = Array.isArray(analytics?.monthlyTrend) ? analytics.monthlyTrend : [];
                    const chartData = MONTH_LABELS.map((label, index) => {
                      const row = trendRows.find((item) => Number(item.month_number) === (index + 1));
                      return {
                        label,
                        month: index + 1,
                        sweden_sales: Number(row?.sweden_sales || 0),
                        sweden_orders: Number(row?.sweden_orders || 0),
                      };
                    });

                    return renderMonthlyTrendChart(
                      chartData,
                      [
                        { key: 'sweden_sales', label: 'Sales (kr)', axis: 'left', color: '#dc2626' },
                        { key: 'sweden_orders', label: 'Orders', axis: 'right', color: '#2563eb' },
                      ],
                      'Total sales per month vs total orders per month, for orders shipped to Sweden'
                    );
                  })()
                )}
              </div>

              <div className="admin-section" style={{ width: '100%', maxWidth: '100%' }}>
                <h2>Current Year Sales Trend - Other Countries</h2>
                {loading ? (
                  <div className="loading"><div className="spinner"></div></div>
                ) : (
                  (() => {
                    const trendRows = Array.isArray(analytics?.monthlyTrend) ? analytics.monthlyTrend : [];
                    const chartData = MONTH_LABELS.map((label, index) => {
                      const row = trendRows.find((item) => Number(item.month_number) === (index + 1));
                      return {
                        label,
                        month: index + 1,
                        other_sek_sales: Number(row?.other_sek_sales || 0),
                        other_sek_orders: Number(row?.other_sek_orders || 0),
                        other_eur_sales: Number(row?.other_eur_sales || 0),
                        other_eur_orders: Number(row?.other_eur_orders || 0),
                      };
                    });

                    return renderMonthlyTrendChart(
                      chartData,
                      [
                        { key: 'other_sek_sales', label: 'Sales SEK (kr)', axis: 'left', color: '#dc2626' },
                        { key: 'other_eur_sales', label: 'Sales EUR (€)', axis: 'left', color: '#16a34a' },
                        { key: 'other_sek_orders', label: 'Orders SEK', axis: 'right', color: '#2563eb' },
                        { key: 'other_eur_orders', label: 'Orders EUR', axis: 'right', color: '#7c3aed' },
                      ],
                      'Total sales per month vs total orders per month, for orders shipped outside Sweden, split by currency'
                    );
                  })()
                )}
              </div>

              <div className="admin-section" style={{ width: '100%', maxWidth: '100%' }}>
                <h2>Product Performance Analytics</h2>
                {loading ? (
                  <div className="loading"><div className="spinner"></div></div>
                ) : (
                  <>
                    <div className="table-wrapper" style={{ overflowX: 'auto', border: '1px solid var(--border-color)', borderRadius: '4px' }}>
                      <table className="products-table" style={{ width: '100%', marginBottom: 0 }}>
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>Category</th>
                            <th>Price</th>
                            <th>Weight (g)</th>
                            <th>Stock</th>
                            <th>Units Sold</th>
                            <th>Orders</th>
                            <th>Revenue</th>
                          </tr>
                        </thead>
                        <tbody>
                          {productAnalytics
                            .slice((analyticsPage - 1) * ANALYTICS_PER_PAGE, analyticsPage * ANALYTICS_PER_PAGE)
                            .map((product) => (
                                <tr key={product.id}>
                                  <td>{product.name}</td>
                                  <td>{product.category}</td>
                                  <td>kr {product.price}</td>
                                  <td>{product.weight ? `${product.weight}g` : '-'}</td>
                                  <td>{product.stock_quantity}</td>
                                  <td>{product.total_sold}</td>
                                  <td>{product.order_count}</td>
                                  <td>kr {parseFloat(product.total_revenue).toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                    {productAnalytics.length > ANALYTICS_PER_PAGE && (
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
                        <button
                          onClick={() => setAnalyticsPage(p => Math.max(1, p - 1))}
                          disabled={analyticsPage === 1}
                          className="btn btn-sm"
                        >
                          ← Previous
                        </button>
                        <span>
                          Page {analyticsPage} of {Math.ceil(productAnalytics.length / ANALYTICS_PER_PAGE)}
                        </span>
                        <button
                          onClick={() => setAnalyticsPage(p => p + 1)}
                          disabled={analyticsPage >= Math.ceil(productAnalytics.length / ANALYTICS_PER_PAGE)}
                          className="btn btn-sm"
                        >
                          Next →
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="admin-section" style={{ width: '100%', maxWidth: '100%' }}>
                <h2>Profit Per Product</h2>
                <p style={{ marginBottom: '12px', color: 'var(--text-gray)' }}>
                  Current year only. Profit = Total Revenue - (Units Sold x Purchase Price)
                </p>
                {loading ? (
                  <div className="loading"><div className="spinner"></div></div>
                ) : (
                  <>
                    <div className="table-wrapper" style={{ overflowX: 'auto', border: '1px solid var(--border-color)', borderRadius: '4px' }}>
                      <table className="products-table" style={{ width: '100%', marginBottom: 0 }}>
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>Category</th>
                            <th>Purchase Price</th>
                            <th>Avg Selling Price</th>
                            <th>Units Sold (Current Year)</th>
                            <th>Total Revenue (Current Year)</th>
                            <th>Total Purchase Cost (Current Year)</th>
                            <th>Profit (Current Year)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {productProfitAnalytics
                            .slice((profitAnalyticsPage - 1) * ANALYTICS_PER_PAGE, profitAnalyticsPage * ANALYTICS_PER_PAGE)
                            .map((product) => {
                              const unitsSold = parseFloat(product.units_sold || 0);
                              const totalRevenue = parseFloat(product.total_revenue || 0);
                              const totalPurchaseCost = parseFloat(product.total_purchase_cost || 0);
                              const totalProfit = parseFloat(product.total_profit || 0);
                              const purchasePrice = parseFloat(product.purchase_price || 0);
                              const avgSellingPrice = unitsSold > 0 ? (totalRevenue / unitsSold) : 0;

                              return (
                                <tr key={`profit-${product.id}`}>
                                  <td>{product.name}</td>
                                  <td>{product.category}</td>
                                  <td>kr {purchasePrice.toFixed(2)}</td>
                                  <td>kr {avgSellingPrice.toFixed(2)}</td>
                                  <td>{unitsSold}</td>
                                  <td>kr {totalRevenue.toFixed(2)}</td>
                                  <td>kr {totalPurchaseCost.toFixed(2)}</td>
                                  <td style={{ color: totalProfit >= 0 ? '#4caf50' : '#f44336', fontWeight: '600' }}>
                                    kr {totalProfit.toFixed(2)}
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                    {productProfitAnalytics.length > ANALYTICS_PER_PAGE && (
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
                        <button
                          onClick={() => setProfitAnalyticsPage(p => Math.max(1, p - 1))}
                          disabled={profitAnalyticsPage === 1}
                          className="btn btn-sm"
                        >
                          ← Previous
                        </button>
                        <span>
                          Page {profitAnalyticsPage} of {Math.ceil(productProfitAnalytics.length / ANALYTICS_PER_PAGE)}
                        </span>
                        <button
                          onClick={() => setProfitAnalyticsPage(p => p + 1)}
                          disabled={profitAnalyticsPage >= Math.ceil(productProfitAnalytics.length / ANALYTICS_PER_PAGE)}
                          className="btn btn-sm"
                        >
                          Next →
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="admin-section" style={{ width: '100%', maxWidth: '100%' }}>
                <h2>Profit Per Product (EUR Orders)</h2>
                <p style={{ marginBottom: '12px', color: 'var(--text-gray)' }}>
                  Current year only, orders placed in EUR. Profit = Total Revenue - (Units Sold x Purchase Price EUR)
                </p>
                {loading ? (
                  <div className="loading"><div className="spinner"></div></div>
                ) : (
                  <>
                    <div className="table-wrapper" style={{ overflowX: 'auto', border: '1px solid var(--border-color)', borderRadius: '4px' }}>
                      <table className="products-table" style={{ width: '100%', marginBottom: 0 }}>
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>Category</th>
                            <th>Purchase Price (EUR)</th>
                            <th>Avg Selling Price</th>
                            <th>Units Sold (Current Year)</th>
                            <th>Total Revenue (Current Year)</th>
                            <th>Total Purchase Cost (Current Year)</th>
                            <th>Profit (Current Year)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {productProfitAnalyticsEur
                            .slice((profitAnalyticsEurPage - 1) * ANALYTICS_PER_PAGE, profitAnalyticsEurPage * ANALYTICS_PER_PAGE)
                            .map((product) => {
                              const unitsSold = parseFloat(product.units_sold || 0);
                              const totalRevenue = parseFloat(product.total_revenue || 0);
                              const totalPurchaseCost = parseFloat(product.total_purchase_cost || 0);
                              const totalProfit = parseFloat(product.total_profit || 0);
                              const purchasePriceEur = parseFloat(product.purchase_price_eur || 0);
                              const avgSellingPrice = unitsSold > 0 ? (totalRevenue / unitsSold) : 0;

                              return (
                                <tr key={`profit-eur-${product.id}`}>
                                  <td>{product.name}</td>
                                  <td>{product.category}</td>
                                  <td>€ {purchasePriceEur.toFixed(2)}</td>
                                  <td>€ {avgSellingPrice.toFixed(2)}</td>
                                  <td>{unitsSold}</td>
                                  <td>€ {totalRevenue.toFixed(2)}</td>
                                  <td>€ {totalPurchaseCost.toFixed(2)}</td>
                                  <td style={{ color: totalProfit >= 0 ? '#4caf50' : '#f44336', fontWeight: '600' }}>
                                    € {totalProfit.toFixed(2)}
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                    {productProfitAnalyticsEur.length > ANALYTICS_PER_PAGE && (
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
                        <button
                          onClick={() => setProfitAnalyticsEurPage(p => Math.max(1, p - 1))}
                          disabled={profitAnalyticsEurPage === 1}
                          className="btn btn-sm"
                        >
                          ← Previous
                        </button>
                        <span>
                          Page {profitAnalyticsEurPage} of {Math.ceil(productProfitAnalyticsEur.length / ANALYTICS_PER_PAGE)}
                        </span>
                        <button
                          onClick={() => setProfitAnalyticsEurPage(p => p + 1)}
                          disabled={profitAnalyticsEurPage >= Math.ceil(productProfitAnalyticsEur.length / ANALYTICS_PER_PAGE)}
                          className="btn btn-sm"
                        >
                          Next →
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}


          {activeTab === 'products' && (
            <div className="admin-section">
              <div className="section-header">
                <h2>Products Management</h2>
                <button
                  onClick={() => {
                    setShowProductForm(true);
                    setEditingProduct(null);
                    setProductMessage({ type: '', text: '' });
                    setProductForm({
                      name: '',
                      category: '',
                      description: '',
                      ingredients: '',
                      price: '',
                      wholesale_price: '',
                      purchase_price: '',
                      wholesale_price_eur: 0,
                      vat_percent: '',
                      discount_percent: '',
                      weight: '',
                      image_url: '',
                      image_files: [],
                      existing_images: [],
                      in_stock: true,
                      stock_quantity: '',
                      gluten_free: false,
                      barcode: '',
                      show_on_website: true
                    });
                  }}
                  className="btn btn-primary"
                >
                  Add New Product
                </button>
              </div>

              {productMessage.text && (
                <div className={`alert alert-${productMessage.type}`} style={{ marginBottom: '20px' }}>
                  {productMessage.text}
                </div>
              )}

              {showProductForm && (
                <div className="product-form-modal">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h3>{editingProduct ? 'Edit Product' : 'Add New Product'}</h3>
                      <button
                        type="button"
                        className="modal-close-btn"
                        onClick={() => {
                          setShowProductForm(false);
                          setEditingProduct(null);
                        }}
                        aria-label="Close"
                      >
                        x
                      </button>
                    </div>
                    <form onSubmit={handleProductSubmit}>
                      <div className="form-group">
                        <label>Product Name *</label>
                        <input
                          type="text"
                          required
                          value={productForm.name}
                          onChange={(e) =>
                            setProductForm({ ...productForm, name: e.target.value })
                          }
                        />
                      </div>


                      <div className="form-row">
                        <div className="form-group" style={{ flex: 1 }}>
                          <label>Category *</label>
                          <div className="category-dropdown">
                            <input
                              type="text"
                              required
                              value={productForm.category}
                              onChange={(e) => {
                                setProductForm({ ...productForm, category: e.target.value });
                                setCategorySearchTerm(e.target.value);
                                setShowCategoryDropdown(e.target.value.length > 0);
                              }}
                              placeholder="e.g., Wheat Crisp, Coin Khakhra"
                            />
                            {showCategoryDropdown && (
                              <div className="dropdown-list">
                                {categories
                                  .filter(cat => cat.toLowerCase().includes(categorySearchTerm.toLowerCase()))
                                  .map((cat) => (
                                    <div
                                      key={cat}
                                      className="dropdown-item"
                                      onClick={() => {
                                        setProductForm({ ...productForm, category: cat });
                                        setShowCategoryDropdown(false);
                                        setCategorySearchTerm('');
                                      }}
                                    >
                                      {cat}
                                    </div>
                                  ))}

                              </div>
                            )}
                          </div>
                        </div>
                        <div className="form-group gluten-free-group" style={{ flex: '0 0 auto' }}>
                          <label>Gluten Free</label>
                          <div className="slider-container">
                            <input
                              type="checkbox"
                              className="slider-checkbox"
                              id="gluten-free-slider"
                              checked={productForm.gluten_free}
                              onChange={() =>
                                setProductForm({ ...productForm, gluten_free: !productForm.gluten_free })
                              }
                            />
                            <label htmlFor="gluten-free-slider" className="slider"></label>
                          </div>
                        </div>
                        <div className="form-group show-on-website-group" style={{ flex: '0 0 auto' }}>
                          <label>Show on Website</label>
                          <div className="slider-container">
                            <input
                              type="checkbox"
                              className="slider-checkbox"
                              id="show-on-website-slider"
                              checked={productForm.show_on_website !== false}
                              onChange={() =>
                                setProductForm({ ...productForm, show_on_website: !(productForm.show_on_website !== false) })
                              }
                            />
                            <label htmlFor="show-on-website-slider" className="slider"></label>
                          </div>
                        </div>
                      </div>


                      <div className="form-group">
                        <label>Description</label>
                        <textarea
                          value={productForm.description}
                          onChange={(e) =>
                            setProductForm({ ...productForm, description: e.target.value })
                          }
                        />
                      </div>


                      {/* Moved Gluten Free next to Category above */}


                      <div className="form-group">
                        <label>Ingredients</label>
                        <div className="ingredients-editor-wrapper">
                          <div className="ingredients-editor-toolbar" role="toolbar" aria-label="Ingredients formatting toolbar">
                            <button type="button" className="editor-btn" onClick={() => applyIngredientsFormat('bold')} title="Bold" aria-label="Bold">
                              B
                            </button>
                            <button type="button" className="editor-btn" onClick={() => applyIngredientsFormat('italic')} title="Italic" aria-label="Italic">
                              I
                            </button>
                            <button type="button" className="editor-btn" onClick={() => applyIngredientsFormat('underline')} title="Underline" aria-label="Underline">
                              U
                            </button>
                            <button type="button" className="editor-btn" onClick={() => applyIngredientsFormat('insertUnorderedList')} title="Bullet list" aria-label="Bullet list">
                              * List
                            </button>
                            <button type="button" className="editor-btn" onClick={() => applyIngredientsFormat('insertOrderedList')} title="Numbered list" aria-label="Numbered list">
                              1. List
                            </button>
                            <button type="button" className="editor-btn" onClick={handleIngredientsLinkInsert} title="Insert link" aria-label="Insert link">
                              Link
                            </button>
                            <button type="button" className="editor-btn" onClick={() => applyIngredientsFormat('removeFormat')} title="Clear formatting" aria-label="Clear formatting">
                              Clear
                            </button>
                          </div>
                          <div
                            ref={ingredientsEditorRef}
                            className="ingredients-editor"
                            contentEditable
                            role="textbox"
                            aria-multiline="true"
                            aria-label="Ingredients"
                            data-placeholder="Add ingredients using rich text formatting"
                            onInput={handleIngredientsInput}
                          />
                        </div>
                      </div>


                      <div className="form-group">
                        <label>Barcode *</label>
                        <input
                          type="text"
                          required
                          readOnly={!!editingProduct}
                          value={productForm.barcode}
                          onChange={(e) =>
                            setProductForm({ ...productForm, barcode: e.target.value })
                          }
                          placeholder="Enter product barcode (optional)"
                        />
                      </div>

                      <div className="form-group">
                        <label>Batch Code *</label>
                        <input
                          type="text"
                          required
                          readOnly={!!editingProduct}
                          value={productForm.batch_code}
                          onChange={(e) => setProductForm({ ...productForm, batch_code: e.target.value })}
                          placeholder="Enter batch code"
                        />
                      </div>


                      <div className="form-row">
                        <div className="form-group">
                          <label>Retail Price (kr) *</label>
                          <input
                            type="number"
                            step="0.01"
                            required
                            value={productForm.price}
                            onChange={(e) =>
                              setProductForm({ ...productForm, price: e.target.value })
                            }
                            onKeyDown={preventArrowKeyChange}
                          />
                        </div>
                        <div className="form-group">
                          <label>Wholesale Price (kr) *</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            required
                            value={productForm.wholesale_price}
                            onChange={(e) =>
                              setProductForm({ ...productForm, wholesale_price: e.target.value })
                            }
                            placeholder="Admin only - not visible to users"
                            onKeyDown={preventArrowKeyChange}
                          />
                        </div>
                        <div className="form-group">
                          <label>Purchase Price (kr) *</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            required
                            readOnly
                            value={0}
                            placeholder="Admin only - not visible to users"
                            onKeyDown={preventArrowKeyChange}
                          />
                        </div>
                        <div className="form-group">
                          <label>Amount in EUR (€)</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={productForm.wholesale_price_eur}
                            onChange={(e) =>
                              setProductForm({ ...productForm, wholesale_price_eur: e.target.value })
                            }
                            placeholder="Admin only - not visible to users"
                            onKeyDown={preventArrowKeyChange}
                          />
                        </div>
                        <div className="form-group">
                          <label>Discount (%)</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={productForm.discount_percent}
                            onChange={(e) =>
                              setProductForm({ ...productForm, discount_percent: e.target.value })
                            }
                            placeholder="e.g., 10 for 10% off"
                            onKeyDown={preventArrowKeyChange}
                          />
                        </div>
                        <div className="form-group">
                          <label>VAT (%) *</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            required
                            value={productForm.vat_percent}
                            onChange={(e) =>
                              setProductForm({ ...productForm, vat_percent: e.target.value })
                            }
                            placeholder="e.g., 12.5"
                            onKeyDown={preventArrowKeyChange}
                          />
                        </div>
                        <div className="form-group">
                          <label>Stock Quantity</label>
                          <input
                            type="number"
                            readOnly={!!editingProduct}
                            value={productForm.stock_quantity}
                            onChange={(e) =>
                              setProductForm({
                                ...productForm,
                                stock_quantity: e.target.value === '' ? '' : parseInt(e.target.value),
                              })
                            }
                            onKeyDown={preventArrowKeyChange}
                          />
                        </div>
                        <div className="form-group">
                          <label>Weight (grams)</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            readOnly={!!editingProduct}
                            value={productForm.weight}
                            onChange={(e) =>
                              setProductForm({ ...productForm, weight: e.target.value })
                            }
                            placeholder="e.g., 200"
                            onKeyDown={preventArrowKeyChange}
                          />
                        </div>
                      </div>


                      <div className="form-group">
                        <label>Product Images <span className="field-hint">(max 3)</span></label>
                        <div className="image-upload-section">
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={(e) => {
                              const files = Array.from(e.target.files || []);
                              if (files.length > 3) {
                                setImageUploadError('You can select at most 3 images.');
                                e.target.value = '';
                                return;
                              }
                              setImageUploadError('');
                              setProductForm({ ...productForm, image_files: files });
                            }}
                          />
                          {imageUploadError && (
                            <p className="upload-error">{imageUploadError}</p>
                          )}
                          {productForm.image_files && productForm.image_files.length > 0 && (
                            <div className="image-preview-row">
                              {productForm.image_files.map((file, idx) => (
                                <div key={idx} className="image-preview-thumb">
                                  <img
                                    src={URL.createObjectURL(file)}
                                    alt={`Preview ${idx + 1}`}
                                  />
                                  <span className="image-preview-name">{file.name}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {productForm.existing_images && productForm.existing_images.length > 0 && (!productForm.image_files || productForm.image_files.length === 0) && (
                            <div className="existing-images-preview">
                              <p className="existing-images-label">Current images ({productForm.existing_images.length}):</p>
                              <div className="existing-slideshow">
                                <img
                                  src={productForm.existing_images[existingImagesSlide % productForm.existing_images.length]}
                                  alt={`Existing ${existingImagesSlide % productForm.existing_images.length + 1}`}
                                  className="existing-slideshow-img"
                                />
                                {productForm.existing_images.length > 1 && (
                                  <div className="existing-slideshow-controls">
                                    <button
                                      type="button"
                                      className="slideshow-nav"
                                      onClick={() => setExistingImagesSlide((s) => (s - 1 + productForm.existing_images.length) % productForm.existing_images.length)}
                                    >&#8249;</button>
                                    <span className="slideshow-counter">{(existingImagesSlide % productForm.existing_images.length) + 1} / {productForm.existing_images.length}</span>
                                    <button
                                      type="button"
                                      className="slideshow-nav"
                                      onClick={() => setExistingImagesSlide((s) => (s + 1) % productForm.existing_images.length)}
                                    >&#8250;</button>
                                  </div>
                                )}
                              </div>
                              <p className="existing-images-hint">Select new images above to replace all current images.</p>
                            </div>
                          )}
                        </div>
                      </div>





                      <div className="form-actions">
                        <button type="submit" className="btn btn-primary">
                          {editingProduct ? 'Update Product' : 'Add Product'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowProductForm(false);
                            setEditingProduct(null);
                          }}
                          className="btn btn-outline"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}


              {loading ? (
                <div className="loading">
                  <div className="spinner"></div>
                </div>
              ) : (
                <>
                  <div style={{ marginTop: '30px', marginBottom: '20px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <input
                      type="text"
                      placeholder="Search products by name, category, or ID..."
                      value={productSearchTerm}
                      onChange={(e) => {
                        setProductSearchTerm(e.target.value);
                        setProductCurrentPage(1);
                      }}
                      style={{
                        flex: 1,
                        padding: '10px 15px',
                        borderRadius: '6px',
                        border: '1px solid var(--border-color)',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                    <select
                      value={productStatusFilter}
                      onChange={(e) => {
                        setProductStatusFilter(e.target.value);
                        setProductCurrentPage(1);
                      }}
                      style={{
                        padding: '10px 15px',
                        borderRadius: '6px',
                        border: '1px solid var(--border-color)',
                        fontSize: '14px',
                        background: 'var(--bg-color, #fff)',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="all">All Statuses</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>

                  <div className="products-table">
                    <table>
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Name</th>
                          <th>Category</th>
                          <th>Retail Price</th>
                          <th>Wholesale Price</th>
                          <th>Weight (g)</th>
                          <th>Stock</th>
                          <th>Defective</th>
                          <th>Available</th>
                          <th>Status</th>
                          <th>Website</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const filteredProducts = products.filter((product) => {
                            const searchLower = productSearchTerm.toLowerCase();
                            const matchesSearch =
                              product.name.toLowerCase().includes(searchLower) ||
                              product.category.toLowerCase().includes(searchLower) ||
                              product.id.toString().includes(searchLower);
                            const matchesStatus =
                              productStatusFilter === 'all' ||
                              (productStatusFilter === 'active' && product.is_active) ||
                              (productStatusFilter === 'inactive' && !product.is_active);
                            return matchesSearch && matchesStatus;
                          });

                          const pageProducts = filteredProducts.slice(
                            (productCurrentPage - 1) * PRODUCTS_PAGE_SIZE,
                            productCurrentPage * PRODUCTS_PAGE_SIZE
                          );

                          return pageProducts.map((product) => (
                        (() => {
                          const stockQty = Number(product.stock_quantity || 0);
                          const defectiveQty = Number(product.defective_count || 0);
                          const availableQty = Math.max(0, stockQty - defectiveQty);
                          return (
                        <tr key={product.id}>
                          <td>{product.id}</td>
                          <td>{product.name}</td>
                          <td>{product.category}</td>
                          <td>kr {product.price}</td>
                          <td>kr {product.wholesale_price || '0.00'}</td>
                          <td>{product.weight ? `${product.weight}g` : '-'}</td>
                          <td>{stockQty}
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <input
                                type="number"
                                min="0"
                                max={stockQty}
                                value={defectiveInputCounts[product.id] ?? 0}
                                onChange={(e) => setDefectiveInputCounts((prev) => ({
                                  ...prev,
                                  [product.id]: e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10) || 0),
                                }))}
                                onKeyDown={preventArrowKeyChange}
                                style={{ width: '40px', padding: '6px' }}
                              />
                              <button
                                onClick={() => handleUpdateDefectiveCount(product)}
                                className="btn-tick"
                                type="button"
                                aria-label="Update defective count"
                                title="Update defective count"
                                disabled={Number(defectiveInputCounts[product.id] || 0) <= 0}
                              >
                                ✓
                              </button>
                            </div>
                          </td>
                          <td>
                              <div className="stock-cell">
                                <span>{availableQty}</span>
                                {availableQty > 0 && availableQty < 20 && (
                                  <span className="low-stock-badge">Very few left</span>
                                )}
                                {availableQty === 0 && (
                                  <span className="low-stock-badge">Out of stock</span>
                                )}
                              </div>
                          </td>
                          <td>
                            <span className={product.is_active ? 'status-active' : 'status-inactive'}>
                              {product.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            <span className={product.show_on_website !== false ? 'status-active' : 'status-inactive'}>
                              {product.show_on_website !== false ? 'Visible' : 'Hidden'}
                            </span>
                          </td>
                          <td>
                            <button
                              onClick={() => handleEditProduct(product)}
                              className="btn-edit"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleToggleProductStatus(product.id, product.is_active)}
                              className={product.is_active ? "btn-deactivate" : "btn-activate"}
                            >
                              {product.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                              <button
                                onClick={() => openBatchModal(product)}
                                className="btn-batches"
                              >
                                Batches
                              </button>
                          </td>
                        </tr>
                          );
                        })()
                          ));
                        })()}
                      </tbody>
                    </table>
                  </div>

                  {(() => {
                    const filteredProducts = products.filter((product) => {
                      const searchLower = productSearchTerm.toLowerCase();
                      return (
                        product.name.toLowerCase().includes(searchLower) ||
                        product.category.toLowerCase().includes(searchLower) ||
                        product.id.toString().includes(searchLower)
                      );
                    });
                    const totalPages = Math.max(1, Math.ceil(filteredProducts.length / PRODUCTS_PAGE_SIZE));

                    return totalPages > 1 ? (
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
                        <button
                          onClick={() => setProductCurrentPage((p) => Math.max(1, p - 1))}
                          disabled={productCurrentPage === 1}
                          className="btn btn-sm"
                        >
                          ← Previous
                        </button>

                        <span>
                          Page {productCurrentPage} of {totalPages}
                        </span>

                        <button
                          onClick={() => setProductCurrentPage((p) => Math.min(totalPages, p + 1))}
                          disabled={productCurrentPage === totalPages}
                          className="btn btn-sm"
                        >
                          Next →
                        </button>
                      </div>
                    ) : null;
                  })()}

                  <div style={{ marginTop: '24px' }}>
                    <h3>Sample Table</h3>
                    <div className="products-table">
                      <table>
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Name</th>
                            <th>Category</th>
                            <th>Defective</th>
                            <th>Samples Used</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const defectiveProducts = products.filter((product) => Number(product.defective_count || 0) > 0);

                            if (defectiveProducts.length === 0) {
                              return (
                                <tr>
                                  <td colSpan="5">No defective products found.</td>
                                </tr>
                              );
                            }

                            return defectiveProducts.map((product) => {
                              const defectiveQty = Number(product.defective_count || 0);

                              return (
                                <tr key={`defective-${product.id}`}>
                                  <td>{product.id}</td>
                                  <td>{product.name}</td>
                                  <td>{product.category}</td>
                                  <td>{defectiveQty}</td>
                                  <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <input
                                        type="number"
                                        min="0"
                                        max={defectiveQty}
                                        value={sampleUsedInputCounts[product.id] ?? 0}
                                        onChange={(e) => setSampleUsedInputCounts((prev) => ({
                                          ...prev,
                                          [product.id]: e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10) || 0),
                                        }))}
                                        onKeyDown={preventArrowKeyChange}
                                        style={{ width: '40px', padding: '6px' }}
                                      />
                                      <button
                                        onClick={() => handleUpdateUsedSampleCount(product)}
                                        className="btn-tick"
                                        type="button"
                                        aria-label="Update used samples"
                                        title="Update used samples"
                                        disabled={Number(sampleUsedInputCounts[product.id] || 0) <= 0}
                                      >
                                        ✓
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            });
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'offers' && (
            <div className="admin-section">
              <div className="section-header">
                <h2>{editingOfferId ? 'Edit Offer' : 'Category Offers'}</h2>
              </div>

              {offersMessage.text && (
                <div className={`alert alert-${offersMessage.type}`} style={{ marginBottom: '20px' }}>
                  {offersMessage.text}
                </div>
              )}

              <form onSubmit={handleOfferSubmit} className="offers-form-grid" style={{ marginBottom: '24px' }}>
                <div className="form-group">
                  <label>Offer Title</label>
                  <input
                    type="text"
                    value={offerForm.title}
                    onChange={(e) => handleOfferFormChange('title', e.target.value)}
                    placeholder="Example: Khakhra Family Pack"
                  />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Categories *</label>
                  {offerForm.items.map((item, index) => (
                    <div key={index} style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '8px' }}>
                      <input
                        type="text"
                        required
                        list="offer-category-suggestions"
                        value={item.category}
                        onChange={(e) => handleOfferItemChange(index, 'category', e.target.value)}
                        placeholder="Example: Khakhra"
                        style={{ flex: 1 }}
                      />
                      {offerForm.offer_type === 'bundle' && (
                        <>
                          <input
                            type="number"
                            min="1"
                            required
                            value={item.quantity}
                            onChange={(e) => handleOfferItemChange(index, 'quantity', e.target.value)}
                            onKeyDown={preventArrowKeyChange}
                            title="Quantity required from this category"
                            style={{ width: '90px' }}
                          />
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            required
                            value={item.category_price}
                            onChange={(e) => handleOfferItemChange(index, 'category_price', e.target.value)}
                            onKeyDown={preventArrowKeyChange}
                            title="Price to charge for this category's items in the combo"
                            placeholder="Price"
                            style={{ width: '110px' }}
                          />
                        </>
                      )}
                      <button
                        type="button"
                        className="btn-delete"
                        onClick={() => handleRemoveOfferItemRow(index)}
                        disabled={offerForm.items.length <= 1}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button type="button" className="btn btn-secondary" onClick={handleAddOfferItemRow}>
                    + Add another category
                  </button>
                  {!areAllOfferCategoriesAvailable && (
                    <div className="field-error-message">Choose existing product categories from suggestions.</div>
                  )}
                  {hasDuplicateCategoryInForm && (
                    <div className="field-error-message">Each category can only be added once per offer.</div>
                  )}
                  {isDuplicateOfferComposition && (
                    <div className="field-error-message">An offer with the same categories and quantities already exists.</div>
                  )}
                  <datalist id="offer-category-suggestions">
                    {categories.map((cat) => (
                      <option key={cat} value={cat} />
                    ))}
                  </datalist>
                </div>
                <div className="form-group">
                  <label>Offer Type *</label>
                  <select
                    required
                    value={offerForm.offer_type}
                    onChange={(e) => handleOfferFormChange('offer_type', e.target.value)}
                  >
                    <option value="bundle">Bundle offer</option>
                    <option value="discount">Discount offer</option>
                  </select>
                </div>
                <div className="form-group">
                  {offerForm.offer_type === 'bundle' ? (
                    <>
                      <label>Total Quantity</label>
                      <p style={{ marginTop: '10px', color: '#4b5563' }}>
                        {offerForm.items.reduce((sum, item) => sum + (parseInt(item.quantity, 10) || 0), 0)} items across the categories above
                      </p>
                    </>
                  ) : (
                    <>
                      <label>Discount Percent *</label>
                      <input
                        type="number"
                        min="0.01"
                        max="100"
                        step="0.01"
                        required
                        value={offerForm.discount_percent}
                        onChange={(e) => handleOfferFormChange('discount_percent', e.target.value)}
                        onKeyDown={preventArrowKeyChange}
                      />
                    </>
                  )}
                </div>
                <div className="form-group">
                  <label>{offerForm.offer_type === 'bundle' ? 'Combo Total Price' : 'Discount Value'}</label>
                  {offerForm.offer_type === 'bundle' ? (
                    <p style={{ marginTop: '10px', color: '#4b5563' }}>
                      {offerForm.currency || 'SEK'} {offerForm.items.reduce((sum, item) => sum + (parseFloat(item.category_price) || 0), 0).toFixed(2)}
                      {' '}(sum of the per-category prices above)
                    </p>
                  ) : (
                    <p style={{ marginTop: '10px', color: '#4b5563' }}>
                      The discount percent above will apply to all items in these categories.
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <label>Currency</label>
                  <input
                    type="text"
                    value={offerForm.currency}
                    onChange={(e) => handleOfferFormChange('currency', e.target.value.toUpperCase())}
                    placeholder="SEK"
                  />
                </div>
                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={offerForm.display_order}
                    onChange={(e) => handleOfferFormChange('display_order', e.target.value)}
                    onKeyDown={preventArrowKeyChange}
                  />
                </div>
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={offerForm.start_date}
                    onChange={(e) => handleOfferFormChange('start_date', e.target.value)}
                  />
                  <small style={{ color: '#6b7280' }}>Leave empty to activate immediately</small>
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={offerForm.end_date}
                    min={offerForm.start_date || undefined}
                    onChange={(e) => handleOfferFormChange('end_date', e.target.value)}
                  />
                  <small style={{ color: '#6b7280' }}>Leave empty for no expiry</small>
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Description</label>
                  <textarea
                    value={offerForm.description}
                    onChange={(e) => handleOfferFormChange('description', e.target.value)}
                    placeholder="Optional note shown under the offer"
                  />
                </div>
                <div className="form-actions" style={{ gridColumn: '1 / -1' }}>
                  <button type="submit" className="btn btn-primary" disabled={savingOffer || isDuplicateOfferComposition || !areAllOfferCategoriesAvailable || hasDuplicateCategoryInForm}>
                    {savingOffer ? 'Saving...' : editingOfferId ? 'Update Offer' : 'Create Offer'}
                  </button>
                  {editingOfferId && (
                    <button type="button" className="btn btn-secondary" onClick={handleCancelEditOffer} disabled={savingOffer}>
                      Cancel Edit
                    </button>
                  )}
                </div>
              </form>

              {loading ? (
                <div className="loading">
                  <div className="spinner"></div>
                </div>
              ) : (
                <div className="products-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Categories</th>
                        <th>Type</th>
                        <th>Rule</th>
                        <th>Price</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {offers.length === 0 ? (
                        <tr>
                          <td colSpan={9} style={{ textAlign: 'center' }}>No offers created yet.</td>
                        </tr>
                      ) : (
                        offers.map((offer) => {
                          const offerTypeLower = String(offer.offer_type || 'bundle').toLowerCase();
                          const offerItems = Array.isArray(offer.items) ? offer.items : [];
                          const categoriesLabel = offerTypeLower === 'bundle'
                            ? offerItems.map((item) => `${item.category} x${item.quantity ?? 1} (${offer.currency || 'SEK'} ${Number(item.category_price || 0).toFixed(2)})`).join(', ')
                            : offerItems.map((item) => item.category).join(', ');
                          return (
                          <tr key={offer.id}>
                            <td>{offer.title || '-'}</td>
                            <td>{categoriesLabel || '-'}</td>
                            <td>{offerTypeLower === 'discount' ? 'Discount' : 'Bundle'}</td>
                            <td>
                              {offerTypeLower === 'discount'
                                ? `${Number(offer.discount_percent || 0).toFixed(2)}% off across these categories`
                                : `Buy ${offerItems.reduce((sum, item) => sum + (parseInt(item.quantity, 10) || 0), 0)} items total`}
                            </td>
                            <td>
                              {offerTypeLower === 'discount'
                                ? `${Number(offer.discount_percent || 0).toFixed(2)}%`
                                : `${offer.currency || 'SEK'} ${Number(offer.offer_price || 0).toFixed(2)}`}
                            </td>
                            <td>{offer.start_date ? new Date(offer.start_date).toLocaleDateString() : '-'}</td>
                            <td>{offer.end_date ? new Date(offer.end_date).toLocaleDateString() : '-'}</td>
                            <td>
                              <span className={offer.is_active ? 'status-active' : 'status-inactive'}>
                                {offer.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td>
                              <button
                                type="button"
                                onClick={() => handleEditOffer(offer)}
                                className="btn-edit"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => handleToggleOffer(offer)}
                                className={offer.is_active ? 'btn-deactivate' : 'btn-activate'}
                              >
                                {offer.is_active ? 'Deactivate' : 'Activate'}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteOffer(offer.id)}
                                className="btn-delete"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}


          {activeTab === 'expenses' && (
            <div className="admin-section">
              <div className="section-header">
                <h2>Company Expenses</h2>
              </div>

              {expenseMessage.text && (
                <div className={`alert alert-${expenseMessage.type}`} style={{ marginBottom: '20px' }}>
                  {expenseMessage.text}
                </div>
              )}

              <form onSubmit={handleExpenseSubmit} className="expense-form-grid" style={{ marginBottom: '24px' }}>
                  <div className="form-group">
                    <label>Company or Vendor *</label>
                    <input
                      type="text"
                      required
                      value={expenseForm.company_name}
                      onChange={(e) => handleExpenseFormChange('company_name', e.target.value)}
                      placeholder="Company name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Org. Number (Optional)</label>
                    <input
                      type="text"
                      value={expenseForm.organization_number}
                      onChange={(e) => handleExpenseFormChange('organization_number', e.target.value)}
                      placeholder="e.g. 559123-4567"
                    />
                  </div>
                  <div className="form-group">
                    <label>Invoice Number *</label>
                    <input
                      type="text"
                      required
                      value={expenseForm.invoice_number}
                      onChange={(e) => handleExpenseFormChange('invoice_number', e.target.value)}
                      placeholder="e.g. INV-2026-102"
                    />
                  </div>
                  <div className="form-group">
                    <label>Price (Excl. VAT) *</label>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      required
                      value={expenseForm.amount}
                      onChange={(e) => handleExpenseFormChange('amount', e.target.value)}
                      onKeyDown={preventArrowKeyChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Currency *</label>
                    <input
                      type="text"
                      required
                      value={expenseForm.currency}
                      onChange={(e) => handleExpenseFormChange('currency', e.target.value.toUpperCase())}
                      placeholder="SEK"
                    />
                  </div>
                  <div className="form-group">
                    <label>VAT % *</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      required
                      value={expenseForm.vat_percent}
                      onChange={(e) => handleExpenseFormChange('vat_percent', e.target.value)}
                      onKeyDown={preventArrowKeyChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>VAT Amount *</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      required
                      value={expenseForm.vat_amount}
                      readOnly
                    />
                  </div>
                  <div className="form-group">
                    <label>Invoice Date *</label>
                    <input
                      type="date"
                      required
                      value={expenseForm.expense_date}
                      onChange={(e) => handleExpenseFormChange('expense_date', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Paid From *</label>
                    <select
                      value={expenseForm.payment_source}
                      onChange={(e) => handleExpenseFormChange('payment_source', e.target.value)}
                    >
                      <option value="company_account">Company Account</option>
                      <option value="personal_account">Personal Account</option>
                      <option value="credit_card">Credit Card</option>
                      <option value="wise_account">Wise Account</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Payment Status *</label>
                    <select
                      value={expenseForm.is_paid ? 'paid' : 'unpaid'}
                      onChange={(e) => handleExpenseFormChange('is_paid', e.target.value === 'paid')}
                    >
                      <option value="paid">Paid</option>
                      <option value="unpaid">Unpaid</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Paid On {expenseForm.is_paid ? '*' : '(optional)'}</label>
                    <input
                      type="date"
                      value={expenseForm.paid_on_date}
                      max={formatDateForInput(new Date())}
                      onChange={(e) => handleExpenseFormChange('paid_on_date', e.target.value)}
                      disabled={!expenseForm.is_paid}
                    />
                  </div>
                  <div className="form-group">
                    <label>Payment Due Date {!expenseForm.is_paid ? '*' : '(optional)'}</label>
                    <input
                      type="date"
                      value={expenseForm.payment_due_date}
                      min={expenseForm.expense_date || undefined}
                      onChange={(e) => handleExpenseFormChange('payment_due_date', e.target.value)}
                      disabled={expenseForm.is_paid}
                    />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Notes</label>
                    <textarea
                      value={expenseForm.notes}
                      onChange={(e) => handleExpenseFormChange('notes', e.target.value)}
                      placeholder="Optional notes"
                    />
                  </div>
                  <div className="form-actions" style={{ gridColumn: '1 / -1' }}>
                    <button type="submit" className="btn btn-primary" disabled={savingExpense}>
                      {savingExpense ? 'Saving...' : 'Add Expense'}
                    </button>
                  </div>
              </form>

              <div className="inventory-filters" style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                <h3>Expense Date Filter</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '15px' }}>
                  <div className="form-group">
                    <label>From Date</label>
                    <input
                      type="date"
                      value={pendingExpenseDateFilter.fromDate}
                      onChange={(e) => setPendingExpenseDateFilter((prev) => ({ ...prev, fromDate: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>To Date</label>
                    <input
                      type="date"
                      value={pendingExpenseDateFilter.toDate}
                      onChange={(e) => setPendingExpenseDateFilter((prev) => ({ ...prev, toDate: e.target.value }))}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                  <button
                    type="button"
                    onClick={handleApplyExpenseDateFilter}
                    className="btn btn-primary"
                  >
                    Apply Filter
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="loading">
                  <div className="spinner"></div>
                </div>
              ) : (
                <>
                  {(() => {
                    const filteredExpenses = expenses.filter((expense) => {
                      const expenseDate = formatDateYMD(expense.expense_date, '');
                      if (!expenseDate) return false;
                      if (expenseDateFilter.fromDate && expenseDate < expenseDateFilter.fromDate) return false;
                      if (expenseDateFilter.toDate && expenseDate > expenseDateFilter.toDate) return false;
                      return true;
                    });

                    const totalExpensePages = Math.max(1, Math.ceil(filteredExpenses.length / EXPENSES_PAGE_SIZE));
                    const paginatedExpenses = filteredExpenses.slice(
                      (expenseCurrentPage - 1) * EXPENSES_PAGE_SIZE,
                      expenseCurrentPage * EXPENSES_PAGE_SIZE
                    );

                    return (
                      <>
                  <div className="products-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Invoice Date</th>
                          <th>Company</th>
                          <th>Org Number</th>
                          <th>Invoice Number</th>
                          <th>Currency</th>
                          <th>Amount</th>
                          <th>VAT</th>
                          <th>VAT Amount</th>
                          <th>Total</th>
                          <th>Paid From</th>
                          <th>Status</th>
                          <th>Paid On</th>
                          <th>Due Date</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredExpenses.length === 0 ? (
                          <tr>
                            <td colSpan={14} style={{ textAlign: 'center' }}>No expenses found.</td>
                          </tr>
                        ) : (
                          paginatedExpenses
                            .map((expense) => {
                              const isPaid = Boolean(expense.is_paid);
                              const isOverdue = !isPaid && Boolean(expense.is_overdue);
                              return (
                                <tr key={expense.id}>
                                  <td>{formatDateYMD(expense.expense_date)}</td>
                                  <td>{expense.company_name}</td>
                                  <td>{expense.organization_number || '-'}</td>
                                  <td>
                                    <button
                                      type="button"
                                      className="invoice-link-btn"
                                      onClick={() => openExpenseNotes(expense)}
                                    >
                                      {expense.invoice_number}
                                    </button>
                                  </td>
                                  <td>{expense.currency || 'SEK'}</td>
                                  <td>{expense.currency || 'SEK'} {Number(expense.amount || 0).toFixed(2)}</td>
                                  <td>{Number(expense.vat_percent || 0).toFixed(2)}%</td>
                                  <td>{expense.currency || 'SEK'} {Number(expense.vat_amount || 0).toFixed(2)}</td>
                                  <td>{expense.currency || 'SEK'} {Number(expense.total_amount || 0).toFixed(2)}</td>
                                  <td>{PAYMENT_SOURCE_LABELS[expense.payment_source] || expense.payment_source}</td>
                                  <td>
                                    <span
                                      style={{
                                        fontWeight: 700,
                                        color: isPaid ? '#166534' : (isOverdue ? '#B91C1C' : '#92400E')
                                      }}
                                    >
                                      {isPaid ? 'Paid' : (isOverdue ? 'Overdue' : 'Pending')}
                                    </span>
                                  </td>
                                  <td>{formatDateYMD(expense.paid_on_date)}</td>
                                  <td>{formatDateYMD(expense.payment_due_date)}</td>
                                  <td>
                                    <button
                                      type="button"
                                      onClick={() => openEditExpense(expense)}
                                      className="btn-activate"
                                      style={{ marginRight: '6px' }}
                                    >
                                      Edit
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteExpense(expense.id)}
                                      className="btn-delete"
                                      style={{ marginRight: '6px' }}
                                    >
                                      Delete
                                    </button>
                                    {!isPaid && (
                                      <button
                                        type="button"
                                        onClick={() => openMarkExpensePaidModal(expense)}
                                        className="btn-activate"
                                      >
                                        Mark Paid
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })
                        )}
                      </tbody>
                    </table>
                  </div>
                  {filteredExpenses.length > EXPENSES_PAGE_SIZE && (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
                      <button
                        onClick={() => setExpenseCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={expenseCurrentPage === 1}
                        className="btn btn-sm"
                      >
                        ← Previous
                      </button>
                      <span>
                        Page {expenseCurrentPage} of {totalExpensePages}
                      </span>
                      <button
                        onClick={() => setExpenseCurrentPage((p) => Math.min(totalExpensePages, p + 1))}
                        disabled={expenseCurrentPage >= totalExpensePages}
                        className="btn btn-sm"
                      >
                        Next →
                      </button>
                    </div>
                  )}
                      </>
                    );
                  })()}
                </>
              )}

              {/* ===== INVESTMENTS SECTION ===== */}
              <div className="section-header" style={{ marginTop: '40px' }}>
                <h2>Business Investments</h2>
              </div>

              {investmentMessage.text && (
                <div className={`alert alert-${investmentMessage.type}`} style={{ marginBottom: '20px' }}>
                  {investmentMessage.text}
                </div>
              )}

              <form onSubmit={handleInvestmentSubmit} className="expense-form-grid" style={{ marginBottom: '24px' }}>
                <div className="form-group">
                  <label>Source Name *</label>
                  <input
                    type="text"
                    required
                    value={investmentForm.source_name}
                    onChange={(e) => handleInvestmentFormChange('source_name', e.target.value)}
                    placeholder="Who invested or source name"
                  />
                </div>
                <div className="form-group">
                  <label>Reference Number</label>
                  <input
                    type="text"
                    value={investmentForm.reference_number}
                    onChange={(e) => handleInvestmentFormChange('reference_number', e.target.value)}
                    placeholder="Optional reference"
                  />
                </div>
                <div className="form-group">
                  <label>Currency *</label>
                  <input
                    type="text"
                    required
                    value={investmentForm.currency}
                    onChange={(e) => handleInvestmentFormChange('currency', e.target.value)}
                    maxLength={5}
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>
                <div className="form-group">
                  <label>Amount *</label>
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    required
                    value={investmentForm.amount}
                    onChange={(e) => handleInvestmentFormChange('amount', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Investment Date *</label>
                  <input
                    type="date"
                    required
                    value={investmentForm.investment_date}
                    onChange={(e) => handleInvestmentFormChange('investment_date', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Received Into</label>
                  <input type="text" value="Company Account" readOnly />
                </div>
                <div className="form-group">
                  <label>Status *</label>
                  <select
                    value={investmentForm.is_received ? 'received' : 'pending'}
                    onChange={(e) => handleInvestmentFormChange('is_received', e.target.value === 'received')}
                  >
                    <option value="received">Received</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Received On {investmentForm.is_received ? '*' : '(optional)'}</label>
                  <input
                    type="date"
                    value={investmentForm.received_on_date}
                    max={formatDateForInput(new Date())}
                    onChange={(e) => handleInvestmentFormChange('received_on_date', e.target.value)}
                    disabled={!investmentForm.is_received}
                  />
                </div>
                <div className="form-group">
                  <label>Expected Date {!investmentForm.is_received ? '*' : '(optional)'}</label>
                  <input
                    type="date"
                    value={investmentForm.expected_date}
                    onChange={(e) => handleInvestmentFormChange('expected_date', e.target.value)}
                    disabled={investmentForm.is_received}
                  />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Notes</label>
                  <textarea
                    value={investmentForm.notes}
                    onChange={(e) => handleInvestmentFormChange('notes', e.target.value)}
                    placeholder="Optional notes"
                  />
                </div>
                <div className="form-actions" style={{ gridColumn: '1 / -1' }}>
                  <button type="submit" className="btn btn-primary" disabled={savingInvestment}>
                    {savingInvestment ? 'Saving...' : 'Add Investment'}
                  </button>
                </div>
              </form>

              <div className="inventory-filters" style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                <h3>Investment Date Filter</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '15px' }}>
                  <div className="form-group">
                    <label>From Date</label>
                    <input
                      type="date"
                      value={pendingInvestmentDateFilter.fromDate}
                      onChange={(e) => setPendingInvestmentDateFilter((prev) => ({ ...prev, fromDate: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>To Date</label>
                    <input
                      type="date"
                      value={pendingInvestmentDateFilter.toDate}
                      onChange={(e) => setPendingInvestmentDateFilter((prev) => ({ ...prev, toDate: e.target.value }))}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                  <button
                    type="button"
                    onClick={handleApplyInvestmentDateFilter}
                    className="btn btn-primary"
                  >
                    Apply Filter
                  </button>
                </div>
              </div>

              {(() => {
                const filteredInvestments = investments.filter((investment) => {
                  const investmentDate = formatDateYMD(investment.investment_date, '');
                  if (!investmentDate) return false;
                  if (investmentDateFilter.fromDate && investmentDate < investmentDateFilter.fromDate) return false;
                  if (investmentDateFilter.toDate && investmentDate > investmentDateFilter.toDate) return false;
                  return true;
                });

                const totalInvestmentPages = Math.max(1, Math.ceil(filteredInvestments.length / INVESTMENTS_PAGE_SIZE));
                const paginatedInvestments = filteredInvestments.slice(
                  (investmentCurrentPage - 1) * INVESTMENTS_PAGE_SIZE,
                  investmentCurrentPage * INVESTMENTS_PAGE_SIZE
                );

                return (
                  <>
              <div className="products-table">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Source</th>
                      <th>Reference</th>
                      <th>Currency</th>
                      <th>Amount</th>
                      <th>Received Into</th>
                      <th>Status</th>
                      <th>Received On</th>
                      <th>Expected Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvestments.length === 0 ? (
                      <tr>
                        <td colSpan={10} style={{ textAlign: 'center' }}>No investments found.</td>
                      </tr>
                    ) : (
                      paginatedInvestments
                        .map((inv) => {
                          const isReceived = Boolean(inv.is_received);
                          return (
                            <tr key={inv.id}>
                              <td>{formatDateYMD(inv.investment_date)}</td>
                              <td>{inv.source_name}</td>
                              <td>{inv.reference_number || '-'}</td>
                              <td>{inv.currency || 'SEK'}</td>
                              <td>{inv.currency || 'SEK'} {Number(inv.amount || 0).toFixed(2)}</td>
                              <td>{PAYMENT_SOURCE_LABELS[inv.payment_source] || inv.payment_source}</td>
                              <td>
                                <span style={{ fontWeight: 700, color: isReceived ? '#166534' : '#92400E' }}>
                                  {isReceived ? 'Received' : 'Pending'}
                                </span>
                              </td>
                              <td>{formatDateYMD(inv.received_on_date)}</td>
                              <td>{formatDateYMD(inv.expected_date)}</td>
                              <td>
                                <button
                                  type="button"
                                  onClick={() => openEditInvestment(inv)}
                                  className="btn-activate"
                                  style={{ marginRight: '6px' }}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteInvestment(inv.id)}
                                  className="btn-delete"
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          );
                        })
                    )}
                  </tbody>
                </table>
              </div>
              {filteredInvestments.length > INVESTMENTS_PAGE_SIZE && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
                  <button
                    onClick={() => setInvestmentCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={investmentCurrentPage === 1}
                    className="btn btn-sm"
                  >
                    ← Previous
                  </button>
                  <span>
                    Page {investmentCurrentPage} of {totalInvestmentPages}
                  </span>
                  <button
                    onClick={() => setInvestmentCurrentPage((p) => Math.min(totalInvestmentPages, p + 1))}
                    disabled={investmentCurrentPage >= totalInvestmentPages}
                    className="btn btn-sm"
                  >
                    Next →
                  </button>
                </div>
              )}
                  </>
                );
              })()}
            </div>
          )}

          {selectedExpenseForNotes && (
            <div className="product-form-modal" onClick={() => setSelectedExpenseForNotes(null)}>
              <div className="modal-content" style={{ maxWidth: '680px' }} onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>Invoice {selectedExpenseForNotes.invoice_number}</h3>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={() => setSelectedExpenseForNotes(null)}
                    aria-label="Close notes popup"
                  >
                    ×
                  </button>
                </div>

                <div className="expense-notes-popup">
                  <p><strong>Company:</strong> {selectedExpenseForNotes.company_name}</p>
                  <p><strong>Organization Number:</strong> {selectedExpenseForNotes.organization_number || '-'}</p>
                  <p><strong>Currency:</strong> {selectedExpenseForNotes.currency || 'SEK'}</p>
                  <p><strong>Invoice Date:</strong> {formatDateYMD(selectedExpenseForNotes.expense_date)}</p>
                  <p><strong>Paid On:</strong> {formatDateYMD(selectedExpenseForNotes.paid_on_date)}</p>
                  <p><strong>Status:</strong> {selectedExpenseForNotes.is_paid ? 'Paid' : 'Pending'}</p>
                  <hr />
                  <p><strong>Notes</strong></p>
                  <div className="expense-notes-content">
                    {selectedExpenseForNotes.notes || 'No notes provided for this invoice.'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {editingExpense && expenseEditForm && (
            <div className="product-form-modal" onClick={() => { setEditingExpense(null); setExpenseEditForm(null); }}>
              <div className="modal-content" style={{ maxWidth: '760px' }} onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>Edit Expense — {editingExpense.invoice_number}</h3>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={() => { setEditingExpense(null); setExpenseEditForm(null); }}
                    aria-label="Close edit expense"
                  >
                    ×
                  </button>
                </div>

                {expenseEditMessage.text && (
                  <div className={`message ${expenseEditMessage.type}`} style={{ marginBottom: '12px' }}>
                    {expenseEditMessage.text}
                  </div>
                )}

                <div className="expense-form-grid">
                  <div className="form-group">
                    <label>Company Name *</label>
                    <input
                      type="text"
                      value={expenseEditForm.company_name}
                      onChange={(e) => handleEditExpenseChange('company_name', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Organization Number</label>
                    <input
                      type="text"
                      value={expenseEditForm.organization_number}
                      onChange={(e) => handleEditExpenseChange('organization_number', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Invoice Number *</label>
                    <input
                      type="text"
                      value={expenseEditForm.invoice_number}
                      onChange={(e) => handleEditExpenseChange('invoice_number', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Currency *</label>
                    <input
                      type="text"
                      value={expenseEditForm.currency}
                      onChange={(e) => handleEditExpenseChange('currency', e.target.value)}
                      maxLength={5}
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Amount *</label>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={expenseEditForm.amount}
                      onChange={(e) => handleEditExpenseChange('amount', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>VAT % *</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={expenseEditForm.vat_percent}
                      onChange={(e) => handleEditExpenseChange('vat_percent', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>VAT Amount</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={expenseEditForm.vat_amount}
                      onChange={(e) => handleEditExpenseChange('vat_amount', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Invoice Date *</label>
                    <input
                      type="date"
                      value={expenseEditForm.expense_date}
                      onChange={(e) => handleEditExpenseChange('expense_date', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Paid From *</label>
                    <select
                      value={expenseEditForm.payment_source}
                      onChange={(e) => handleEditExpenseChange('payment_source', e.target.value)}
                    >
                      <option value="company_account">Company Account</option>
                      <option value="personal_account">Personal Account</option>
                      <option value="credit_card">Credit Card</option>
                      <option value="wise_account">Wise Account</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Payment Status *</label>
                    <select
                      value={expenseEditForm.is_paid ? 'paid' : 'unpaid'}
                      onChange={(e) => handleEditExpenseChange('is_paid', e.target.value === 'paid')}
                    >
                      <option value="paid">Paid</option>
                      <option value="unpaid">Unpaid</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Paid On {expenseEditForm.is_paid ? '*' : '(optional)'}</label>
                    <input
                      type="date"
                      value={expenseEditForm.paid_on_date}
                      max={formatDateForInput(new Date())}
                      onChange={(e) => handleEditExpenseChange('paid_on_date', e.target.value)}
                      disabled={!expenseEditForm.is_paid}
                    />
                  </div>
                  <div className="form-group">
                    <label>Payment Due Date {!expenseEditForm.is_paid ? '*' : '(optional)'}</label>
                    <input
                      type="date"
                      value={expenseEditForm.payment_due_date}
                      min={expenseEditForm.expense_date || undefined}
                      onChange={(e) => handleEditExpenseChange('payment_due_date', e.target.value)}
                      disabled={expenseEditForm.is_paid}
                    />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Notes</label>
                    <textarea
                      value={expenseEditForm.notes}
                      onChange={(e) => handleEditExpenseChange('notes', e.target.value)}
                      placeholder="Optional notes"
                    />
                  </div>
                </div>

                <div className="form-actions" style={{ marginTop: '16px' }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleEditExpenseSubmit}
                    disabled={savingEditExpense}
                  >
                    {savingEditExpense ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => { setEditingExpense(null); setExpenseEditForm(null); }}
                    disabled={savingEditExpense}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {markPaidModalExpense && (
            <div className="product-form-modal" onClick={() => setMarkPaidModalExpense(null)}>
              <div className="modal-content" style={{ maxWidth: '460px' }} onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>Mark Invoice As Paid</h3>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={() => setMarkPaidModalExpense(null)}
                    aria-label="Close mark paid popup"
                  >
                    ×
                  </button>
                </div>

                <div className="form-group" style={{ marginBottom: '12px' }}>
                  <label>Invoice Number</label>
                  <input type="text" value={markPaidModalExpense.invoice_number || ''} readOnly />
                </div>

                <div className="form-group" style={{ marginBottom: '12px' }}>
                  <label>Paid On *</label>
                  <input
                    type="date"
                    value={markPaidOnDate}
                    max={formatDateForInput(new Date())}
                    onChange={(e) => setMarkPaidOnDate(e.target.value)}
                  />
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleMarkExpensePaid}
                    disabled={markingExpensePaid}
                  >
                    {markingExpensePaid ? 'Saving...' : 'Confirm Paid'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => setMarkPaidModalExpense(null)}
                    disabled={markingExpensePaid}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {deleteConfirmModal && (
            <div className="product-form-modal" onClick={() => !deletingRecord && setDeleteConfirmModal(null)}>
              <div className="modal-content" style={{ maxWidth: '460px' }} onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>{deleteConfirmModal.title}</h3>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={() => !deletingRecord && setDeleteConfirmModal(null)}
                    aria-label="Close delete confirmation popup"
                    disabled={deletingRecord}
                  >
                    ×
                  </button>
                </div>

                <p style={{ marginBottom: '18px', color: '#374151', lineHeight: 1.5 }}>
                  {deleteConfirmModal.message}
                </p>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn-delete"
                    onClick={handleConfirmDeleteRecord}
                    disabled={deletingRecord}
                  >
                    {deletingRecord ? 'Deleting...' : 'Delete'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => setDeleteConfirmModal(null)}
                    disabled={deletingRecord}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {editingInvestment && investmentEditForm && (
            <div className="product-form-modal" onClick={() => { setEditingInvestment(null); setInvestmentEditForm(null); }}>
              <div className="modal-content" style={{ maxWidth: '760px' }} onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>Edit Investment — {editingInvestment.source_name}</h3>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={() => { setEditingInvestment(null); setInvestmentEditForm(null); }}
                    aria-label="Close edit investment"
                  >
                    ×
                  </button>
                </div>

                {investmentEditMessage.text && (
                  <div className={`message ${investmentEditMessage.type}`} style={{ marginBottom: '12px' }}>
                    {investmentEditMessage.text}
                  </div>
                )}

                <div className="expense-form-grid">
                  <div className="form-group">
                    <label>Source Name *</label>
                    <input
                      type="text"
                      value={investmentEditForm.source_name}
                      onChange={(e) => handleEditInvestmentChange('source_name', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Reference Number</label>
                    <input
                      type="text"
                      value={investmentEditForm.reference_number}
                      onChange={(e) => handleEditInvestmentChange('reference_number', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Currency *</label>
                    <input
                      type="text"
                      value={investmentEditForm.currency}
                      onChange={(e) => handleEditInvestmentChange('currency', e.target.value)}
                      maxLength={5}
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Amount *</label>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={investmentEditForm.amount}
                      onChange={(e) => handleEditInvestmentChange('amount', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Investment Date *</label>
                    <input
                      type="date"
                      value={investmentEditForm.investment_date}
                      onChange={(e) => handleEditInvestmentChange('investment_date', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Received Into</label>
                    <input type="text" value="Company Account" readOnly />
                  </div>
                  <div className="form-group">
                    <label>Status *</label>
                    <select
                      value={investmentEditForm.is_received ? 'received' : 'pending'}
                      onChange={(e) => handleEditInvestmentChange('is_received', e.target.value === 'received')}
                    >
                      <option value="received">Received</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Received On {investmentEditForm.is_received ? '*' : '(optional)'}</label>
                    <input
                      type="date"
                      value={investmentEditForm.received_on_date}
                      max={formatDateForInput(new Date())}
                      onChange={(e) => handleEditInvestmentChange('received_on_date', e.target.value)}
                      disabled={!investmentEditForm.is_received}
                    />
                  </div>
                  <div className="form-group">
                    <label>Expected Date {!investmentEditForm.is_received ? '*' : '(optional)'}</label>
                    <input
                      type="date"
                      value={investmentEditForm.expected_date}
                      onChange={(e) => handleEditInvestmentChange('expected_date', e.target.value)}
                      disabled={investmentEditForm.is_received}
                    />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Notes</label>
                    <textarea
                      value={investmentEditForm.notes}
                      onChange={(e) => handleEditInvestmentChange('notes', e.target.value)}
                      placeholder="Optional notes"
                    />
                  </div>
                </div>

                <div className="form-actions" style={{ marginTop: '16px' }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleEditInvestmentSubmit}
                    disabled={savingEditInvestment}
                  >
                    {savingEditInvestment ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => { setEditingInvestment(null); setInvestmentEditForm(null); }}
                    disabled={savingEditInvestment}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'orders' && (
            <div className="admin-section">
              <div className="orders-header">
                <h2>Orders</h2>
              </div>
              <div className="card" style={{ marginTop: '10px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h3 style={{ marginBottom: '4px' }}>Corporate Order</h3>
                    <p style={{ margin: 0, color: 'var(--text-gray)', fontSize: '0.9rem' }}>
                      Create an order for a corporate customer (supports retail or corporate pricing per item).
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => setShowCorporateForm((prev) => !prev)}
                  >
                    {showCorporateForm ? 'Hide Form' : 'Show Form'}
                  </button>
                </div>

                {showCorporateForm && (
                  <form onSubmit={handleCreateCorporateOrder} style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {corporateOrderError && (
                      <div className="alert alert-error">{corporateOrderError}</div>
                    )}

                    <div className="form-row" style={{ gap: '12px' }}>
                      <div className={`form-group ${corporateFieldErrors.customer_name ? 'field-error' : ''}`} style={{ flex: 1 }}>
                        <label>Customer Name*</label>
                        <input
                          type="text"
                          value={corporateOrderForm.customer_name}
                          onChange={(e) => {
                            const value = e.target.value;
                            setCorporateOrderForm({ ...corporateOrderForm, customer_name: value });
                            setCorporateFieldErrors({
                              ...corporateFieldErrors,
                              customer_name: validateCorporateField('customer_name', value, corporateOrderForm)
                            });
                            searchStoredCorporateCustomers(value);
                          }}
                          placeholder="Organization name"
                        />
                        {corporateFieldErrors.customer_name && (
                          <div className="field-error-message">{corporateFieldErrors.customer_name}</div>
                        )}
                      </div>
                      <div className={`form-group ${corporateFieldErrors.organization_number ? 'field-error' : ''}`} style={{ flex: 1 }}>
                        <label>Organization Number*</label>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <input
                            type="text"
                            value={corporateOrderForm.organization_number}
                            onChange={(e) => {
                              const value = e.target.value;
                              setCorporateOrderForm({ ...corporateOrderForm, organization_number: value });
                              setCorporateFieldErrors({
                                ...corporateFieldErrors,
                                organization_number: validateCorporateField('organization_number', value, corporateOrderForm)
                              });
                              searchStoredCorporateCustomers(value);
                            }}
                            placeholder="Org number (xxxxxx-xxxx or xxxxxxxxxx)"
                            style={{ flex: 1 }}
                          />
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleFetchCorporateDetails}
                            disabled={fetchingCorporateDetails || creatingCorporateOrder}
                          >
                            {fetchingCorporateDetails ? 'Fetching...' : 'Fetch'}
                          </button>
                        </div>
                        {corporateFieldErrors.organization_number && (
                          <div className="field-error-message">{corporateFieldErrors.organization_number}</div>
                        )}
                      </div>
                    </div>

                    {(searchingCorporateCustomers || corporateCustomerMatches.length > 0) && (
                      <div className="form-group" style={{ marginTop: '-4px' }}>
                        <label style={{ marginBottom: '6px' }}>Saved Corporate Records</label>
                        {searchingCorporateCustomers ? (
                          <div style={{ fontSize: '0.9rem', color: 'var(--text-gray)' }}>Searching saved records...</div>
                        ) : (
                          <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'hidden' }}>
                            {corporateCustomerMatches.map((match, index) => (
                              <button
                                key={`${match.organization_number || match.customer_name || 'record'}-${index}`}
                                type="button"
                                onClick={() => handleSelectStoredCorporateCustomer(match)}
                                style={{
                                  width: '100%',
                                  textAlign: 'left',
                                  border: 'none',
                                  borderBottom: index === corporateCustomerMatches.length - 1 ? 'none' : '1px solid var(--border-color)',
                                  background: 'var(--bg-secondary)',
                                  padding: '10px 12px',
                                  cursor: 'pointer'
                                }}
                              >
                                <div style={{ fontWeight: 600 }}>{match.customer_name || 'Corporate Customer'}</div>
                                <div style={{ fontSize: '0.85rem', color: 'var(--text-gray)' }}>
                                  {(match.organization_number || 'No org number')}
                                  {match.shop_name ? ` | ${match.shop_name}` : ''}
                                  {match.contact_name ? ` | ${match.contact_name}` : ''}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="form-row" style={{ gap: '12px' }}>
                      <div className={`form-group ${corporateFieldErrors.vat_number ? 'field-error' : ''}`} style={{ flex: 1 }}>
                        <label>Customer VAT Number</label>
                        <input
                          type="text"
                          value={corporateOrderForm.vat_number}
                          onChange={(e) => {
                            const value = e.target.value;
                            setCorporateOrderForm({ ...corporateOrderForm, vat_number: value });
                            setCorporateFieldErrors({
                              ...corporateFieldErrors,
                              vat_number: validateCorporateField('vat_number', value, corporateOrderForm)
                            });
                          }}
                          placeholder="EU VAT / Tax number"
                        />
                        {corporateFieldErrors.vat_number && (
                          <div className="field-error-message">{corporateFieldErrors.vat_number}</div>
                        )}
                      </div>
                      <div className={`form-group ${corporateFieldErrors.shop_name ? 'field-error' : ''}`} style={{ flex: 1 }}>
                        <label>Shop Name</label>
                        <input
                          type="text"
                          value={corporateOrderForm.shop_name}
                          onChange={(e) => {
                            const value = e.target.value;
                            setCorporateOrderForm({ ...corporateOrderForm, shop_name: value });
                            setCorporateFieldErrors({
                              ...corporateFieldErrors,
                              shop_name: validateCorporateField('shop_name', value, corporateOrderForm)
                            });
                          }}
                          placeholder="Shop or business name"
                        />
                        {corporateFieldErrors.shop_name && (
                          <div className="field-error-message">{corporateFieldErrors.shop_name}</div>
                        )}
                      </div>
                      <div className={`form-group ${corporateFieldErrors.contact_name ? 'field-error' : ''}`} style={{ flex: 1 }}>
                        <label>Contact Person</label>
                        <input
                          type="text"
                          value={corporateOrderForm.contact_name}
                          onChange={(e) => {
                            const value = e.target.value;
                            setCorporateOrderForm({ ...corporateOrderForm, contact_name: value });
                            setCorporateFieldErrors({
                              ...corporateFieldErrors,
                              contact_name: validateCorporateField('contact_name', value, corporateOrderForm)
                            });
                          }}
                          placeholder="Contact person"
                        />
                        {corporateFieldErrors.contact_name && (
                          <div className="field-error-message">{corporateFieldErrors.contact_name}</div>
                        )}
                      </div>
                    </div>

                    <div className="form-row" style={{ gap: '12px' }}>
                      <div className={`form-group ${corporateFieldErrors.customer_email ? 'field-error' : ''}`} style={{ flex: 1 }}>
                        <label>Email*</label>
                        <input
                          type="email"
                          value={corporateOrderForm.customer_email}
                          onChange={(e) => {
                            const value = e.target.value;
                            setCorporateOrderForm({ ...corporateOrderForm, customer_email: value });
                            setCorporateFieldErrors({
                              ...corporateFieldErrors,
                              customer_email: validateCorporateField('customer_email', value, corporateOrderForm)
                            });
                          }}
                          placeholder="customer@example.com"
                        />
                        {corporateFieldErrors.customer_email && (
                          <div className="field-error-message">{corporateFieldErrors.customer_email}</div>
                        )}
                      </div>
                      <div className={`form-group ${corporateFieldErrors.customer_phone ? 'field-error' : ''}`} style={{ flex: 1 }}>
                        <label>Phone*</label>
                        <input
                          type="text"
                          value={corporateOrderForm.customer_phone}
                          onChange={(e) => {
                            const value = e.target.value;
                            setCorporateOrderForm({ ...corporateOrderForm, customer_phone: value });
                            setCorporateFieldErrors({
                              ...corporateFieldErrors,
                              customer_phone: validateCorporateField('customer_phone', value, corporateOrderForm)
                            });
                          }}
                          placeholder="+46 ..."
                        />
                        {corporateFieldErrors.customer_phone && (
                          <div className="field-error-message">{corporateFieldErrors.customer_phone}</div>
                        )}
                      </div>
                    </div>

                    <div className={`form-group ${corporateFieldErrors.address_line1 ? 'field-error' : ''}`}>
                      <label>Address Line 1*</label>
                      <input
                        type="text"
                        value={corporateOrderForm.address_line1}
                        onChange={(e) => {
                          const value = e.target.value;
                          setCorporateOrderForm({ ...corporateOrderForm, address_line1: value });
                          setCorporateFieldErrors({
                            ...corporateFieldErrors,
                            address_line1: validateCorporateField('address_line1', value, corporateOrderForm)
                          });
                        }}
                        placeholder="Street address, P.O. box"
                      />
                      {corporateFieldErrors.address_line1 && (
                        <div className="field-error-message">{corporateFieldErrors.address_line1}</div>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Address Line 2</label>
                      <input
                        type="text"
                        value={corporateOrderForm.address_line2}
                        onChange={(e) => setCorporateOrderForm({ ...corporateOrderForm, address_line2: e.target.value })}
                        placeholder="Apartment, suite, unit, building, floor, etc."
                      />
                    </div>

                    <div className="form-row" style={{ gap: '12px' }}>
                      <div className={`form-group ${corporateFieldErrors.city ? 'field-error' : ''}`} style={{ flex: 1 }}>
                        <label>City*</label>
                        <input
                          type="text"
                          value={corporateOrderForm.city}
                          onChange={(e) => {
                            const value = e.target.value;
                            setCorporateOrderForm({ ...corporateOrderForm, city: value });
                            setCorporateFieldErrors({
                              ...corporateFieldErrors,
                              city: validateCorporateField('city', value, corporateOrderForm)
                            });
                          }}
                          placeholder="City"
                        />
                        {corporateFieldErrors.city && (
                          <div className="field-error-message">{corporateFieldErrors.city}</div>
                        )}
                      </div>
                      <div className={`form-group ${corporateFieldErrors.pincode ? 'field-error' : ''}`} style={{ flex: 1 }}>
                        <label>Postal Code*</label>
                        <input
                          type="text"
                          value={corporateOrderForm.pincode}
                          onChange={(e) => {
                            const value = e.target.value;
                            setCorporateOrderForm({ ...corporateOrderForm, pincode: value });
                            setCorporateFieldErrors({
                              ...corporateFieldErrors,
                              pincode: validateCorporateField('pincode', value, corporateOrderForm)
                            });
                          }}
                          placeholder="Postal code"
                        />
                        {corporateFieldErrors.pincode && (
                          <div className="field-error-message">{corporateFieldErrors.pincode}</div>
                        )}
                      </div>
                    </div>

                    <div className={`form-group ${corporateFieldErrors.country ? 'field-error' : ''}`}>
                      <label>Country*</label>
                      <select
                        value={corporateOrderForm.country || 'Sweden'}
                        onChange={(e) => {
                          const value = e.target.value;
                          setCorporateOrderForm({ ...corporateOrderForm, country: value });
                          setCorporateFieldErrors({
                            ...corporateFieldErrors,
                            country: validateCorporateField('country', value, corporateOrderForm),
                            vat_number: validateCorporateField('vat_number', corporateOrderForm.vat_number, { ...corporateOrderForm, country: value })
                          });
                        }}
                      >
                        <option value="">-- Select country --</option>
                        {EU_COUNTRY_OPTIONS.map((country) => (
                          <option key={country} value={country}>{country}</option>
                        ))}
                      </select>
                      {corporateFieldErrors.country && (
                        <div className="field-error-message">{corporateFieldErrors.country}</div>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Currency *</label>
                      <select
                        value={corporateOrderForm.currency}
                        onChange={(e) => setCorporateOrderForm({ ...corporateOrderForm, currency: e.target.value })}
                      >
                        <option value="SEK">SEK</option>
                        <option value="EUR">EUR</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Notes</label>
                      <textarea
                        rows="2"
                        value={corporateOrderForm.notes}
                        onChange={(e) => setCorporateOrderForm({ ...corporateOrderForm, notes: e.target.value })}
                        placeholder="Special instructions"
                      />
                    </div>

                    {corporateFieldErrors.items && (
                      <div className="field-error-message" style={{ marginBottom: '8px' }}>{corporateFieldErrors.items}</div>
                    )}

                    <div className="form-row" style={{ gap: '10px', alignItems: 'flex-end', flexWrap: 'nowrap' }}>
                      <div className="form-group" style={{ flex: 2 }}>
                        <label>Add Product</label>
                        <select
                          value={corporateSelection.product_id}
                          onChange={(e) => setCorporateSelection({ ...corporateSelection, product_id: e.target.value })}
                        >
                          <option value="">-- Choose product --</option>
                          {products
                            .filter((product) => {
                              const stockQuantity = parseInt(product.stock_quantity, 10) || 0;
                              const defectiveCount = parseInt(product.defective_count, 10) || 0;
                              const availableStock = Math.max(0, stockQuantity - defectiveCount);
                              return product.in_stock && availableStock > 0;
                            })
                            .map((product) => (
                            <option key={product.id} value={product.id}>
                              {product.name} (Available: {Math.max(0, (parseInt(product.stock_quantity, 10) || 0) - (parseInt(product.defective_count, 10) || 0))}) (Retail: {corporateCurrencySymbol} {product.price ?? 0}, Corporate: {corporateCurrencySymbol} {corporateOrderForm.currency === 'EUR' ? (product.wholesale_price_eur ?? product.wholesale_price ?? product.price ?? 0) : (product.wholesale_price ?? product.price ?? 0)})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group" style={{ width: '180px' }}>
                        <label>Price Type</label>
                        <select
                          value={corporateSelection.price_type || 'corporate'}
                          onChange={(e) => setCorporateSelection({ ...corporateSelection, price_type: e.target.value })}
                        >
                          <option value="corporate">Corporate Price</option>
                          <option value="retail">Retail Price</option>
                        </select>
                      </div>
                      <div className="form-group" style={{ width: '140px' }}>
                        <label>Quantity</label>
                        <input
                          type="number"
                          min="1"
                          value={corporateSelection.quantity}
                          onChange={(e) => setCorporateSelection({ ...corporateSelection, quantity: e.target.value })}
                          onKeyDown={preventArrowKeyChange}
                        />
                      </div>
                      <div className="form-group" style={{ width: '160px', marginBottom: '20px' }}>
                        <label style={{ visibility: 'hidden' }}>Action</label>
                        <button type="button" className="btn btn-secondary" onClick={handleAddCorporateItem} style={{ width: '100%' }}>
                          Add to order
                        </button>
                      </div>
                    </div>

                    {corporateItemsWithDetails.length > 0 && (
                      <div className="table-wrapper" style={{ overflowX: 'auto' }}>
                        <table className="orders-table">
                          <thead>
                            <tr>
                              <th>Product</th>
                              <th>Price Type</th>
                              <th>Unit Price</th>
                              <th>Quantity</th>
                              <th>Subtotal</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {corporateItemsWithDetails.map((item) => (
                              <tr key={`${item.product_id}-${item.priceType}`}>
                                <td>{item.name}</td>
                                <td>{item.priceType === 'retail' ? 'Retail' : 'Corporate'}</td>
                                <td>{corporateCurrencySymbol} {Number(item.price).toFixed(2)}</td>
                                <td>
                                  <input
                                    type="number"
                                    min="1"
                                    value={item.quantity}
                                    onChange={(e) => handleCorporateItemQuantityChange(item.product_id, item.priceType, e.target.value)}
                                    style={{ width: '70px' }}
                                    onKeyDown={preventArrowKeyChange}
                                  />
                                </td>
                                <td>{corporateCurrencySymbol} {(Number(item.price) * Number(item.quantity)).toFixed(2)}</td>
                                <td>
                                  <button type="button" className="btn btn-outline" onClick={() => handleRemoveCorporateItem(item.product_id, item.priceType)}>
                                    Remove
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                          <strong>Total: {corporateCurrencySymbol} {corporateOrderTotal.toFixed(2)}</strong>
                        </div>
                      </div>
                    )}

                    <div className="form-actions" style={{ justifyContent: 'flex-start', gap: '10px' }}>
                      <button type="submit" className="btn btn-primary" disabled={creatingCorporateOrder}>
                        {creatingCorporateOrder ? 'Creating...' : 'Create Corporate Order'}
                      </button>
                      <button type="button" className="btn btn-outline" onClick={resetCorporateOrderForm} disabled={creatingCorporateOrder}>
                        Clear
                      </button>
                      <button type="button" className="btn btn-outline" onClick={() => setShowCorporateForm(false)} disabled={creatingCorporateOrder}>
                        Close
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* Return Order Form - Directly after Corporate Order */}
              <div className="card" style={{ marginTop: '10px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h3 style={{ marginBottom: '4px' }}>Return Order</h3>
                    <p style={{ margin: 0, color: 'var(--text-gray)', fontSize: '0.9rem' }}>
                      Process returns for shipped or delivered orders. Returns are processed immediately.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => setShowReturnForm((prev) => !prev)}
                  >
                    {showReturnForm ? 'Hide Form' : 'Show Form'}
                  </button>
                </div>

                {showReturnForm && (
                  <form onSubmit={handleCreateReturn} style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>

                      <div className="form-row" style={{ gap: '12px' }}>
                        <div className="form-group" style={{ flex: 1 }}>
                          <label>Select Order (Type Order Number)*</label>
                          <input
                            type="text"
                            list="eligible-return-orders"
                            value={returnOrderInput}
                            onChange={(e) => handleReturnOrderInputChange(e.target.value)}
                            placeholder="Type order number"
                            required
                          />
                          <datalist id="eligible-return-orders">
                            {getEligibleReturnOrders().map((order) => (
                              <option key={order.id} value={order.order_number}>
                                {order.customer_name} ({order.status})
                              </option>
                            ))}
                          </datalist>
                          <small style={{ color: 'var(--text-gray)' }}>
                            Only shipped/delivered orders created within the last 30 days are eligible for return.
                          </small>
                        </div>
                      </div>

                      {returnFormData.order_id && returnFormData.items.length > 0 && (
                        <div style={{ marginTop: '15px' }}>
                          <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>Select Items to Return (Set quantity for each item)*</label>
                          <div className="table-wrapper" style={{ overflowX: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                            <table className="orders-table" style={{ marginBottom: 0 }}>
                              <thead>
                                <tr>
                                  <th>Product</th>
                                  <th>Ordered Qty</th>
                                  <th>Already Returned</th>
                                  <th>Available for Return</th>
                                  <th>Return Qty</th>
                                </tr>
                              </thead>
                              <tbody>
                                {returnFormData.items.map((item, index) => (
                                  <tr key={item.order_item_id}>
                                    <td>{item.product_name}</td>
                                    <td>{item.max_quantity}</td>
                                    <td style={{ color: item.quantity_returned_already > 0 ? '#dc3545' : 'inherit' }}>
                                      {item.quantity_returned_already > 0 ? item.quantity_returned_already : '-'}
                                    </td>
                                    <td>{item.available_quantity}</td>
                                    <td>
                                      <input
                                        type="number"
                                        min="0"
                                        max={item.available_quantity}
                                        value={item.quantity_returned}
                                        onChange={(e) => {
                                          const newItems = [...returnFormData.items];
                                          newItems[index].quantity_returned = Math.min(parseInt(e.target.value) || 0, item.available_quantity);
                                          setReturnFormData({ ...returnFormData, items: newItems });
                                        }}
                                        style={{ width: '80px', padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }}
                                        disabled={item.available_quantity === 0}
                                        onKeyDown={preventArrowKeyChange}
                                      />
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      <div className="form-group">
                        <label>Return Reason</label>
                        <textarea
                          rows="2"
                          value={returnFormData.reason || ''}
                          onChange={(e) => setReturnFormData({ ...returnFormData, reason: e.target.value })}
                          placeholder="e.g., Defective, Wrong item, Customer request"
                        />
                      </div>

                      <div className="form-group">
                        <label>Notes</label>
                        <textarea
                          rows="2"
                          value={returnFormData.notes || ''}
                          onChange={(e) => setReturnFormData({ ...returnFormData, notes: e.target.value })}
                          placeholder="Additional notes"
                        />
                      </div>

                      <div className="form-actions" style={{ justifyContent: 'flex-start', gap: '10px' }}>
                        <button type="submit" className="btn btn-primary" disabled={creatingCorporateOrder}>
                          Process Return
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline"
                          onClick={() => {
                            setShowReturnForm(false);
                            setReturnFormData({
                              order_id: '',
                              items: [],
                              reason: '',
                              notes: ''
                            });
                            setReturnOrderInput('');
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline"
                          onClick={() => {
                            setShowReturnForm(false);
                            setReturnOrderInput('');
                          }}
                        >
                          Close
                        </button>
                      </div>
                    </form>
                  )}
              </div>

              {orderMessage.text && (
                <div className={`alert alert-${orderMessage.type}`} style={{ marginBottom: '20px' }}>
                  {orderMessage.text}
                </div>
              )}

              {/* Order Management Section */}
              <div style={{ marginTop: '30px' }}>
                <div className="orders-header">
                  <h3>Order Management</h3>
                  <button
                    onClick={() => setShowDateFilter(!showDateFilter)}
                    className="btn btn-outline"
                  >
                    {showDateFilter ? 'Hide Filters' : 'Show Filters'}
                  </button>
                </div>
              </div>

              {showDateFilter && (
                <div className="date-filter-section">
                  <div className="filter-row">
                    <div className="form-group">
                      <label>Start Date</label>
                      <input
                        type="date"
                        value={dateFilter.startDate}
                        onChange={(e) => setDateFilter({ ...dateFilter, startDate: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>End Date</label>
                      <input
                        type="date"
                        value={dateFilter.endDate}
                        onChange={(e) => setDateFilter({ ...dateFilter, endDate: e.target.value })}
                      />
                    </div>
                    <button
                      onClick={() => setDateFilter({
                        startDate: defaultOrderDateRange.startDate,
                        endDate: defaultOrderDateRange.endDate,
                      })}
                      className="btn btn-outline"
                      style={{ alignSelf: 'flex-end' }}
                    >
                      Reset to Last 30 Days
                    </button>
                  </div>
                </div>
              )}


              {loading ? (
                <div className="loading">
                  <div className="spinner"></div>
                </div>
              ) : (
                <div className="orders-tabbed">
                  <div className="order-status-tabs">
                    {['pending', 'shipped', 'delivered', 'cancelled', 'unpaid'].map((status) => {
                      // Count for badge
                      const count = status === 'unpaid'
                        ? orders.filter((o) => !Boolean(o.is_paid) && String(o.status || '').toLowerCase() !== 'cancelled').length
                        : orders.filter((o) => o.status === status).length;
                      return (
                        <button
                          key={status}
                          className={`tab-pill ${activeOrderStatus === status ? 'active' : ''}`}
                          onClick={() => setActiveOrderStatus(status)}
                        >
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                          <span className="pill-count">{count}</span>
                        </button>
                      );
                    })}
                  </div>


                  {(() => {
                    // Filter orders by active status
                    let filteredOrders = activeOrderStatus === 'unpaid'
                      ? orders.filter((o) => !Boolean(o.is_paid) && String(o.status || '').toLowerCase() !== 'cancelled')
                      : orders.filter((o) => o.status === activeOrderStatus);


                    // Apply date filter for status tabs except unpaid
                    if (activeOrderStatus !== 'unpaid' && (dateFilter.startDate || dateFilter.endDate)) {
                      filteredOrders = filteredOrders.filter(o => {
                        const orderDate = new Date(o.created_at);
                        if (dateFilter.startDate) {
                          const startDate = new Date(dateFilter.startDate);
                          if (orderDate < startDate) return false;
                        }
                        if (dateFilter.endDate) {
                          const endDate = new Date(dateFilter.endDate);
                          endDate.setHours(23, 59, 59);
                          if (orderDate > endDate) return false;
                        }
                        return true;
                      });
                    }

                    const totalOrderPages = Math.max(1, Math.ceil(filteredOrders.length / ORDERS_PAGE_SIZE));
                    const currentOrderPage = Math.min(orderCurrentPage, totalOrderPages);
                    const paginatedOrders = filteredOrders.slice(
                      (currentOrderPage - 1) * ORDERS_PAGE_SIZE,
                      currentOrderPage * ORDERS_PAGE_SIZE
                    );


                    return (
                      <div className="status-group">
                        {activeOrderStatus === 'delivered' && filteredOrders.length > 0 && (
                          <div style={{ marginBottom: '15px' }}>
                            <button
                              onClick={handleDownloadAllInvoices}
                              disabled={loading}
                              className="btn btn-primary"
                              style={{
                                fontSize: '0.95rem',
                                padding: '10px 20px',
                                backgroundColor: '#4CAF50',
                                opacity: loading ? 0.6 : 1,
                                cursor: loading ? 'not-allowed' : 'pointer'
                              }}
                              title="Download all displayed delivered order invoices as ZIP"
                            >
                              {loading ? '⏳ Generating...' : '📥 Download All Invoices (ZIP)'}
                            </button>
                          </div>
                        )}
                        {filteredOrders.length > 0 ? (
                          <table className={`orders-table status-${activeOrderStatus}`}>
                            <thead>
                              <tr>
                                <th>Order ID</th>
                                <th>Customer</th>
                                <th>Org #</th>
                                <th>Email</th>
                                <th>Date</th>
                                <th>Items</th>
                                <th>Total</th>
                                <th>Payment</th>
                                <th>Status</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paginatedOrders.map((order) => (
                                <tr key={order.id}>
                                  <td>
                                    <button
                                      type="button"
                                      className="order-id-link"
                                      onClick={() => openOrderItemsPopup(order)}
                                      disabled={order.status === 'cancelled'}
                                      title={order.status === 'cancelled' ? 'Cancelled orders cannot be opened' : 'View ordered items'}
                                    >
                                      {order.order_number}
                                    </button>
                                  </td>
                                  <td>{order.customer_name}</td>
                                  <td>{order.organization_number || '-'}</td>
                                  <td>{order.customer_email}</td>
                                  <td>{new Date(order.created_at).toLocaleDateString()}</td>
                                  <td>{order.items?.length || 0}</td>
                                  <td>{getCurrencySymbol(order.currency)} {recomputeOrderTotals(order).grandTotal.toFixed(2)}</td>
                                  <td>
                                    <span className={order.is_paid ? 'status-active' : 'status-inactive'}>
                                      {order.is_paid ? 'Paid' : 'Unpaid'}
                                    </span>
                                  </td>
                                  <td>
                                    <select
                                      value={order.status}
                                      onChange={(e) => handleOrderStatusUpdate(order.id, e.target.value)}
                                      className="status-select"
                                      disabled={order.status === 'delivered' || order.status === 'cancelled'}
                                      title={order.status === 'delivered' || order.status === 'cancelled' ? 'Status cannot be changed once delivered or cancelled' : 'Change order status'}
                                    >
                                      <option value="pending">Pending</option>
                                      <option value="shipped">Shipped</option>
                                      <option value="delivered">Delivered</option>
                                      <option value="cancelled">Cancelled</option>
                                    </select>
                                  </td>
                                  <td>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                      <button
                                        onClick={() => {
                                          const totals = recomputeOrderTotals(order);
                                          const isCorporateOrder = Boolean(order.organization_number);
                                          const normalizedOrder = {
                                            ...order,
                                            items: (order.items || []).map((item) => {
                                              if (!isCorporateOrder) return item;
                                              const product = products.find((p) => String(p.id) === String(item.product_id));
                                              return {
                                                ...item,
                                                price_type: inferCorporatePriceType(product, item.price)
                                              };
                                            }),
                                            discount_type: order.discount_type === 'amount' ? 'amount' : 'percentage',
                                            discount_amount: parseFloat(order.discount_amount || 0) || 0,
                                            notes: order.notes || '',
                                            total_amount: totals.itemsTotal || order.total_amount,
                                            computed_total_with_shipping: totals.grandTotal,
                                          };
                                          setEditingOrder(normalizedOrder);
                                          setOriginalOrder(JSON.parse(JSON.stringify(normalizedOrder)));
                                          setShowOrderDetail(true);
                                          setAddProductToOrder({
                                            product_id: '',
                                            quantity: 1,
                                            price_type: isCorporateOrder ? 'corporate' : 'retail'
                                          });
                                          const initialDiscountType = order.discount_type === 'amount' ? 'amount' : 'percentage';
                                          setDiscountForm({
                                            type: initialDiscountType,
                                            value: initialDiscountType === 'amount'
                                              ? String(parseFloat(order.discount_amount || 0) || 0)
                                              : String(parseFloat(order.discount_percent || 0) || 0)
                                          });
                                        }}
                                        disabled={order.status !== 'pending'}
                                        className="btn btn-primary"
                                        style={{
                                          fontSize: '0.85rem',
                                          padding: '6px 12px',
                                          opacity: order.status !== 'pending' ? 0.5 : 1,
                                          cursor: order.status !== 'pending' ? 'not-allowed' : 'pointer'
                                        }}
                                        title={order.status !== 'pending' ? 'Only pending orders can be edited' : 'Edit order'}
                                      >
                                        Edit
                                      </button>
                                      {(order.status === 'shipped' || order.status === 'delivered') && (
                                        <button
                                          onClick={() => handleDownloadInvoice(order.id, order.order_number)}
                                          className="btn btn-secondary"
                                          style={{
                                            fontSize: '0.85rem',
                                            padding: '6px 12px'
                                          }}
                                          title="Download invoice PDF"
                                        >
                                          📄 Invoice
                                        </button>
                                      )}
                                      {order.organization_number && (
                                        <button
                                          onClick={() => handleSendCorporateOrderEmail(order.id)}
                                          disabled={order.status === 'cancelled' || sendingCorporateEmailOrderId === order.id}
                                          className="btn btn-secondary"
                                          style={{
                                            fontSize: '0.85rem',
                                            padding: '6px 12px',
                                            opacity: (order.status === 'cancelled' || sendingCorporateEmailOrderId === order.id) ? 0.7 : 1,
                                            cursor: (order.status === 'cancelled' || sendingCorporateEmailOrderId === order.id) ? 'not-allowed' : 'pointer'
                                          }}
                                          title={order.status === 'cancelled' ? 'Cancelled orders cannot send email' : 'Send order email to corporate customer'}
                                        >
                                          {sendingCorporateEmailOrderId === order.id ? 'Sending...' : 'Send Email'}
                                        </button>
                                      )}
                                      <button
                                        onClick={() => handleOrderPaymentUpdate(order.id, !Boolean(order.is_paid))}
                                        disabled={order.status === 'cancelled'}
                                        className={order.is_paid ? 'btn btn-secondary' : 'btn btn-primary'}
                                        style={{
                                          fontSize: '0.85rem',
                                          padding: '6px 12px',
                                          opacity: order.status === 'cancelled' ? 0.7 : 1,
                                          cursor: order.status === 'cancelled' ? 'not-allowed' : 'pointer'
                                        }}
                                        title={order.status === 'cancelled' ? 'Cancelled orders cannot update payment' : (order.is_paid ? 'Mark as unpaid' : 'Mark as paid')}
                                      >
                                        {order.is_paid ? 'Mark Unpaid' : 'Mark Paid'}
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <p className="no-orders">No orders with this status</p>
                        )}

                        {filteredOrders.length > 0 && totalOrderPages > 1 && (
                          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
                            <button
                              onClick={() => setOrderCurrentPage((page) => Math.max(1, page - 1))}
                              disabled={currentOrderPage === 1}
                              className="btn btn-sm"
                            >
                              ← Previous
                            </button>
                            <span>
                              Page {currentOrderPage} of {totalOrderPages}
                            </span>
                            <button
                              onClick={() => setOrderCurrentPage((page) => Math.min(totalOrderPages, page + 1))}
                              disabled={currentOrderPage === totalOrderPages}
                              className="btn btn-sm"
                            >
                              Next →
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}


              {showOrderDetail && editingOrder && (
                <div className="product-form-modal">
                  <div className="modal-content" style={{ maxWidth: '1000px', maxHeight: '90vh', overflowY: 'auto' }}>
                    <div className="modal-header">
                      <h3>Edit Order {editingOrder.order_number}</h3>
                      <button
                        type="button"
                        className="modal-close-btn"
                        onClick={() => {
                          setShowOrderDetail(false);
                          setEditingOrder(null);
                        }}
                        aria-label="Close"
                      >
                        x
                      </button>
                    </div>
                    {editOrderMessage.text && (
                      <div className={`alert alert-${editOrderMessage.type}`} style={{ marginBottom: '20px' }}>
                        {editOrderMessage.text}
                      </div>
                    )}
                    <div className="order-edit-details">
                      <div className="order-info-grid">
                        <div>
                          <p><strong>Customer:</strong> {editingOrder.customer_name}</p>
                          <p><strong>Email:</strong> {editingOrder.customer_email}</p>
                          <p><strong>Phone:</strong> {editingOrder.customer_phone}</p>
                          {editingOrder.organization_number && (
                            <p><strong>Organization #:</strong> {editingOrder.organization_number}</p>
                          )}
                        </div>
                        <div>
                          <p><strong>Status:</strong> {editingOrder.status}</p>
                          <p><strong>Date:</strong> {new Date(editingOrder.created_at).toLocaleString()}</p>
                          <p><strong>Total:</strong> {getCurrencySymbol(editingOrder.currency)} {parseFloat(editingOrder.total_amount || 0).toFixed(2)}</p>
                          <p><strong>Total with shipping:</strong> {getCurrencySymbol(editingOrder.currency)} {parseFloat(editingOrder.computed_total_with_shipping ?? (parseFloat(editingOrder.total_amount || 0) + parseFloat(editingOrder.shipping_charges || 0))).toFixed(2)}</p>
                        </div>
                      </div>
                     
                      <h4 style={{ marginTop: '30px', marginBottom: '15px' }}>Current Order Items</h4>
                      {editingOrder.items && editingOrder.items.length > 0 ? (
                        <table className="order-items-table">
                          <thead>
                            <tr>
                              <th>Product</th>
                              <th>Weight</th>
                              {editingOrder.organization_number && <th>Price Type</th>}
                              <th>Price</th>
                              <th>Ordered Qty</th>
                              <th>Returned Qty</th>
                              <th>Subtotal</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {editingOrder.items.map((item) => (
                              <tr key={item.id} style={{ backgroundColor: item.quantity_returned > 0 ? '#fff3cd' : 'transparent' }}>
                                <td>{item.product_name}</td>
                                <td>{item.weight && item.weight > 0 ? `${item.weight}g` : '-'}</td>
                                {editingOrder.organization_number && (
                                  <td>
                                    <select
                                      value={item.price_type || inferCorporatePriceType(products.find((p) => String(p.id) === String(item.product_id)), item.price)}
                                      onChange={(e) => handleOrderItemPriceTypeChange(item.id, e.target.value)}
                                      style={{ width: '130px' }}
                                    >
                                      <option value="corporate">Corporate</option>
                                      <option value="retail">Retail</option>
                                    </select>
                                  </td>
                                )}
                                <td>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={item.price}
                                    onChange={(e) => handleUpdateOrderItemPrice(editingOrder.id, item.id, e.target.value)}
                                    style={{ width: '80px', padding: '5px' }}
                                    onKeyDown={preventArrowKeyChange}
                                  />
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    min="1"
                                    value={item.quantity}
                                    onChange={(e) => handleUpdateOrderItemQuantity(editingOrder.id, item.id, e.target.value)}
                                    style={{ width: '60px', padding: '5px', backgroundColor: item.quantity_returned > 0 ? '#fffacd' : 'white' }}
                                    onKeyDown={preventArrowKeyChange}
                                  />
                                </td>
                                <td style={{ color: item.quantity_returned > 0 ? '#dc3545' : 'inherit' }}>
                                  {item.quantity_returned > 0 ? item.quantity_returned : '-'}
                                </td>
                                <td>kr {((item.quantity || 0) * item.price).toFixed(2)}</td>
                                <td>
                                  <button
                                    onClick={() => handleRemoveOrderItem(editingOrder.id, item.id)}
                                    className="btn-remove"
                                    style={{ fontSize: '0.85rem', padding: '5px 10px' }}
                                  >
                                    Remove
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <p>No items in this order</p>
                      )}


                      <h4 style={{ marginTop: '30px', marginBottom: '15px' }}>Add New Product to Order</h4>
                      <div className="add-product-section">
                        <div className="form-row" style={{ gap: '10px', alignItems: 'flex-end' }}>
                          <div className="form-group" style={{ flex: 1 }}>
                            <label>Select Product</label>
                            <select
                              value={addProductToOrder.product_id}
                              onChange={(e) => setAddProductToOrder({ ...addProductToOrder, product_id: e.target.value })}
                              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--border-color)' }}
                            >
                              <option value="">-- Choose a product --</option>
                              {products
                                .filter((product) => product.is_active && Math.max(0, (parseInt(product.stock_quantity, 10) || 0) - (parseInt(product.defective_count, 10) || 0)) > 0)
                                .map((product) => (
                                <option key={product.id} value={product.id}>
                                  {product.name} (Available: {Math.max(0, (parseInt(product.stock_quantity, 10) || 0) - (parseInt(product.defective_count, 10) || 0))})
                                  {editingOrder?.organization_number
                                    ? ` (Retail: kr ${product.price ?? 0}, Corporate: kr ${product.wholesale_price ?? product.price ?? 0})`
                                    : ` (kr ${product.price ?? 0})`}
                                </option>
                              ))}
                            </select>
                          </div>
                          {editingOrder.organization_number && (
                            <div className="form-group" style={{ width: '140px' }}>
                              <label>Price Type</label>
                              <select
                                value={addProductToOrder.price_type || 'corporate'}
                                onChange={(e) => setAddProductToOrder({ ...addProductToOrder, price_type: e.target.value })}
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--border-color)' }}
                              >
                                <option value="corporate">Corporate</option>
                                <option value="retail">Retail</option>
                              </select>
                            </div>
                          )}
                          <div className="form-group" style={{ width: '120px' }}>
                            <label>Quantity</label>
                            <input
                              type="number"
                              min="1"
                              value={addProductToOrder.quantity}
                              onChange={(e) => setAddProductToOrder({ ...addProductToOrder, quantity: e.target.value === '' ? '' : parseInt(e.target.value, 10) })}
                              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--border-color)' }}
                              onKeyDown={preventArrowKeyChange}
                            />
                          </div>
                          <button
                            onClick={handleAddProductToOrder}
                            className="btn btn-primary"
                            style={{ marginBottom: 0 }}
                          >
                            Add Product
                          </button>
                        </div>
                      </div>

                      <h4 style={{ marginTop: '30px', marginBottom: '15px' }}>Shipping Charges</h4>
                      <div className="shipping-section">
                        <div className="form-row" style={{ gap: '10px', alignItems: 'flex-end' }}>
                          <div className="form-group" style={{ width: '200px' }}>
                            <label>Shipping Charges (kr)</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={editingOrder.shipping_charges === 0 ? 0 : (editingOrder.shipping_charges ?? '')}
                              onChange={(e) => handleShippingInputChange(editingOrder.id, e.target.value, false)}
                              onBlur={(e) => handleShippingInputChange(editingOrder.id, e.target.value === '' ? 0 : e.target.value, true)}
                              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--border-color)' }}
                              onKeyDown={preventArrowKeyChange}
                            />
                          </div>
                        </div>
                        <p style={{ marginTop: '10px', fontSize: '0.9rem', color: 'var(--text-gray)' }}>
                          <strong>Order Total (with shipping):</strong> {getCurrencySymbol(editingOrder.currency)} {(editingOrder.computed_total_with_shipping ?? (parseFloat(editingOrder.total_amount || 0) + parseFloat(editingOrder.shipping_charges || 0))).toFixed(2)}
                        </p>
                      </div>

                      <h4 style={{ marginTop: '30px', marginBottom: '15px' }}>Order Notes</h4>
                      <div className="notes-section">
                        <div className="form-group">
                          <label>Add or update notes for this order</label>
                          <textarea
                            value={editingOrder.notes || ''}
                            onChange={(e) => setEditingOrder({ ...editingOrder, notes: e.target.value })}
                            rows={4}
                            maxLength={2000}
                            placeholder="Enter additional information for this order"
                            style={{
                              width: '100%',
                              padding: '10px',
                              borderRadius: '4px',
                              border: '1px solid var(--border-color)',
                              resize: 'vertical'
                            }}
                          />
                        </div>
                      </div>

                      <h4 style={{ marginTop: '30px', marginBottom: '15px' }}>Apply Discount</h4>
                      <div className="discount-section">
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '15px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <label style={{ marginBottom: '5px', fontSize: '0.9rem', fontWeight: 'bold' }}>Discount Type</label>
                            <select
                              value={discountForm.type}
                              onChange={(e) => {
                                const nextType = e.target.value === 'amount' ? 'amount' : 'percentage';
                                const nextValue = nextType === 'amount'
                                  ? String(parseFloat(editingOrder.discount_amount || 0) || 0)
                                  : String(parseFloat(editingOrder.discount_percent || 0) || 0);
                                setDiscountForm({ type: nextType, value: nextValue });
                              }}
                              style={{ width: '170px', padding: '8px', borderRadius: '4px', border: '1px solid var(--border-color)' }}
                            >
                              <option value="percentage">Percentage (%)</option>
                              <option value="amount">Absolute Amount (SEK)</option>
                            </select>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <label style={{ marginBottom: '5px', fontSize: '0.9rem', fontWeight: 'bold' }}>
                              {discountForm.type === 'amount' ? 'Discount Amount (SEK)' : 'Discount Percentage (%)'}
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              max={discountForm.type === 'percentage' ? '100' : undefined}
                              value={discountForm.value}
                              onChange={(e) => setDiscountForm({ ...discountForm, value: e.target.value })}
                              placeholder={discountForm.type === 'amount' ? 'Enter amount in SEK' : 'Enter discount %'}
                              style={{ width: '150px', padding: '8px', borderRadius: '4px', border: '1px solid var(--border-color)' }}
                              onKeyDown={preventArrowKeyChange}
                            />
                          </div>
                          <button
                            onClick={handleApplyDiscount}
                            disabled={discountForm.value === ''}
                            className="btn btn-secondary"
                            style={{ marginBottom: 0, padding: '8px 16px' }}
                          >
                            Apply Discount
                          </button>
                          {(parseFloat(editingOrder.discount_percent || 0) > 0 || parseFloat(editingOrder.discount_amount || 0) > 0) && (
                            <button
                              onClick={handleRemoveDiscount}
                              className="btn btn-outline"
                              style={{ marginBottom: 0, padding: '8px 16px' }}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        {(parseFloat(editingOrder.discount_percent || 0) > 0 || parseFloat(editingOrder.discount_amount || 0) > 0) && (
                          <p style={{ marginTop: '10px', fontSize: '0.9rem', color: 'var(--text-gray)' }}>
                            <strong>Current Discount:</strong> {editingOrder.discount_type === 'amount'
                              ? `${getCurrencySymbol(editingOrder.currency)} ${(parseFloat(editingOrder.discount_amount || 0) || 0).toFixed(2)}`
                              : `${(parseFloat(editingOrder.discount_percent || 0) || 0).toFixed(2)}% (${getCurrencySymbol(editingOrder.currency)} ${(((parseFloat(editingOrder.total_amount || 0) || 0) * (parseFloat(editingOrder.discount_percent || 0) || 0)) / 100).toFixed(2)})`}
                          </p>
                        )}
                        <p style={{ marginTop: '10px', fontSize: '0.9rem', color: 'var(--text-gray)' }}>
                          <strong>Order Subtotal:</strong> {getCurrencySymbol(editingOrder.currency)} {parseFloat(editingOrder.total_amount || 0).toFixed(2)}<br />
                          <strong>Shipping:</strong> {getCurrencySymbol(editingOrder.currency)} {parseFloat(editingOrder.shipping_charges || 0).toFixed(2)}<br />
                          <strong>Discount:</strong> {editingOrder.discount_type === 'amount'
                            ? `-${getCurrencySymbol(editingOrder.currency)} ${(parseFloat(editingOrder.discount_amount || 0) || 0).toFixed(2)}`
                            : `-${(parseFloat(editingOrder.discount_percent || 0) || 0).toFixed(2)}% (-${getCurrencySymbol(editingOrder.currency)} ${(((parseFloat(editingOrder.total_amount || 0) || 0) * (parseFloat(editingOrder.discount_percent || 0) || 0)) / 100).toFixed(2)})`}<br />
                          <strong style={{ color: 'var(--primary-color)' }}>Final Total:</strong> {getCurrencySymbol(editingOrder.currency)} {(editingOrder.computed_total_with_shipping ?? recomputeOrderTotals(editingOrder).grandTotal).toFixed(2)}
                        </p>
                      </div>
                     
                      <div className="order-edit-actions" style={{ marginTop: '30px' }}>
                        <button
                          type="button"
                          onClick={handleSaveOrderEdits}
                          className="btn btn-primary"
                          disabled={savingOrder}
                        >
                          {savingOrder ? 'Saving...' : 'Done Editing'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowOrderDetail(false);
                            setEditingOrder(null);
                          }}
                          className="btn btn-outline"
                        >
                          Discard Changes
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {showOrderItemsPopup && selectedOrderForItems && (
                <div className="product-form-modal" role="dialog" aria-modal="true" aria-label="Ordered items">
                  <div className="modal-content" style={{ maxWidth: '720px' }}>
                    <div className="modal-header">
                      <h3>Ordered Items - {selectedOrderForItems.order_number}</h3>
                      <button type="button" className="modal-close-btn" onClick={closeOrderItemsPopup} aria-label="Close">
                        x
                      </button>
                    </div>

                    {selectedOrderForItems.items && selectedOrderForItems.items.length > 0 ? (
                      <div className="table-wrapper" style={{ maxHeight: '55vh', overflowY: 'auto' }}>
                        <table className="orders-table" style={{ marginBottom: 0 }}>
                          <thead>
                            <tr>
                              <th>Product</th>
                              <th>Quantity</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedOrderForItems.items.map((item) => (
                              <tr key={`${selectedOrderForItems.id}-${item.id || item.product_id}`}>
                                <td>{item.product_name || item.name || `Product #${item.product_id}`}</td>
                                <td>{item.quantity}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="no-orders">No item details available for this order.</p>
                    )}

                    <div className="form-actions" style={{ justifyContent: 'flex-end' }}>
                      <button type="button" className="btn btn-outline" onClick={closeOrderItemsPopup}>
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}


          {activeTab === 'company-info' && (
            <div className="admin-section">
              <h2>Company Information</h2>
              {companyInfoMessage.text && (
                <div className={`alert alert-${companyInfoMessage.type}`} style={{ marginBottom: '20px', maxWidth: '600px' }}>
                  {companyInfoMessage.text}
                </div>
              )}
              <form onSubmit={handleCompanyInfoSubmit} style={{ maxWidth: '1000px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', border: '1px solid #e5e7eb' }}>
                  <tbody>
                    <tr>
                      <td style={{ width: '50%', padding: '12px 16px', borderBottom: '1px solid #e5e7eb', verticalAlign: 'top' }}>
                        <div className="form-group" style={{ marginBottom: '12px' }}>
                          <label>Company Name</label>
                          <input
                            type="text"
                            value={companyInfo.company_name || ''}
                            onChange={(e) => setCompanyInfo({ ...companyInfo, company_name: e.target.value })}
                            placeholder="Enter company name"
                          />
                        </div>
                      </td>
                      <td style={{ width: '50%', padding: '12px 16px', borderBottom: '1px solid #e5e7eb', verticalAlign: 'top' }}>
                        <div className="form-group" style={{ marginBottom: '12px' }}>
                          <label>VAT Number</label>
                          <input
                            type="text"
                            value={companyInfo.vat_number || ''}
                            onChange={(e) => setCompanyInfo({ ...companyInfo, vat_number: e.target.value })}
                            placeholder="Enter VAT number"
                          />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td style={{ width: '50%', padding: '12px 16px', borderBottom: '1px solid #e5e7eb', verticalAlign: 'top' }}>
                        <div className="form-group" style={{ marginBottom: '12px' }}>
                          <label>Address</label>
                          <input
                            type="text"
                            value={companyInfo.address || ''}
                            onChange={(e) => setCompanyInfo({ ...companyInfo, address: e.target.value })}
                            placeholder="Enter company address"
                          />
                        </div>
                      </td>
                      <td style={{ width: '50%', padding: '12px 16px', borderBottom: '1px solid #e5e7eb', verticalAlign: 'top' }}>
                        <div className="form-group" style={{ marginBottom: '12px' }}>
                          <label>Organization Number</label>
                          <input
                            type="text"
                            value={companyInfo.organization_number || ''}
                            onChange={(e) => setCompanyInfo({ ...companyInfo, organization_number: e.target.value })}
                            placeholder="Enter organization number"
                          />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td style={{ width: '50%', padding: '12px 16px', borderBottom: '1px solid #e5e7eb', verticalAlign: 'top' }}>
                        <div className="form-group" style={{ marginBottom: '12px' }}>
                          <label>Contact Information</label>
                          <input
                            type="text"
                            value={companyInfo.contact_information || ''}
                            onChange={(e) => setCompanyInfo({ ...companyInfo, contact_information: e.target.value })}
                            placeholder="Phone, email, etc."
                          />
                        </div>
                      </td>
                      <td style={{ width: '50%', padding: '12px 16px', borderBottom: '1px solid #e5e7eb', verticalAlign: 'top' }}>
                        <div className="form-group" style={{ marginBottom: '12px' }}>
                          <label>Email</label>
                          <input
                            type="email"
                            value={companyInfo.email || ''}
                            onChange={(e) => setCompanyInfo({ ...companyInfo, email: e.target.value })}
                            placeholder="Enter email address"
                          />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td style={{ width: '50%', padding: '12px 16px', borderBottom: '1px solid #e5e7eb', verticalAlign: 'top' }}>
                        <div className="form-group" style={{ marginBottom: '12px' }}>
                          <label>Swish Number</label>
                          <input
                            type="text"
                            value={companyInfo.swish_number || ''}
                            onChange={(e) => setCompanyInfo({ ...companyInfo, swish_number: e.target.value })}
                            placeholder="Enter swish number"
                          />
                        </div>
                      </td>
                      <td style={{ width: '50%', padding: '12px 16px', borderBottom: '1px solid #e5e7eb', verticalAlign: 'top' }}>
                        <div className="form-group" style={{ marginBottom: '12px' }}>
                          <label>Bank Giro Number</label>
                          <input
                            type="text"
                            value={companyInfo.bank_giro_number || ''}
                            onChange={(e) => setCompanyInfo({ ...companyInfo, bank_giro_number: e.target.value })}
                            placeholder="Enter bank giro number"
                          />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td style={{ width: '50%', padding: '12px 16px', borderBottom: '1px solid #e5e7eb', verticalAlign: 'top' }}>
                        <div className="form-group" style={{ marginBottom: '12px' }}>
                          <label>IBAN</label>
                          <input
                            type="text"
                            value={companyInfo.iban || ''}
                            onChange={(e) => setCompanyInfo({ ...companyInfo, iban: e.target.value })}
                            placeholder="Enter IBAN"
                          />
                        </div>
                      </td>
                      <td style={{ width: '50%', padding: '12px 16px', borderBottom: '1px solid #e5e7eb', verticalAlign: 'top' }}>
                        <div className="form-group" style={{ marginBottom: '12px' }}>
                          <label>SWIFT Code</label>
                          <input
                            type="text"
                            value={companyInfo.swift_code || ''}
                            onChange={(e) => setCompanyInfo({ ...companyInfo, swift_code: e.target.value })}
                            placeholder="Enter SWIFT/BIC code"
                          />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td style={{ width: '50%', padding: '12px 16px', borderBottom: '1px solid #e5e7eb', verticalAlign: 'top' }}>
                        <div className="form-group" style={{ marginBottom: '12px' }}>
                          <label>Bank Name</label>
                          <input
                            type="text"
                            value={companyInfo.bank_name || ''}
                            onChange={(e) => setCompanyInfo({ ...companyInfo, bank_name: e.target.value })}
                            placeholder="Enter bank name"
                          />
                        </div>
                      </td>
                      <td style={{ width: '50%', padding: '12px 16px', borderBottom: '1px solid #e5e7eb', verticalAlign: 'top' }}>
                        <div className="form-group" style={{ marginBottom: '12px' }}>
                          <label>Account Holder Name</label>
                          <input
                            type="text"
                            value={companyInfo.account_holder_name || ''}
                            onChange={(e) => setCompanyInfo({ ...companyInfo, account_holder_name: e.target.value })}
                            placeholder="Enter account holder name"
                          />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="2" style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', verticalAlign: 'top' }}>
                        <div className="form-group" style={{ marginBottom: '0' }}>
                          <label>Bank Address</label>
                          <input
                            type="text"
                            value={companyInfo.bank_address || ''}
                            onChange={(e) => setCompanyInfo({ ...companyInfo, bank_address: e.target.value })}
                            placeholder="Enter bank address"
                          />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="2" style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', verticalAlign: 'top' }}>
                        <div className="form-group" style={{ marginBottom: '0' }}>
                          <label>Payment Terms</label>
                          <textarea
                            value={companyInfo.payment_terms || ''}
                            onChange={(e) => setCompanyInfo({ ...companyInfo, payment_terms: e.target.value })}
                            placeholder="Enter standard payment terms"
                            rows="3"
                          />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="2" style={{ padding: '12px 16px', verticalAlign: 'top' }}>
                        <div className="form-group" style={{ marginBottom: '0' }}>
                          <label>Banner Message (Home Page)</label>
                          <textarea
                            value={companyInfo.banner_message || ''}
                            onChange={(e) => setCompanyInfo({ ...companyInfo, banner_message: e.target.value })}
                            placeholder="Enter a banner message to display on the home page"
                            rows="3"
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div className="form-actions" style={{ marginTop: '20px', justifyContent: 'flex-end' }}>
                  <button type="submit" className="btn btn-primary">
                    Save Company Information
                  </button>
                </div>
              </form>
            </div>
          )}


          {activeTab === 'change-password' && (
            <div className="admin-section">
              <h2>Change Password</h2>
              {passwordMessage.text && (
                <div className={`alert alert-${passwordMessage.type}`} style={{ marginBottom: '20px' }}>
                  {passwordMessage.text}
                </div>
              )}
              <form onSubmit={handlePasswordChange} style={{ maxWidth: '400px' }}>
                <div className="form-group">
                  <label>Current Password</label>
                  <input
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    placeholder="Enter current password"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>New Password</label>
                  <input
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    placeholder="Enter new password"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Confirm New Password</label>
                  <input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    placeholder="Confirm new password"
                    required
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">
                    Change Password
                  </button>
                </div>
              </form>
            </div>
          )}


          {activeTab === 'contacts' && (
            <div className="admin-section">
              <h2>Contact Submissions</h2>
              {loading ? (
                <div className="loading">
                  <div className="spinner"></div>
                </div>
              ) : (
                <div className="contacts-list">
                  {contacts.map((contact) => (
                    <div key={contact.id} className="contact-card">
                      <div className="contact-header">
                        <div>
                          <h3>{contact.name}</h3>
                          <p>{contact.email}</p>
                          {contact.phone && <p>Phone: {contact.phone}</p>}
                          <p className="contact-date">
                            {new Date(contact.created_at).toLocaleString()}
                          </p>
                        </div>
                        <select
                          value={contact.status}
                          onChange={(e) =>
                            handleContactStatusUpdate(contact.id, e.target.value)
                          }
                          className="status-select"
                        >
                          <option value="new">New</option>
                          <option value="read">Read</option>
                          <option value="replied">Replied</option>
                          <option value="closed">Closed</option>
                        </select>
                      </div>
                      {contact.subject && (
                        <p className="contact-subject">
                          <strong>Subject:</strong> {contact.subject}
                        </p>
                      )}
                      <p className="contact-message">{contact.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'inventory' && (
            <div className="admin-section">
              <h2>Product Inventory Management</h2>
              {inventoryMessage.text && (
                <div className={`alert alert-${inventoryMessage.type}`} style={{ marginBottom: '20px' }}>
                  {inventoryMessage.text}
                </div>
              )}

              {/* Inventory Summary */}
              {inventorySummary && (
                <div className="inventory-summary" style={{ marginBottom: '30px' }}>
                  <h3>Product Inventory Summary</h3>
                  <div className="analytics-grid">
                    <div className="stat-card">
                      <h4>Total Products</h4>
                      <p className="stat-value">{inventorySummary.inventory.total_products}</p>
                      <p className="stat-sub">In Stock: {inventorySummary.inventory.in_stock_products}</p>
                      <p className="stat-sub">Out of Stock: {inventorySummary.inventory.out_of_stock_products}</p>
                      <p className="stat-sub" style={{ color: '#ff9800' }}>Low Stock: {inventorySummary.inventory.low_stock_products}</p>
                    </div>
                    <div className="stat-card">
                      <h4>Total Stock Units</h4>
                      <p className="stat-value">{inventorySummary.inventory.total_stock_units}</p>
                    </div>
                    <div className="stat-card">
                      <h4>Product Inventory Value</h4>
                      <p className="stat-value">kr {parseFloat(inventorySummary.inventory.total_cost_value || 0).toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Inventory Report Table */}
              <div className="inventory-report-section" style={{ marginBottom: '30px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3>Product Inventory Report</h3>
                  <button
                    type="button"
                    onClick={exportInventoryCSV}
                    className="btn btn-primary"
                    style={{ fontSize: '12px' }}
                  >
                    📥 Export CSV
                  </button>
                </div>
                {loading ? (
                  <div className="loading">
                    <div className="spinner"></div>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Product ID</th>
                            <th>Product Name</th>
                            <th>Category</th>
                            <th>Weight (g)</th>
                            <th>Stock</th>
                            <th>Product Price</th>
                            <th>Purchase Price</th>
                            <th>Total Stock Value</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inventoryReport.slice(
                            (inventoryReportPage - 1) * INVENTORY_REPORT_PAGE_SIZE,
                            inventoryReportPage * INVENTORY_REPORT_PAGE_SIZE
                          ).map(item => (
                            <tr key={item.id}>
                              <td>{item.id}</td>
                              <td>{item.name}</td>
                              <td>{item.category}</td>
                              <td>{item.weight ? `${item.weight}g` : '-'}</td>
                              <td>
                                <span style={{ 
                                  fontWeight: 'bold',
                                  color: item.current_stock === 0 ? '#f44336' : item.current_stock < 10 ? '#ff9800' : '#4caf50'
                                }}>
                                  {item.current_stock}
                                </span>
                              </td>
                              <td>kr {parseFloat(item.price || 0).toFixed(2)}</td>
                              <td>kr {(parseFloat(item.purchase_price || 0) + parseFloat(item.wholesale_price_eur || 0)).toFixed(2)}</td>
                              <td>kr {(item.current_stock * (parseFloat(item.purchase_price || 0) + parseFloat(item.wholesale_price_eur || 0))).toFixed(2)}</td>
                              <td>
                                <span style={{
                                  color: item.current_stock === 0 ? '#f44336' : '#4caf50'
                                }}>
                                  {item.current_stock === 0 ? 'Out of Stock' : 'In Stock'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Pagination */}
                    {inventoryReport.length > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
                        <button
                          onClick={() => setInventoryReportPage(Math.max(1, inventoryReportPage - 1))}
                          disabled={inventoryReportPage === 1}
                          className="btn btn-sm"
                        >
                          ← Previous
                        </button>
                        <span>
                          Page {inventoryReportPage} of {Math.ceil(inventoryReport.length / INVENTORY_REPORT_PAGE_SIZE)}
                        </span>
                        <button
                          onClick={() => setInventoryReportPage(Math.min(Math.ceil(inventoryReport.length / INVENTORY_REPORT_PAGE_SIZE), inventoryReportPage + 1))}
                          disabled={inventoryReportPage === Math.ceil(inventoryReport.length / INVENTORY_REPORT_PAGE_SIZE)}
                          className="btn btn-sm"
                        >
                          Next →
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Filter Section - Moved After Report */}
              <div className="inventory-filters" style={{ marginBottom: '30px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                <h3>Filter Product Inventory Transactions</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                  <div className="form-group">
                    <label>Product</label>
                    <select
                      value={pendingInventoryFilters.product_id}
                      onChange={(e) => setPendingInventoryFilters({ ...pendingInventoryFilters, product_id: e.target.value })}
                    >
                      <option value="">All Products</option>
                      {products.map(product => (
                        <option key={product.id} value={product.id}>{product.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Start Date</label>
                    <input
                      type="date"
                      value={pendingInventoryFilters.start_date}
                      onChange={(e) => setPendingInventoryFilters({ ...pendingInventoryFilters, start_date: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>End Date</label>
                    <input
                      type="date"
                      value={pendingInventoryFilters.end_date}
                      onChange={(e) => setPendingInventoryFilters({ ...pendingInventoryFilters, end_date: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Transaction Type</label>
                    <select
                      value={pendingInventoryFilters.transaction_type}
                      onChange={(e) => setPendingInventoryFilters({ ...pendingInventoryFilters, transaction_type: e.target.value })}
                    >
                      <option value="">All Types</option>
                      <option value="stock_added">Stock Added</option>
                      <option value="defective_added">Defective Added</option>
                      <option value="defective_used">Defective Used</option>
                      <option value="order_placed">Order Placed</option>
                      <option value="order_returned">Order Returned</option>
                      <option value="adjustment">Adjustment</option>
                      <option value="batch_adjusted">Batch Adjusted</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                  <button
                    type="button"
                    onClick={handleInventoryFilterChange}
                    className="btn btn-primary"
                  >
                    Apply Filters
                  </button>
                  <button
                    type="button"
                    onClick={handleResetInventoryTransactionFilters}
                    className="btn btn-outline"
                  >
                    Reset to Last 30 Days
                  </button>
                </div>
              </div>

              {/* Inventory Transactions */}
              <div className="transactions-section">
                {(() => {
                  const totalTransactionPages = Math.max(1, Math.ceil(inventoryTransactions.length / INVENTORY_TRANSACTIONS_PAGE_SIZE));
                  const paginatedTransactions = inventoryTransactions.slice(
                    (inventoryTransactionsPage - 1) * INVENTORY_TRANSACTIONS_PAGE_SIZE,
                    inventoryTransactionsPage * INVENTORY_TRANSACTIONS_PAGE_SIZE
                  );

                  return (
                    <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3>Recent Product Inventory Transactions</h3>
                  <button
                    type="button"
                    onClick={exportInventoryTransactionsCSV}
                    className="btn btn-primary"
                    style={{ fontSize: '12px' }}
                  >
                    📥 Export CSV
                  </button>
                </div>
                {loading ? (
                  <div className="loading">
                    <div className="spinner"></div>
                  </div>
                ) : (
                  <>
                  <div className="table-responsive">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Transaction ID</th>
                          <th>Date</th>
                          <th>Product</th>
                          <th>Weight (g)</th>
                          <th>Type</th>
                          <th>Quantity</th>
                          <th>Reference</th>
                          <th>Notes</th>
                          <th>Created By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedTransactions.map(transaction => (
                          <tr key={transaction.id}>
                            <td>{transaction.id}</td>
                            <td>{new Date(transaction.created_at).toLocaleDateString()}</td>
                            <td>{transaction.product_name}</td>
                            <td>{transaction.weight ? `${transaction.weight}g` : '-'}</td>
                            <td>
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                backgroundColor:
                                  transaction.transaction_type === 'stock_added' ? '#4caf50' :
                                  transaction.transaction_type === 'defective_added' ? '#e53935' :
                                  transaction.transaction_type === 'defective_used' ? '#8e24aa' :
                                  transaction.transaction_type === 'order_placed' ? '#2196f3' :
                                  transaction.transaction_type === 'order_returned' ? '#ff9800' :
                                  transaction.transaction_type === 'batch_adjusted' ? '#00bcd4' :
                                  '#9e9e9e',
                                color: 'white'
                              }}>
                                {transaction.transaction_type.replace(/_/g, ' ').toUpperCase()}
                              </span>
                            </td>
                            <td style={{
                              color: transaction.quantity > 0 ? '#4caf50' : '#f44336',
                              fontWeight: 'bold'
                            }}>
                              {transaction.quantity > 0 ? '+' : ''}{transaction.quantity}
                            </td>
                            <td>
                              {transaction.reference_type === 'order' ? `Order #${transaction.reference_id}` :
                               transaction.reference_type === 'return' ? `Return #${transaction.reference_id}` :
                               transaction.reference_type}
                            </td>
                            <td style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {transaction.notes}
                            </td>
                            <td>{transaction.created_by}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {inventoryTransactions.length > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
                      <button
                        onClick={() => setInventoryTransactionsPage(Math.max(1, inventoryTransactionsPage - 1))}
                        disabled={inventoryTransactionsPage === 1}
                        className="btn btn-sm"
                      >
                        ← Previous
                      </button>
                      <span>
                        Page {inventoryTransactionsPage} of {totalTransactionPages}
                      </span>
                      <button
                        onClick={() => setInventoryTransactionsPage(Math.min(totalTransactionPages, inventoryTransactionsPage + 1))}
                        disabled={inventoryTransactionsPage === totalTransactionPages}
                        className="btn btn-sm"
                      >
                        Next →
                      </button>
                    </div>
                  )}
                  </>
                )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


export default AdminDashboard;